import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import { MapView } from '../lib/map/MapView';
import type { FallbackStyleName } from '../lib/map/style';
import { syncTrailCache, TRAILS_UPDATED_EVENT, type CachedTrail } from '../lib/trails';
import {
  syncRunCache,
  listCachedRuns,
  RUNS_UPDATED_EVENT,
  type CachedRun,
} from '../lib/runs';
import {
  runAsTrail,
  isRunPseudoId,
  extractRunId,
  sliceRunIntoSegments,
  buildRunElevationSegments,
} from '../lib/runAsTrail';
import {
  customTrailAsTrail,
  isCustomTrailPseudoId,
} from '../lib/customTrailAsTrail';
import {
  getCustomTrail,
  listCustomTrails,
  CUSTOM_TRAILS_UPDATED_EVENT,
} from '../lib/customTrails';
import { fetchFeaturedTourGpx } from '../lib/featuredTours';
import {
  FEATURED_TOUR_PREFS_UPDATED_EVENT,
  listFeaturedTourPrefs,
} from '../lib/featuredTourPrefs';
import { parseGpx, haversine, DEFAULT_REGION } from '@adtu/shared';

/**
 * Wave 14cd: In-Memory-Cache für parsed Featured-Tour-Geometrien.
 * Vermeidet wiederholtes Fetchen + Parsen beim Re-Render (FEATURED_TOUR_
 * PREFS_UPDATED_EVENT feuert jedes Mal wenn der User auf eine andere
 * Tour-Pille klickt). Key = `${tourId}:${gpxFilename}` damit ein Admin-
 * Update der Tour (neuer gpxFilename) den Cache invalidiert.
 */
const featuredTourCoordsCache = new Map<string, Array<[number, number]>>();
import {
  computeKmMarkers,
  ensureKmMarkerSourceAndLayer,
  ensureShowOnMapSourceAndLayer,
  removeShowOnMapLayer,
  setKmMarkerData,
  setShowOnMapData,
  SHOW_ON_MAP_LAYER,
  SHOW_ON_MAP_LAYER_CASING,
} from '../lib/map/showOnMapTours';
import {
  buildEverestEasterEggTrail,
  EVEREST_EASTER_EGG_EVENT,
  isEverestEasterEggId,
} from '../lib/everestEasterEgg';
import { difficultySwatchBackground, DIFFICULTY_COLOR, DIFFICULTY_LABEL, type Difficulty, type TrailType } from '@adtu/shared';
// Wave 14ca-fix64: nearest-trail-Detection waehrend Ride.
import { useNearestTrail } from '../lib/useNearestTrail';
// Wave 14ca-fix64: pro-Segment-Trail-Mapping → elevation segments (Custom-Tour).
import {
  routeSegmentsToElevationSegments,
  uniqueTrailsFromSegments,
  deriveRouteSegments,
} from '../lib/routeSegmentMap';
import type { CruxPoint } from '@adtu/shared';
import { useCurrentPosition } from '../lib/useCurrentPosition';
import { kmhFromCoords, formatKmh } from '../lib/speed';
import { mapBus } from '../lib/map/mapBus';
import { locateControlBus } from '../lib/map/locateControlBus';
import { compassBus } from '../lib/map/compassBus';
import type { CompassMode } from '../lib/map/CompassRose';
import { mapIntentsBus } from '../lib/map/mapIntentsBus';
import { useTrailElevation } from '../lib/useTrailElevation';
import { cumulativeDistances, nearestPointOnTrack } from '../lib/trailGeom';
import { useRideZoom, useExploreZoom } from '../lib/useRideSettings';
import { useSatelliteEnabled, useActiveRegion } from '../lib/useUiSettings';
import { useOnline } from '../lib/useOnline';
import { ElevationProfile } from '../components/ElevationProfile';
import { PredictBadge } from '../components/PredictBadge';
import { TrailEditSheet } from '../components/TrailEditSheet';
import { ActiveRideOverlay } from '../components/ActiveRideOverlay';
import { FullscreenToggle } from '../components/FullscreenToggle';
import { RideIntroModal } from '../components/RideIntroModal';
import { shouldShowRideIntro } from '../lib/rideIntro';
import { BatteryStationOverlay } from '../components/BatteryStationOverlay';
import { ShuttleModeOverlay } from '../components/ShuttleModeOverlay';
import {
  useAdminMode,
  useAdminToken,
  useDiscoveryMode,
  // Wave 14ca-fix64: re-render bei Color-Assist-Toggle, damit Difficulty-
  // Swatches im Trail-Detail-Sheet + ausgewaehlter-Trail-Badge sofort
  // die neue Palette zeigen.
  useColorAssistMode,
} from '../lib/useUiSettings';
import { PlusActionMenu } from '../components/PlusActionMenu';
import { fetchMe, roleAtLeast, type SessionUser } from '../lib/auth';
// Wave 14ca-fix64: Way-ID-Pick-Mode + Rain-Mode + Pocket-Mode.
import { wayPickBus } from '../lib/wayPickBus';
import { rainModeBus } from '../lib/rainModeBus';
import { pocketModeBus } from '../lib/pocketModeBus';
// Feature-Gates fuer Rain + Pocket Mode (Buttons im Zoom-Stack nur rendern
// wenn die jeweiligen Admin-Toggles in Profile aktiv sind).
import {
  useRainModeEnabled,
  usePocketModeEnabled,
  useRideNextAuraMeters,
} from '../lib/useUiSettings';
import {
  snapPosition,
  markWayOverride,
  type SnapResult,
  type WayOverrideStatus,
} from '../lib/discoveryApi';
import {
  rideRecorder,
  findRecentlyEndedRide,
} from '../lib/rideRecorder';
import { useRecorderState } from '../lib/useRideRecorder';
import { positionBus } from '../lib/positionBus';
import { routeBus } from '../lib/routeBus';
import { pathsBus } from '../lib/pathsBus';
import {
  extractFeaturedTourId,
  isFeaturedTourPseudoId,
  listFeaturedTours,
  featuredTourAsTrail,
  type FeaturedTour,
} from '../lib/featuredTours';
import {
  filterFeaturedTours,
  useFeaturedFilter,
} from '../lib/featuredTourFilter';
import { fullscreenBus } from '../lib/fullscreenBus';
import { NavigateToTrailhead } from '../components/NavigateToTrailhead';
import { POIButton } from '../components/POIButton';
import { PitstopPanel } from '../components/PitstopPanel';
import { ScaleBar } from '../components/ScaleBar';
import { CruxToggle, HAZARD_TRIANGLE_SVG_MARKUP } from '../components/CruxToggle';
import { AddCruxHereButton } from '../components/AddCruxHereButton';
import { RebuildElevationButton } from '../components/RebuildElevationButton';
import { RunsForTrailButton } from '../components/RunsForTrailButton';
import { useShowCruxPoints, useShowHillshade, useVectorPoiEnabled } from '../lib/useUiSettings';
import { usePoiDrinkingWater, usePoiVisibility, useOverpassPoiToggles } from '../lib/usePois';
import {
  ensurePoisAvailableOffline,
  loadPoisWithRefresh,
  getGooglePois,
  GOOGLE_LIVE_CATEGORIES,
  POI_CATEGORIES,
  OVERPASS_EXTRA_CATEGORIES,
  type PoiCategoryId,
  type POI,
} from '../lib/poi';

import {
  POI_DW_LAYER,
  ensurePoiIcon,
  ensurePoiSourceAndLayer,
  removePoiSymbol,
  setPoiData,
} from '../lib/map/poiSymbol';
import {
  OVERPASS_POI_LAYER,
  overpassIconKey,
  ensureOverpassPoiIcons,
  ensureOverpassPoiSourceAndLayer,
  removeOverpassPoiLayer,
  setOverpassPoiData,
  setOverpassPoiHighlight,
} from '../lib/map/overpassPoiLayer';
import {
  VECTOR_POI_DW_LAYER,
  VECTOR_POI_FF_LAYER,
  ensureVectorPoiLayers,
  setVectorPoiVisibility,
  removeVectorPoiLayers,
  vectorPoiFeatureToPopup,
} from '../lib/map/vectorPoiLayer';
import { pitstopTargetBus, type PitstopTarget } from '../lib/pitstopTargetBus';
import { pitstopResultsBus, type PitstopResults } from '../lib/pitstopResultsBus';
import { forwardRejoin } from '../lib/pitstopDetour';
import { postPinRoute } from '../lib/naviApi';
import { raceRefBus } from '../lib/raceRefBus';
import { useBoolPreference } from '../lib/usePreference';
import { SIM_ENABLED_PREF, setSimLocation, restoreSimIfEnabled } from '../lib/simLocation';
import {
  CUSTOM_POI_LAYER,
  ensureCustomPoiIcons,
  ensureCustomPoiSourceAndLayer,
  removeCustomPoiSymbol,
  setCustomPoiData,
} from '../lib/map/customPoiSymbol';
import { loadAdminPoisWithRefresh, forceRefreshAdminPois } from '../lib/adminPois';
import { listUserPois } from '../lib/userPois';

const STYLE_KEY = 'adt_fallback_style';

/**
 * Ab diesem Zoom-Level fangen wir an, Drinking-Water-POIs überhaupt zu rendern.
 * Darunter sind die Marker rein visuell ein unleserlicher Blob (hunderte
 * übereinander). Wave 14bo: im Symbol-Layer direkt als `minzoom` konfiguriert —
 * MapLibre rendert darunter gar nicht erst.
 * 12 ≈ ~15 km Diagonale → Dorf- bis Tal-Level, da sieht man einzelne
 * Fountains sinnvoll.
 */
const POI_MIN_ZOOM = 12;

// POI-Symbol-Layer-Helpers wurden nach lib/map/poiSymbol.ts extrahiert,
// damit CreateCustomPage dieselben benutzen kann (User-Wunsch: POIs auch im
// Plan-Modus sichtbar, um Routen an Wasserstellen vorbei zu planen).

/**
 * Zwei UI-Modi auf der MapPage:
 *   explore: klassische Ansicht. User kann frei pannen/zoomen. Bei Trail-Tap
 *            erscheint das Detail-Sheet, im Sheet ist ein 'Ride'-Button.
 *   ride:    Fokus-Mode. Trail-Detail-Sheet wird zusammengeklappt zu einer
 *            Mini-Leiste. Höhenprofil mit Progress dominiert den unteren Rand.
 *            Auto-Follow ist hart an (LocateControl.forceActivate beim Enter).
 *            Zoom springt auf ride-zoom aus Settings (default 16).
 *
 * Beim Ab-Wählen des Trails (X im Sheet) ODER bei Wechsel auf Explore fallen
 * wir zurück in explore-Mode mit explore-zoom-Setting.
 */
type ViewMode = 'explore' | 'ride';

export function MapPage() {
  const [trails, setTrails] = useState<CachedTrail[]>([]);
  const [runs, setRuns] = useState<CachedRun[]>([]);
  const [selected, setSelected] = useState<CachedTrail | null>(null);
  const [mode, setMode] = useState<ViewMode>('explore');

  // Wave 14ds: ausgewählte Route-Geometrie in den globalen routeBus
  // publishen, damit das Battery-Safe-Mode-Overlay (global in App.tsx, außer-
  // halb MapPage) die Mini-Map-"Briefmarke" zeichnen kann. track.points ist
  // {lat,lon}; der Bus will [lat,lon]. Deselektion (selected=null) → Bus
  // auf null. Cleanup beim Unmount ebenfalls null, damit kein Stale-State
  // bleibt wenn der User die Map-Page verlässt.
  useEffect(() => {
    if (!selected) {
      routeBus.set(null);
      return;
    }
    routeBus.set(selected.track.points.map((p) => [p.lat, p.lon, p.ele]));
    return () => routeBus.set(null);
  }, [selected]);

  // Wave 14ds: alle geladenen Trails als schlanke Polylines in den pathsBus
  // publishen — das Battery-Safe-Mode-Overlay zeichnet die umliegenden als
  // dezenten Kontext-Layer (bbox-Filter passiert im Overlay). Bei Unmount
  // null, damit kein Stale-State bleibt.
  useEffect(() => {
    if (trails.length === 0) {
      pathsBus.set(null);
      return;
    }
    pathsBus.set(trails.map((t) => t.track.points.map((p) => [p.lat, p.lon])));
    return () => pathsBus.set(null);
  }, [trails]);
  // Wave 14ca-fix39: Admin-Quick-Edit aus dem Trail-Detail. Wenn editingTrail
  // gesetzt, rendert TrailEditSheet (Bottom-Sheet via Portal). adminMode +
  // adminToken steuern Sichtbarkeit der Edit/Add-Key-Buttons in der Stat-Row.
  const [editingTrail, setEditingTrail] = useState<CachedTrail | null>(null);
  // Wave 14ch: Ride-Intro-Gate. Wenn der User RIDE drückt aber das Popup
  // noch nie weggeklickt hat, parken wir den eigentlichen Ride-Start hier
  // in einem "pending"-Callback und rendern den RideIntroModal. Nach
  // "Got it" rufen wir den Callback auf und der Ride-Start läuft durch.
  const [rideIntroPending, setRideIntroPending] = useState<
    null | (() => void)
  >(null);
  const [adminMode] = useAdminMode();
  const [adminToken] = useAdminToken();
  // Wave 14ca-fix58: Discovery Mode — buttons in trail-detail erscheinen
  // während Recording + Discovery on; auto-approve auf stop.
  const [discoveryMode] = useDiscoveryMode();
  // Wave 14cf: Ride-Next-Aura — User-konfigurierbare Länge in Metern.
  // 0 = aus, default 500m. Wirkt nur wenn der User eine Route hat
  // und im Ride-Mode ist (s. <MapView ...rideNextAuraMeters /> unten).
  const [rideNextAuraMeters] = useRideNextAuraMeters();
  // Wave 14ca-fix64: re-render bei Color-Assist-Toggle.
  useColorAssistMode();
  // Wave 14ca-fix64: User-Rolle fuer Button-Sichtbarkeit (Schluesselstellen
  // brauchen nur admin_light, BLOCK/ONE-WAY brauchen admin_light_plus).
  // fetchMe einmal beim Mount; null bis das Promise resolved (kein UI-
  // Flash, da DiscoveryButtons selbst gated).
  const [role, setRole] = useState<SessionUser['role'] | null>(null);
  useEffect(() => {
    fetchMe().then((u) => setRole(u?.role ?? null)).catch(() => setRole(null));
  }, []);

  // Wave 14ca-fix64: Way-ID-Pick-Mode.
  // User tippt im Discovery-Buttons-Stack auf "WAY ID" → wayPickBus.activate()
  // → wir rendern eine orange umrahmte Overlay ueber der Karte, ein Tap
  // darauf snappt zur naechstgelegenen OSM-Way und oeffnet ein Modal mit
  // way_id + Length + Add-/Dismiss-Buttons. Der Modus bleibt aktiv bis
  // explizit Dismiss getappt wird, sodass der Admin mehrere Ways
  // hintereinander markieren kann.
  const [wayPickMode, setWayPickMode] = useState(false);
  useEffect(() => {
    setWayPickMode(wayPickBus.get());
    return wayPickBus.subscribe(setWayPickMode);
  }, []);
  // Snapshot fuer das Modal: wenn != null, wird das Modal gerendert.
  const [pickedSnap, setPickedSnap] = useState<SnapResult | null>(null);
  // Wave 14ca-fix64: wo der User getappt hat (lon/lat). Wir zeigen einen
  // kleinen Marker auf der Karte damit er sieht ob der richtige Weg gesnapped
  // wurde — wenn der Marker und die hervorgehobene Polyline visuell weit
  // auseinander liegen, hat er einen Nachbar-Way erwischt.
  const [pickedTapLonLat, setPickedTapLonLat] = useState<
    [number, number] | null
  >(null);
  // Tap-Handler fuer den Pick-Mode-Overlay. Click-Coords → unproject ueber
  // die Map-Instanz aus mapBus → snapPosition (Server-Roundtrip) → Modal.
  const handlePickClick = useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const map = mapBus.get();
    if (!map) return;
    const target = e.currentTarget as HTMLDivElement;
    const rect = target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    let lonLat;
    try {
      lonLat = map.unproject([x, y]);
    } catch {
      return;
    }
    // Tap-Punkt sofort speichern — der Marker erscheint dann waehrend der
    // snap-Roundtrip laeuft, sodass der User sofort weiss "mein Tap wurde
    // registriert" auch wenn das Modal erst nach dem Server-Round-Trip kommt.
    setPickedTapLonLat([lonLat.lng, lonLat.lat]);
    const snap = await snapPosition(lonLat.lat, lonLat.lng, 50);
    if (!snap || !snap.wayId) {
      // Kein Way im Radius — Tap-Marker wieder weg + Hinweis.
      setPickedTapLonLat(null);
      window.alert('No OSM way within 50 m of this spot.');
      return;
    }
    setPickedSnap(snap);
  }, []);
  const handleDismissPick = useCallback(() => {
    setPickedSnap(null);
    setPickedTapLonLat(null);
    wayPickBus.deactivate();
  }, []);
  const handleAddOverride = useCallback(
    async (status: WayOverrideStatus) => {
      if (!pickedSnap?.wayId) return;
      const res = await markWayOverride({
        osmWayId: pickedSnap.wayId,
        status,
        source: 'manual',
      });
      if (!res.ok) {
        window.alert(`Failed to add override: ${res.error || 'unknown'}`);
        return;
      }
      // Nach Add: Modal schliessen, im Pick-Mode bleiben (User kann naechsten
      // Way picken). User kann via Dismiss explizit raus.
      setPickedSnap(null);
      setPickedTapLonLat(null);
    },
    [pickedSnap],
  );

  // Wave 14ca-fix64: Way-Geometry-Highlight + Tap-Marker auf der Karte
  // solange das Pick-Modal offen ist (oder zumindest der Tap registriert
  // wurde, auch waehrend snap noch laeuft).
  // User-Wuensche kombiniert:
  //   1) "ich will den ganzen Weg sehen, sonst kann ich schwer entscheiden
  //      ob bannen" → fuchsia Polyline + Glow
  //   2) "ich will einen Punkt sehen wo ich getappt habe um zu sehen ob
  //      ich den richtigen Weg ausgewaehlt habe" → weisser Tap-Marker mit
  //      fuchsia Center, sitzt UEBER der Polyline
  useEffect(() => {
    const map = mapBus.get();
    if (!map) return;
    const SOURCE_ID = 'adt-pick-highlight';
    const LAYER_ID = 'adt-pick-highlight-line';
    const LAYER_GLOW_ID = 'adt-pick-highlight-glow';
    const TAP_SOURCE_ID = 'adt-pick-tap';
    const TAP_GLOW_LAYER_ID = 'adt-pick-tap-glow';
    const TAP_RING_LAYER_ID = 'adt-pick-tap-ring';
    const TAP_DOT_LAYER_ID = 'adt-pick-tap-dot';
    const cleanup = () => {
      try {
        if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
        if (map.getLayer(LAYER_GLOW_ID)) map.removeLayer(LAYER_GLOW_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
        if (map.getLayer(TAP_DOT_LAYER_ID)) map.removeLayer(TAP_DOT_LAYER_ID);
        if (map.getLayer(TAP_RING_LAYER_ID)) map.removeLayer(TAP_RING_LAYER_ID);
        if (map.getLayer(TAP_GLOW_LAYER_ID)) map.removeLayer(TAP_GLOW_LAYER_ID);
        if (map.getSource(TAP_SOURCE_ID)) map.removeSource(TAP_SOURCE_ID);
      } catch {
        /* Style mid-switch — ignorieren */
      }
    };
    // Wenn weder Tap noch Snap → alles weg.
    if (!pickedSnap?.wayGeometry && !pickedTapLonLat) {
      cleanup();
      return;
    }

    try {
      // ─── Way-Geometry-Highlight ───
      if (pickedSnap?.wayGeometry) {
        const lines = pickedSnap.wayGeometry.segments.map((seg) =>
          seg.map(([lat, lon]) => [lon, lat] as [number, number]),
        );
        const fc: GeoJSON.FeatureCollection<GeoJSON.MultiLineString> = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'MultiLineString', coordinates: lines },
              properties: {},
            },
          ],
        };
        const existing = map.getSource(SOURCE_ID) as
          | maplibregl.GeoJSONSource
          | undefined;
        if (existing) {
          existing.setData(fc);
        } else {
          map.addSource(SOURCE_ID, { type: 'geojson', data: fc });
          map.addLayer({
            id: LAYER_GLOW_ID,
            type: 'line',
            source: SOURCE_ID,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': '#f0abfc',
              'line-width': 14,
              'line-opacity': 0.5,
              'line-blur': 4,
            },
          });
          map.addLayer({
            id: LAYER_ID,
            type: 'line',
            source: SOURCE_ID,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': '#d946ef',
              'line-width': 5,
              'line-opacity': 0.95,
            },
          });
        }
        // Wave 14ca-fix64: fitBounds statt nur Center-Pan, damit der
        // GANZE Way garantiert sichtbar wird — wir wollen ja nicht raten
        // ob der User reinzoomen muss. Padding-bottom = ~50% Viewport-
        // Hoehe, weil der Bottom-Sheet die untere Haelfte verdeckt.
        // Padding-top mit Margin damit die Top-Bar (Style-Switcher etc.)
        // nicht ueberlappt.
        const [minLon, minLat, maxLon, maxLat] = pickedSnap.wayGeometry.bbox;
        const viewport = map.getContainer();
        const viewportH = viewport?.clientHeight ?? 600;
        try {
          map.fitBounds(
            [
              [minLon, minLat],
              [maxLon, maxLat],
            ],
            {
              padding: {
                top: 80, // Style-Switcher-Stack
                right: 90, // Compass + Zoom-Stack
                bottom: Math.round(viewportH * 0.45), // Bottom-Sheet
                left: 24,
              },
              duration: 450,
              maxZoom: 17, // sehr kurze Wege nicht uebermaessig reinzoomen
            },
          );
        } catch {
          // Falls Bbox degeneriert ist (Punkt ohne Ausdehnung) → easeTo.
          const cx = (minLon + maxLon) / 2;
          const cy = (minLat + maxLat) / 2;
          map.easeTo({ center: [cx, cy], duration: 350 });
        }
      }

      // ─── Tap-Marker ───
      // 3 Layer: Aussen-Glow + weisser Ring + fuchsia-Innen-Dot. Das
      // Glow hilft auf hellen Map-Hintergruenden (Maps OSM hat helle
      // Pastell-Tones), der weisse Ring hilft auf dunklen / fuchsia-
      // Highlights, der innere Dot ist der praezise Tap-Punkt.
      if (pickedTapLonLat) {
        const tapFc: GeoJSON.FeatureCollection<GeoJSON.Point> = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: pickedTapLonLat },
              properties: {},
            },
          ],
        };
        const tapExisting = map.getSource(TAP_SOURCE_ID) as
          | maplibregl.GeoJSONSource
          | undefined;
        if (tapExisting) {
          tapExisting.setData(tapFc);
        } else {
          map.addSource(TAP_SOURCE_ID, { type: 'geojson', data: tapFc });
          map.addLayer({
            id: TAP_GLOW_LAYER_ID,
            type: 'circle',
            source: TAP_SOURCE_ID,
            paint: {
              'circle-radius': 16,
              'circle-color': '#000000',
              'circle-opacity': 0.35,
              'circle-blur': 0.6,
            },
          });
          map.addLayer({
            id: TAP_RING_LAYER_ID,
            type: 'circle',
            source: TAP_SOURCE_ID,
            paint: {
              'circle-radius': 10,
              'circle-color': '#ffffff',
              'circle-stroke-color': '#d946ef',
              'circle-stroke-width': 2,
              'circle-opacity': 0.95,
            },
          });
          map.addLayer({
            id: TAP_DOT_LAYER_ID,
            type: 'circle',
            source: TAP_SOURCE_ID,
            paint: {
              'circle-radius': 4,
              'circle-color': '#d946ef',
              'circle-opacity': 1,
            },
          });
        }
      }
    } catch (err) {
      console.warn('[map] pick-highlight setup failed', err);
    }
    return cleanup;
  }, [pickedSnap, pickedTapLonLat]);
  // Wave 14ca-fix41: Recorder-State fuer ActiveRideOverlay + Free-Ride-Button.
  const recorderState = useRecorderState();
  // Wave 14ca-fix64: Naechstgelegener Trail (Snap-to-nearest auf der GPS-
  // Position). Wird als kleine Pill ueber dem Hoehenprofil angezeigt
  // waehrend Recording — User-Wunsch: "wenn ich auf einem Trail bin will
  // ich seinen Namen sehen". null wenn ausserhalb 30m off-track-Schwelle.
  const nearestTrail = useNearestTrail(trails, 30);
  // Wave 14ca-fix50: Featured-Tours State + Filter fuer Map-Swipe-Nav.
  // Liste wird einmal gefetcht beim ersten Featured-Tour-Open, danach
  // re-used fuer prev/next + Filter-Anwendung.
  const [featuredTours, setFeaturedTours] = useState<FeaturedTour[]>([]);
  const featuredFilter = useFeaturedFilter();
  const filteredFeaturedTours = useMemo(
    () => filterFeaturedTours(featuredTours, featuredFilter),
    [featuredTours, featuredFilter],
  );
  // Wave 14ca-fix43: Pruefe ob wir aktuell auf der Map-Route sind. Ansonsten
  // bleeded der RIDE-FAB durch die Overlay-Routen (NAVI etc.) — Overlay ist
  // nur z-10, FAB war z-30. Statt z-Index zu zwingen pruefen wir die Route
  // explizit; semantisch sauberer und MapPage bleibt unwichtig fuer
  // Overlay-Stacking.
  const location = useLocation();
  const isMapRoute = location.pathname === '/';
  const [style, setStyle] = useState<FallbackStyleName>(() => {
    const stored = localStorage.getItem(STYLE_KEY) as FallbackStyleName | null;
    const valid: FallbackStyleName[] = ['mapout', 'outdoor', 'minimal', 'satellite'];
    if (stored && valid.includes(stored)) return stored;
    return 'mapout'; // Default: MapOut-Style (Vektor, mit Höhenlinien)
  });

  const [rideZoom] = useRideZoom();
  const [exploreZoom] = useExploreZoom();
  const [satelliteEnabled] = useSatelliteEnabled();
  const online = useOnline();
  const [activeRegion] = useActiveRegion();

  // Wenn der User Satellite im Profile ausschaltet während er grad im
  // Satellite-Style ist → graceful back to 'mapout'. Sonst hängt der User
  // in einem Style fest dessen Switcher-Button verschwunden ist.
  //
  // Wave 14aw: Gleiche Logik für Offline. Satellite ist ein externer Esri-
  // CDN-Style — offline komplett nutzlos, zeigt nur dunkelblaue leere
  // Canvas. Sobald der User Netz verliert und gerade Satellite an hat,
  // schalten wir silent auf 'mapout' zurück.
  useEffect(() => {
    if (!satelliteEnabled && style === 'satellite') setStyle('mapout');
    if (!online && style === 'satellite') setStyle('mapout');
  }, [satelliteEnabled, online, style]);

  useEffect(() => {
    const parser = new DOMParser();
    // Wave 14ax — Progressive Sync: syncTrailCache feuert onProgress pro
    // Trail (erst alle IDB-Cached, dann pro frisch geladenem Trail). Wir
    // mergen das in den State, sodass die Map nicht 60 s leer ist während
    // 28× parseGpx den Main Thread hält. `.then` am Ende setzt den
    // konsolidierten Snapshot (inkl. Server-seitiger Deletes).
    syncTrailCache({
      domParser: parser,
      onProgress: (trail) => {
        setTrails((prev) => {
          const idx = prev.findIndex((t) => t.id === trail.id);
          if (idx < 0) return [...prev, trail];
          const next = prev.slice();
          next[idx] = trail;
          return next;
        });
      },
    })
      .then((list) => {
        setTrails(list);
        // TabBar-Badge etc. benachrichtigen, dass der IDB-Count jetzt valide ist.
        window.dispatchEvent(new Event(TRAILS_UPDATED_EVENT));
      })
      .catch((err) => console.error('syncTrailCache failed', err));
  }, []);

  // Runs-Cache parallel synchronisieren — wir brauchen die Liste im Trail-
  // Detail-Sheet für den "DH Runs"-Button (Wave 14af). Unabhängig vom
  // Trails-Sync, damit ein Backend-404 für /api/runs den Trails-Load nicht
  // mitreißt.
  //
  // Wave 14bv hotfix: beim RUNS_UPDATED_EVENT lesen wir jetzt NUR noch aus
  // dem IDB-Cache (`listCachedRuns`) — nicht erneut `syncRunCache`, weil
  // der Event von `syncRunCache` selber kommt und sonst ein Infinite-Loop
  // entstehen würde (Backend-Fetch → Event → Backend-Fetch → …).
  // Zusätzlich dispatched `syncRunCache` jetzt nur noch bei echten Cache-
  // Änderungen — aber diese lokale Schutz-Schicht haben wir trotzdem gern
  // belt+suspenders.
  useEffect(() => {
    let cancelled = false;
    const parser = new DOMParser();
    syncRunCache({ domParser: parser })
      .then((list) => {
        if (!cancelled) setRuns(list);
      })
      .catch((err) => console.warn('syncRunCache failed', err));

    const onUpdated = () => {
      listCachedRuns()
        .then((list) => {
          if (!cancelled) setRuns(list);
        })
        .catch(() => {
          /* ignore — halten wir an alten State */
        });
    };
    window.addEventListener(RUNS_UPDATED_EVENT, onUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener(RUNS_UPDATED_EVENT, onUpdated);
    };
  }, []);

  // POI-Prefetch: beim App-Mount alle Kategorien in den IDB-Cache holen,
  // damit sie offline verfügbar sind auch wenn der User sie vorher nie
  // aktiviert hat. User-Wunsch Batch 7: "POIs auch offline downloadbar/
  // verfügbar machen". Silent — wenn offline, nächster Mount probiert's neu.
  useEffect(() => {
    void ensurePoisAvailableOffline();
  }, []);

  // Wave 14ce-fix-revert: vorher hat dieser Effect ein
  // `map.setStyle(map.getStyle(), { diff: false })` auf jedem
  // OPFS-Update gefeuert, um MapLibre's Tile-Cache zu flushen. Nebenwirkung:
  // dynamische Sources/Layers/Images (POI-Symbole, Trail-Linien, Crux-
  // Marker) werden beim Re-Style genuked und müssen über styledata-
  // Listener neu attachen — race-anfällig, in Praxis blieben POIs/Trails
  // gelegentlich weg (User-Bug-Report 14ce: "wenn ich POIs wasser
  // aktiviere, werden diese nicht mehr auf der karte angezeigt").
  //
  // Der OpfsPmtilesSource-Cache wird via Event in lib/map/pmtiles.ts
  // sauber invalidiert — das reicht für den Easter-Egg-Use-Case, weil
  // der Camera-Flug nach Nepal ohnehin NEUE Tile-Requests in einem nicht
  // gecachten Bereich auslöst, die durch die jetzt frisch ausgelesene
  // OPFS-Datei laufen. Tiles im aktuellen Liguria-Viewport bleiben bis
  // zum nächsten Pan/Zoom gecacht — akzeptabler Trade-off, weil der
  // Inhalt sich für Liguria nach einem Re-Download eh nicht ändert.

  // Easter Egg: "Chuck Norris"-Preset im DifficultyLegend-Sheet feuert
  // EVEREST_EASTER_EGG_EVENT. Wir wählen den Everest-Trail aus — der
  // bestehende `selected`-useEffect übernimmt `fitBoundsToTrail` und fliegt
  // die Karte nach Nepal.
  //
  // Wave 14ce-fix: Style wird NICHT mehr automatisch auf Satellite
  // umgeschaltet. Vorher haben wir `setStyle('satellite')` + ein
  // Bypass-Ref-Hack rund um den satelliteEnabled-Guard gemacht; das war
  // race-anfällig (User-Bug: schwarzer Canvas wenn man auf mapout klickt).
  // Jetzt bleiben wir auf dem Style auf dem der User schon ist — User-
  // Wunsch: "öffne es in der normalen karte aber ich brauche um die gpx
  // tour zum everest herum die offlinekarte". Heisst: für Everest auf
  // mapout brauchen wir Offline-PMTiles-Coverage in Nepal — siehe
  // generate-pmtiles.sh-Refactor (multi-region tile-join). Bis die
  // gebaut + deployt sind, zeigt mapout in Nepal schwarz und der User
  // muss manuell auf Satellite umschalten; das ist der bewusste Trade-off.
  //
  // Trail-Quelle: bevorzugt der echte "Everest"-Trail aus dem trails-Cache
  // (Admin-Upload). Erst wenn keiner mit "everest" im Namen existiert,
  // fallen wir auf den synthetischen `buildEverestEasterEggTrail()` zurück.
  //
  // Wir lesen `trails` über einen Ref statt aus dem Effect-Closure, sonst
  // wäre die Liste beim Mount leer (syncTrailCache ist async) und wir hätten
  // permanent den Synthetic-Fallback genutzt.
  const trailsRef = useRef(trails);
  trailsRef.current = trails;
  useEffect(() => {
    const onEverest = () => {
      const real = trailsRef.current.find((t) =>
        t.name.toLowerCase().includes('everest'),
      );
      const easterEggDescription =
        "Chuck Norris doesn't navigate trails. Trails navigate around Chuck Norris.";
      const next = real
        ? { ...real, description: easterEggDescription }
        : buildEverestEasterEggTrail();
      // Diagnose-Log — User-Bug "Easter Egg fliegt nicht mehr zur GPX":
      // hilft zu sehen ob real oder synthetic ausgewählt wird, und ob die
      // Track-Points da sind (sonst kann fitBoundsToTrail die Bounds nicht
      // berechnen).
      console.info('[easter-egg] chuck norris triggered', {
        foundReal: !!real,
        realName: real?.name,
        trailsCount: trailsRef.current.length,
        nameUsed: next.name,
        pointsCount: next.track.points.length,
        firstPoint: next.track.points[0],
        lastPoint: next.track.points[next.track.points.length - 1],
      });
      // Defensive: setMode('explore') stellt sicher, dass der `selected`-
      // useEffect die fitBoundsToTrail-Branch erreicht. Wenn der User
      // vorher in 'ride' war (z. B. anderer Trail, dann Easter-Egg), würde
      // der Mode-Check die fitBounds-Aktion blockieren.
      setMode('explore');
      setSelected(next);
    };
    window.addEventListener(EVEREST_EASTER_EGG_EVENT, onEverest);
    return () => window.removeEventListener(EVEREST_EASTER_EGG_EVENT, onEverest);
  }, []);

  useEffect(() => {
    localStorage.setItem(STYLE_KEY, style);
  }, [style]);

  // Wave 14cf-fix6: lockZoom IMMER auf den modus-passenden Wert setzen
  // — vorher wurde nur explore gesetzt, ride blieb auf dem zuletzt
  // gesetzten Wert hängen. User-Bug: nachdem im Ride-Mode die Karte
  // gepanned wurde und der Center-Button getappt wurde, flog sie auf
  // den exploreZoom statt rideZoom zurück. Ursache war dass forceActivate
  // nach moveend `lockZoom` auf prevLock zurücksetzt (= exploreZoom);
  // jetzt setzt der useEffect parallel zum Mode-Wechsel `lockZoom = rideZoom`,
  // wodurch der prevLock-Restore harmlos wird (rideZoom → rideZoom).
  //
  // Wave 14bo (Energiespar-Tipp #4): gleichzeitig den Tracking-Mode des
  // Controls auf Ride/Explore setzen. Im Explore-Mode darf die UA Fixes
  // bis 5s cachen → GPS-HW kann Duty-Cyclen.
  useEffect(() => {
    const locate = locateControlBus.get();
    if (!locate) return;
    if (mode === 'ride') {
      locate.setLockZoom(rideZoom);
    } else {
      locate.setLockZoom(exploreZoom);
    }
    locate.setMode(mode === 'ride' ? 'ride' : 'explore');
  }, [exploreZoom, rideZoom, mode]);

  // Bei Trail-Deselect immer in Explore-Mode zurück.
  useEffect(() => {
    if (!selected && mode === 'ride') setMode('explore');
  }, [selected, mode]);

  // Wave 14bh — Hillshade conditional visibility (Akku-Optimierung).
  //
  // Problem: der Hillshade-Layer hat `illumination-anchor: 'viewport'` (siehe
  // style.ts), d.h. bei jeder Bearing-Änderung muss die GPU den DEM-Shader
  // pro Pixel neu rechnen. Im heading-Mode dreht der Kompass die Karte
  // quasi kontinuierlich — auf iPhone 17 Pro Max (DPR=3) sind das ~9 Mio
  // Pixel, die pro Frame re-shaded werden. Das frisst Akku wie verrückt
  // (gemessen: 34–41%/h mit Hillshade + Rotation vs 20%/h statisch).
  //
  // Regel: Hillshade NUR sichtbar wenn
  //   - mode === 'explore'  (ruhige Exploration, Karte dreht nicht)
  //   AND
  //   - compassMode === 'north'  (echte North-up-Ausrichtung, bearing=0)
  //
  // Wave 14ds: Verschärfung gegenüber 14bp. Davor war die Regel
  // `compassMode !== 'heading'`, d.h. in 'free' (vom User per 2-Finger-Drag
  // gedreht) blieb Hillshade an. User-Report: das fühlt sich falsch an —
  // sobald die Karte NICHT north-up ist, will man kein 3D-Relief mehr.
  // Im 'free'-Mode gibt's zwar keine kontinuierliche Bearing-Animation, aber
  // der Shader läuft trotzdem bei jedem User-Pan/Pinch über den DEM-Mesh.
  // Strenger ist sicherer für die Batterie und matched die ursprüngliche
  // mentale Regel "Relief = nur wenn norden oben".
  //
  // Im Ride-Mode (mode !== 'explore') ODER sobald der User die Karte aus
  // North-up dreht (egal ob durch Heading-Tracking oder manuell), blenden
  // wir den Layer komplett aus. Der User hat dann sowieso Fokus auf
  // seinen Trail, nicht auf die 3D-Geländeschattierung.
  //
  // State: compassMode wird aus der aktiven CompassRose-Instanz abonniert.
  // Die Instanz kommt über compassBus (von MapView beim Mount gepusht).
  const [compassMode, setCompassMode] = useState<CompassMode>('north');
  useEffect(() => {
    // Outer subscription: reagiert auf Wechsel der CompassRose-Instanz
    // (z.B. bei Map-Remount). Inner subscription: reagiert auf Mode-Wechsel
    // innerhalb einer Instanz.
    let innerUnsub: (() => void) | null = null;
    const applyFromCompass = () => {
      const compass = compassBus.get();
      if (innerUnsub) {
        innerUnsub();
        innerUnsub = null;
      }
      if (compass) {
        setCompassMode(compass.getMode());
        innerUnsub = compass.onModeChange((m) => setCompassMode(m));
      } else {
        setCompassMode('north');
      }
    };
    applyFromCompass();
    const outerUnsub = compassBus.subscribe(applyFromCompass);
    return () => {
      outerUnsub();
      if (innerUnsub) innerUnsub();
    };
  }, []);

  // Hillshade-Visibility an die Map pushen. Wir subscribe'n mapBus, damit
  // der Effect auch greift wenn die Map noch nicht bereit war beim ersten
  // Mount (race mit dem MapView-Style-Load). Auf 'styledata' lauschen wir
  // zusätzlich, damit die Visibility nach einem Style-Swap (user flipped
  // zwischen mapout/outdoor/minimal/satellite) erneut gesetzt wird —
  // MapLibre verwirft Layout-Properties zusammen mit dem alten Style.
  //
  // setLayoutProperty auf einem Layer, der im aktuellen Style gar nicht
  // existiert (z.B. satellite hat kein hillshade), wirft intern — deshalb
  // guard mit `getLayer`. Still-fail ist ok.
  // Wave 14bp: Master-Toggle für Hillshade (Profile → "Show hillshade").
  // User hat i.d.R. im Explore-Mode am meisten Akku-Verbrauch durchs
  // viewport-anchored Shader-Rendering — wer das volle Sparsam-Paket will
  // schaltet den Layer hart aus. Default bleibt AN.
  const [showHillshade] = useShowHillshade();
  useEffect(() => {
    const hillshadeVisible =
      showHillshade && mode === 'explore' && compassMode === 'north';
    let currentMap: maplibregl.Map | null = null;
    let onStyleData: (() => void) | null = null;
    let onIdle: (() => void) | null = null;

    const applyToMap = (map: maplibregl.Map) => {
      if (!map.getLayer('hillshade')) return;
      map.setLayoutProperty(
        'hillshade',
        'visibility',
        hillshadeVisible ? 'visible' : 'none',
      );
    };

    const attach = (map: maplibregl.Map | null) => {
      // Alte Map? Listener weg.
      if (currentMap) {
        if (onStyleData) currentMap.off('styledata', onStyleData);
        if (onIdle) currentMap.off('idle', onIdle);
      }
      currentMap = map;
      onStyleData = null;
      onIdle = null;
      if (!map) return;
      applyToMap(map);
      // Wave 14ca-fix23: zusätzlich auf 'idle' lauschen — robuster Trigger
      // wenn ein style-swap oder programmatic easeTo (z.B. setMode-pitch=0)
      // die Visibility temporär resetted hatte. Stellt sicher dass nach
      // jeder Map-Aktion der hillshade-Zustand korrekt ist.
      onStyleData = () => applyToMap(map);
      onIdle = () => applyToMap(map);
      map.on('styledata', onStyleData);
      map.on('idle', onIdle);
    };

    attach(mapBus.get());
    const unsub = mapBus.subscribe((m) => attach(m));
    return () => {
      unsub();
      if (currentMap) {
        if (onStyleData) currentMap.off('styledata', onStyleData);
        if (onIdle) currentMap.off('idle', onIdle);
      }
    };
  }, [mode, compassMode, showHillshade]);

  /**
   * fitBoundsToTrail — zentriert die Karte so, dass der komplette Trail ÜBER
   * dem Detail-Sheet sichtbar ist. User-Wunsch: "wenn ich auf einen trail
   * klicke und das infos fenster aufgeht, möchte ich dass die karte so
   * zentriert wird, dass der ganze trail oberhalb vom infofenster auf der
   * karte sichtbar sein wird."
   *
   * Padding: oben moderat, unten grosszügig (Sheet verdeckt sonst den Trail-
   * Start/Ende). Seitenpuffer klein — Liguria-Trails sind meist hoch-breit-
   * orientiert.
   *
   * Robust gegen Map-noch-nicht-bereit-Race: wenn mapBus leer ist, re-try'n
   * wir einmal per setTimeout. Normal ist die Map lange da bevor das erste
   * Trail-Select durchkommt (MapPage ist dauerhaft gemountet).
   */
  const fitBoundsToTrail = useCallback((trail: CachedTrail) => {
    const doFit = () => {
      const map = mapBus.get();
      if (!map) {
        setTimeout(doFit, 100);
        return;
      }
      const pts = trail.track.points;
      if (!pts.length) return;
      let minLon = pts[0]!.lon,
        maxLon = pts[0]!.lon;
      let minLat = pts[0]!.lat,
        maxLat = pts[0]!.lat;
      for (const p of pts) {
        if (p.lon < minLon) minLon = p.lon;
        if (p.lon > maxLon) maxLon = p.lon;
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
      }
      try {
        map.fitBounds(
          [
            [minLon, minLat],
            [maxLon, maxLat],
          ],
          {
            // Mit dem kompakteren Info-Sheet (siehe unten, keine Photo-Row
            // im primary) reicht ~260px bottom-padding. Wir halten aber
            // einen gesunden Puffer, falls das Sheet durch description/
            // photos doch auf max-h-[70vh] wächst.
            padding: { top: 60, right: 32, bottom: 280, left: 32 },
            // Auto-fit soll nicht selbst in den pixeligen Overzoom-Bereich
            // (>16) springen — der User kann nach Batch 7 manuell bis z20
            // reingehen, aber der Default-Overview hier bleibt crisp.
            maxZoom: 16,
            duration: 700,
          },
        );
      } catch (err) {
        console.warn('[map] fitBounds failed', err);
      }
    };
    doFit();
  }, []);

  // Intent-Konsument aus der TrailsPage: Trail auswählen + resetMode + fitBounds.
  // fitBounds wird hier NICHT mehr explizit angestossen — das übernimmt der
  // useEffect darunter, der auf `selected`-Änderungen lauscht. Das spart
  // Duplikation und sorgt dafür, dass jede Selection (egal ob aus Intent oder
  // Map-Click) identisch zentriert wird.
  //
  // Wave 14ag: Intent darf zusätzlich einen runId tragen. Für Runs bauen wir
  // on-the-fly einen Pseudo-Trail (siehe lib/runAsTrail) und behandeln ihn
  // wie einen ganz normalen selected Trail.
  useEffect(() => {
    // ULTRA-Fix: KEIN Früh-Return mehr, wenn trails/runs leer sind. Vorher
    // returnte dieser Effekt (bevor er den mapIntentsBus-Subscriber anhängt),
    // sobald keine Trails/Runs geladen waren und kein Custom-Intent gerade
    // anlag. In der MTB-App war trails immer voll (Liguria) → nie getriggert.
    // ULTRA hat aber GAR KEINE Trails/Runs → der Effekt returnte immer früh →
    // der Subscriber wurde nie angehängt → Custom-Tour-Klicks (Bus-Publish)
    // erreichten die MapPage nie (keine Selektion/Höhenprofil/Ride).
    // Der Subscriber muss IMMER hängen; trail/run-Intents, die mangels Daten
    // (noch) nicht anwendbar sind, geben einfach false zurück und bleiben im
    // Bus für einen späteren Effekt-Lauf.

    // trailsById hier lokal — Intent kann einen Run lange VOR dem nächsten
    // Render-Cycle feuern, da wäre der useMemo trailsById oben noch stale.
    const localTrailsById = new Map<string, CachedTrail>();
    for (const t of trails) localTrailsById.set(t.id, t);

    const applyTrailIntent = (trailId: string, resetMode: boolean): boolean => {
      const trail = trails.find((t) => t.id === trailId);
      if (!trail) return false;
      setSelected(trail);
      if (resetMode) setMode('explore');
      return true;
    };
    const applyRunIntent = (runId: string, resetMode: boolean): boolean => {
      const run = runs.find((r) => r.id === runId);
      if (!run) return false;
      setSelected(runAsTrail(run, localTrailsById));
      if (resetMode) setMode('explore');
      return true;
    };
    // Stage 6m: Custom-Trail-Intent — asynchron, weil wir den Trail erst aus
    // IDB holen müssen. Für die Bus-Semantik simulieren wir "Intent wurde
    // erfolgreich konsumiert" optimistisch per synchronem true-return; der
    // Async-Load läuft im Hintergrund und setzt setSelected sobald fertig.
    // Wenn der Custom-Trail nicht existiert (schon gelöscht), loggen wir
    // und ignorieren — nichts passiert, User sieht die bisherige Auswahl.
    const applyCustomTrailIntent = (
      customTrailId: string,
      resetMode: boolean,
      autoStartRide = false,
    ): boolean => {
      if (resetMode) setMode('explore');
      void (async () => {
        try {
          const ct = await getCustomTrail(customTrailId);
          if (!ct) {
            console.warn('[map] custom trail not found', customTrailId);
            return;
          }
          setSelected(customTrailAsTrail(ct));
          // Wave 14ch: "Save & ride now"-Pfad von CreateCustomPage. Sobald
          // der Custom-Trail selektiert ist, den Ride-Modus starten — geht
          // durch dasselbe startRide-Gate inkl. RideIntroModal beim ersten
          // Aufruf. Ein Tick warten damit setSelected den nächsten Render
          // committet hat, sonst arbeiten Mode-Wechsel + Trail-Selection
          // parallel auf einer noch nicht aktualisierten State-Snapshot.
          if (autoStartRide) {
            setTimeout(() => startRide(), 0);
          }
        } catch (err) {
          console.warn('[map] load custom trail failed', err);
        }
      })();
      return true;
    };
    // Wave 14ca-fix48: Featured-Tour-Intent — fetch + parse + wrap analog
    // applyCustomTrailIntent. Async, optimistic-true return damit der Bus
    // konsumiert wird; setSelected passiert wenn der Fetch durch ist.
    // Wave 14ca-fix50: zusaetzlich speichern wir die volle Tour-Liste in
    // State damit die Map-Swipe-Nav (prev/next zwischen gefilterten Touren)
    // ohne Re-Fetch klappt.
    const applyFeaturedTourIntent = (
      featuredTourId: string,
      resetMode: boolean,
    ): boolean => {
      if (resetMode) setMode('explore');
      void (async () => {
        try {
          const tours = await listFeaturedTours();
          setFeaturedTours(tours);
          const tour = tours.find((t) => t.id === featuredTourId);
          if (!tour) {
            console.warn('[map] featured tour not found', featuredTourId);
            return;
          }
          const trail = await featuredTourAsTrail(tour, new DOMParser());
          if (!trail) {
            console.warn('[map] featured tour wrap failed', featuredTourId);
            return;
          }
          setSelected(trail);
        } catch (err) {
          console.warn('[map] load featured tour failed', err);
        }
      })();
      return true;
    };
    // Liefert true wenn Intent erfolgreich angewandt (dann darf er konsumiert
    // werden). Sonst bleibt er im Bus, und die Effekt-Re-Run mit frischen
    // trails/runs kriegt einen zweiten Versuch.
    const applyIntent = (intent: {
      trailId?: string;
      runId?: string;
      customTrailId?: string;
      featuredTourId?: string;
      ephemeralTrail?: CachedTrail;
      resetMode?: boolean;
      autoStartRide?: boolean;
    }): boolean => {
      const resetMode = intent.resetMode ?? false;
      const autoStartRide = intent.autoStartRide ?? false;
      // ULTRA: flüchtige Route (nicht gespeichert) — direkt selektieren + ggf.
      // sofort fahren. Kein IDB-Load nötig.
      if (intent.ephemeralTrail) {
        if (resetMode) setMode('explore');
        setSelected(intent.ephemeralTrail);
        if (autoStartRide) setTimeout(() => startRide(), 0);
        return true;
      }
      if (intent.trailId) return applyTrailIntent(intent.trailId, resetMode);
      if (intent.runId) return applyRunIntent(intent.runId, resetMode);
      if (intent.customTrailId)
        return applyCustomTrailIntent(intent.customTrailId, resetMode, autoStartRide);
      if (intent.featuredTourId)
        return applyFeaturedTourIntent(intent.featuredTourId, resetMode);
      return false;
    };

    const pending = mapIntentsBus.peek();
    if (pending && applyIntent(pending)) mapIntentsBus.consume();

    const unsub = mapIntentsBus.subscribe((intent) => {
      if (!intent) return;
      if (applyIntent(intent)) mapIntentsBus.consume();
    });
    return unsub;
  }, [trails, runs]);

  // Wish 3: fitBounds bei JEDER Trail-Auswahl — egal ob per Map-Tap, Liste oder
  // Intent. Wir zentrieren allerdings NUR im Explore-Mode; im Ride-Mode
  // dominiert die Rider-Verfolgung und wir wollen die Kamera nicht unter dem
  // User wegziehen. Bei De-Select (selected → null) tun wir nichts.
  //
  // User-Wunsch 14i: "wenn oben rechts der button für aktuelle position
  // aktiviert ist, man aber einen trail anklickt, soll der button deaktiviert
  // werden — sonst springt es wieder zu mir zurück". fitBounds ist ein
  // programmatischer move (essential:true, kein originalEvent), den der
  // LocateControl-Pause-Listener absichtlich NICHT erkennt (sonst würden
  // unsere eigenen Flyto's den Tracking-Status zerlegen). Deshalb pausieren
  // wir hier explizit bevor wir fitBounds anstoßen — das beendet das
  // Follow-easeTo beim nächsten GPS-Fix, der Trail bleibt im Blick. Re-Tap
  // auf den Locate-Button holt den User jederzeit zurück.
  useEffect(() => {
    if (!selected) {
      console.info('[trail-select] no selected, skip fitBounds');
      return;
    }
    if (mode !== 'explore') {
      console.info('[trail-select] mode != explore, skip fitBounds', { mode });
      return;
    }
    console.info('[trail-select] fitBoundsToTrail', {
      name: selected.name,
      pointsCount: selected.track.points.length,
      hasMapBus: !!mapBus.get(),
    });
    locateControlBus.get()?.pause();
    fitBoundsToTrail(selected);
    // Nicht auf `mode` deppen — im Ride-Mode wollen wir nicht re-fitten, wenn
    // der User stopRide/startRide cycled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, fitBoundsToTrail]);

  // ULTRA-Fork: MTB-Trails werden auf der Karte NICHT mehr gerendert.
  // Die fruehere Visibility-Maske (trailVisibilityBus) + Difficulty-Filter
  // (trailFilterBus) sind damit gegenstandslos — mapTrails (unten) startet
  // jetzt von einer leeren Liste und nimmt nur noch Selection-Pseudo-Trails
  // (Custom-Tours, Run-Segmente, Everest-Easter-Egg) auf.

  // Wave 14cb: Fullscreen-Mode. Wenn aktiv → BottomBar + Trail-Detail-Sheets
  // + Ride-Höhenprofil-Sheet werden weggeblendet, der User sieht maximal viel
  // Karte. Zoom + Locate + Compass + Style-Switcher + FullscreenToggle bleiben.
  // Bus-State übersteht Reload (localStorage), s. fullscreenBus.ts.
  const [isFullscreen, setIsFullscreen] = useState(() => fullscreenBus.get());
  useEffect(() => {
    setIsFullscreen(fullscreenBus.get());
    return fullscreenBus.subscribe(setIsFullscreen);
  }, []);

  // Trails an MapView weiterreichen. Wave 14ca: Difficulty-Filter aus dem
  // Bus angewandt — DifficultyLegend ist jetzt tappable und schreibt dort
  // hinein. Optional zusätzlich die TrailsPage-Visibility-Maske
  // (explizite ID-Liste, "Show these N on map"). Beide AND-kombiniert.
  //
  // Wave 14ah: Wenn ein DH-Run selektiert ist (Pseudo-Trail mit ID-Prefix
  // `run:`), splitten wir ihn in pro-Trail-Segmente auf. Jedes Segment kriegt
  // eine eigene Pseudo-ID `run:<id>:seg:<i>` und die Difficulty / colorHex
  // vom zugehörigen Original-Trail. So kann die Map entlang eines Runs die
  // Linien-Farbe wechseln, wenn der Run unterschiedliche Difficulties
  // kombiniert (User-Wunsch: "wenn ein run aus 2 oder mehreren downhills
  // besteht, muss sich die farbe der schwerigkeit auf der karte ändern").
  // Die Aura liefern wir über `selectionOverride` separat (s. MapView-Call
  // unten) — die läuft über den gesamten Run, nicht pro Segment.
  const mapTrails = useMemo(
    () => {
      // ULTRA-Fork: keine MTB-Trails auf der Karte. Wir starten von einer
      // leeren Liste — nur Selection-Pseudo-Trails (Run-Segmente, Custom-
      // Tours, Everest-Easter-Egg) werden unten hinzugefuegt, damit ihre
      // Linie + Aura + Start/End-Marker weiterhin gerendert werden.
      const list: Array<{
        id: string;
        difficulty: Difficulty;
        colorHex?: string | null;
        track: typeof trails[number]['track'];
      }> = [];
      if (selected && isRunPseudoId(selected.id)) {
        const runId = extractRunId(selected.id);
        const run = runs.find((r) => r.id === runId);
        if (run) {
          const segments = sliceRunIntoSegments(run, new Map(trails.map((t) => [t.id, t])));
          for (const s of segments) {
            list.push({
              id: s.id,
              difficulty: s.difficulty,
              colorHex: s.colorHex,
              track: s.track,
            });
          }
        }
      }
      // Stage 6m: Custom-Trail-Pseudo — MapView soll die Linie rendern, damit
      // Aura + Start/End-Marker sich normal an die Trail-Geometrie anhaften.
      // Die colorHex kommt aus customTrailAsTrail() und ist immer sky-400.
      if (selected && isCustomTrailPseudoId(selected.id)) {
        list.push({
          id: selected.id,
          difficulty: selected.difficulty,
          colorHex: selected.colorHex,
          track: selected.track,
        });
      }
      // Easter Egg: Everest-Synthetic-Trail rendert sein Polyline genauso wie
      // ein Custom-Trail. Ohne diesen Push würde nur die Trail-Card erscheinen,
      // aber der User würde keine Linie auf der Satellitenansicht sehen.
      if (selected && isEverestEasterEggId(selected.id)) {
        list.push({
          id: selected.id,
          difficulty: selected.difficulty,
          colorHex: selected.colorHex,
          track: selected.track,
        });
      }
      return list;
    },
    [trails, selected, runs],
  );

  // Trail-Lookup Map für den "DH Runs"-Modal — Chain-Labels brauchen
  // Trail-Namen per ID.
  const trailsById = useMemo(() => {
    const m = new Map<string, CachedTrail>();
    for (const t of trails) m.set(t.id, t);
    return m;
  }, [trails]);

  // Wave 14ak: Aura-Geometrie-Override für Pseudo-Runs. MUSS memoized sein —
  // das Inline-Literal `{ track, difficulty, colorHex }` hätte bei jedem
  // Re-Render eine neue Referenz und würde den Selection-Overlay-Effect in
  // MapView triggern (cleanup + re-apply = sichtbares Flackern der Aura +
  // Start/End-Marker). Stable reference → Effect feuert nur bei echten
  // Changes.
  const selectionOverride = useMemo(() => {
    if (!selected) return null;
    // Stage 6m: Custom-Trails liegen nicht in `trails` (separater IDB-Store),
    // daher muss MapView die Track-Geometrie via Override kriegen um die
    // Aura + Linie rendern zu können. Analog zum Run-Pseudo-Trail.
    if (!isRunPseudoId(selected.id) && !isCustomTrailPseudoId(selected.id) && !isFeaturedTourPseudoId(selected.id))
      return null;
    return {
      track: selected.track,
      difficulty: selected.difficulty,
      colorHex: selected.colorHex,
    };
  }, [selected]);

  // Wave 14ak: Trails-Liste im Run (in Reihenfolge) — brauchen wir sowohl für
  // den Multi-Kreis-Header als auch für andere UI. Leer bei normalen Trails.
  const runTrailsInOrder = useMemo(() => {
    if (!selected || !isRunPseudoId(selected.id)) return [] as CachedTrail[];
    const runId = extractRunId(selected.id);
    const run = runs.find((r) => r.id === runId);
    if (!run) return [];
    const out: CachedTrail[] = [];
    for (const id of run.trailIds) {
      const t = trailsById.get(id);
      if (t) out.push(t);
    }
    return out;
  }, [selected, runs, trailsById]);

  // Wave 14ak-fix: `onTrailClick` an MapView MUSS stable-referenziert sein.
  // Ein neues Inline-Callback pro Render triggert in MapView den Trail-Render-
  // Effect (dep=[trails, onTrailClick, mapReady]) → alle Trail-Sources werden
  // neu aufgebaut. Bei DH-Runs (N Sub-Segmente) heisst das: bei jedem MapPage-
  // Render werden N Sources+Layers entfernt und neu angelegt → sichtbares
  // Flackern der Trail-Linie, und während `fitBoundsToTrail` animiert ist das
  // besonders schlimm. Memoiziert → Effect feuert nur noch, wenn echt `trails`
  // sich ändert (also extrem selten, nur bei Sync/Initial-Load).
  const handleTrailClick = useCallback(
    (id: string) => {
      // Wave 14ah: Klicks auf Run-Sub-Segmente (`run:<id>:seg:<i>`) sollen
      // den Run-Pseudo-Trail selektiert lassen, NICHT durchschalten auf
      // ein Sub-Segment (das hätte keine sinnvollen Stats/Cruxes).
      // Click-Events von echten Trails wie immer auflösen.
      //
      // Stage 6m: Custom-Trail-Pseudo-IDs (`custom:<id>`) werden ebenfalls
      // ignoriert — der Trail ist bereits selektiert (sonst läge er nicht
      // in mapTrails), ein Tap drauf soll ihn nicht "neu auswählen".
      if (isRunPseudoId(id)) return;
      if (isCustomTrailPseudoId(id)) return;
      setSelected(trails.find((t) => t.id === id) ?? null);
    },
    [trails],
  );

  // Höhenprofil-Daten für den selektierten Trail — hook feuert nur wenn
  // selected != null (sonst trailId=null → source='none' ohne side-effects).
  const elevation = useTrailElevation({
    trailId: selected?.id ?? null,
    track: selected?.track ?? null,
  });

  // Gain/Loss aus dem UI-Profil ableiten (statt aus selected.stats).
  //
  // Warum nicht selected.stats.{gain,loss}?
  //   Die werden beim Trail-Import aus roh-GPX-Punkten berechnet (geo.ts ->
  //   elevationStats). Bei GPX OHNE ele-Werten (häufig: "Appendaun" und
  //   andere extern gezogene Tracks ohne Höhendaten) landet da {0, 0} im
  //   Backend und bleibt da. Das Profil-Widget darunter zeigt bereits den
  //   SRTM-Fallback an — dieselbe Datenquelle für die Stats oben zu nehmen,
  //   damit sie konsistent mit dem Chart sind, ist das Naheliegendste.
  //
  // Quelle der Samples in elevation.elevations (siehe useTrailElevation):
  //   - source='gpx':  interpolierte GPX-ele-Werte (100 Samples)
  //   - source='srtm': map.queryTerrainElevation an 100 Sample-Punkten
  //   - source='pending': noch am Fetchen (zeigen wir als "…")
  //   - source='none': weder GPX noch SRTM verfügbar → fallback auf
  //     selected.stats (zumindest kriegen wir dann 0/0 angezeigt statt
  //     "—" zu spoilen, falls der Trail wirklich flach ist).
  const derivedStats = useMemo<{ gain: number; loss: number } | null>(() => {
    const samples = elevation.elevations;
    if (!samples || samples.length < 2) return null;
    // Wave 14cf-fix4: Peak-to-Peak-Hysterese statt per-Step-Deadband.
    // Wave 14cf-fix5: Threshold ist quellen-abhängig.
    //
    // Garmin/Strava/Komoot-Standard: laufende Min/Max seit der letzten
    // "gelockten" Extremstelle (pivot). Erst wenn die Höhe von einem
    // Peak um mindestens THRESHOLD nach unten reverst, gilt der Peak
    // als bestätigt — gain += peak - pivot. Analog für Tal → loss.
    //
    // Quellen-abhängiger Threshold:
    //   - GPX (vom Komoot/Strava/Wahoo importiert):  THRESHOLD = 5 m
    //     Diese Daten sind bereits sauber/geglättet (Komoot benutzt
    //     intern einen guten DEM + Smoothing). Aggressives Filtern
    //     würde echte kleine Climbs unterdrücken.
    //   - SRTM (im Browser an Track-Punkten gesampelt): THRESHOLD = 10 m
    //     Per-Cell-Drift in der SRTM-Quelle ist real und kann ~3–5 m
    //     pro Sample-Übergang produzieren. Höherer Threshold filtert
    //     dieses Phantom-Noise zuverlässig.
    //
    // Validierung gegen die echte Komoot-Elba-GPX (s. outputs/
    // elba_real_test.mjs):
    //   - Raw GPX (max physikalisch möglich):  936 m
    //   - Hysterese t=5:                       930 m  (vs Apple-Watch 1007 m, -7.6 %)
    //   - Hysterese t=10:                      900 m  (-10.6 %)
    // Die Lücke zur Watch (~70 m) liegt in der GPX-Quelle, nicht in
    // unserer Mathematik — Komoot exportiert leicht entfeatured.
    //
    // SRTM-Validierung (synthetic, s. outputs/elev_hysteresis.mjs)
    // mit 150 m Smoothing + 10 m Threshold:
    //   - I26 (73 km / 2600 m):  2431 m (-7 %)
    //   - Long sparse (100 km):  1971 m (-1 %)
    //   - Flach + ±5 m Noise:    0 m Phantom
    const source = elevation.source;
    const THRESHOLD = source === 'gpx' ? 5 : 10;
    if (samples.length < 2) return { gain: 0, loss: 0 };
    let gain = 0;
    let loss = 0;
    let pivot = samples[0]!;
    let runMin = samples[0]!;
    let runMax = samples[0]!;
    // direction: 0 = undecided, +1 = climbing, -1 = descending.
    let direction: -1 | 0 | 1 = 0;
    for (let i = 1; i < samples.length; i++) {
      const v = samples[i]!;
      if (v > runMax) runMax = v;
      if (v < runMin) runMin = v;

      // Event A: Peak bestätigt (we WERE climbing or undecided, jetzt
      // sind wir um THRESHOLD vom Peak runter).
      if (direction !== -1 && runMax - v >= THRESHOLD) {
        gain += runMax - pivot;
        pivot = runMax;
        direction = -1;
        runMin = v;
        runMax = v;
      }
      // Event B: Tal bestätigt (we WERE descending or undecided, jetzt
      // um THRESHOLD vom Tal hoch).
      else if (direction !== +1 && v - runMin >= THRESHOLD) {
        loss += pivot - runMin;
        pivot = runMin;
        direction = +1;
        runMax = v;
        runMin = v;
      }
    }
    // Tail: was zwischen letztem Pivot und dem letzten Sample noch
    // unconfirmed offen ist, würde sonst verloren gehen. Anhand der
    // aktuellen Richtung ranziehen.
    const last = samples[samples.length - 1]!;
    if (direction === +1 || (direction === 0 && last > pivot)) {
      gain += Math.max(0, last - pivot);
    } else if (direction === -1 || (direction === 0 && last < pivot)) {
      loss += Math.max(0, pivot - last);
    }
    return { gain, loss };
  }, [elevation.elevations, elevation.source]);

  // Progress-Berechnung: wo steht der User auf dem Track?
  //
  // Wave 14cf-fix5: Monotone Progression. Wir merken uns den letzten
  // alongTrackMeters-Wert und reichen ihn als hint an nearestPointOnTrack
  // weiter. Dadurch kann der Snap nicht mehr auf einen früher überfahrenen
  // Punkt zurückspringen wenn die Route sich selbst kreuzt (Out-and-Back,
  // Loops). Bei Trail-Wechsel resetten wir den Hint, weil der bisherige
  // Wert für den neuen Trail keinen Sinn ergibt.
  const pos = useCurrentPosition();
  const progressHintRef = useRef<{ trailId: string | null; meters: number | null }>({
    trailId: null,
    meters: null,
  });
  // Reset-on-Trail-Change: wenn selected.id wechselt, hint löschen.
  useEffect(() => {
    if (progressHintRef.current.trailId !== (selected?.id ?? null)) {
      progressHintRef.current = { trailId: selected?.id ?? null, meters: null };
    }
  }, [selected?.id]);
  // ULTRA: Track-Gesamtlänge (für Odometer-Clamp). Memoized je selected.
  const routeLenMeters = useMemo(() => {
    if (!selected) return 0;
    const cum = cumulativeDistances(selected.track.points);
    return cum[cum.length - 1] ?? 0;
  }, [selected]);
  const progress = useMemo(() => {
    if (!selected || !pos) return null;
    // Hint = wo wir den User auf dem Track erwarten.
    // 1) Beim AUFZEICHNEN: die echte gefahrene Distanz (Tacho). Das ist monoton
    //    und schleifensicher — auf einer Schleife/Out-and-Back schnappt die rein
    //    geometrische Suche sonst aufs falsche (früher überfahrene) Segment
    //    (User-Bug: Rückweg-Anstieg wurde auf die Hinweg-Abfahrt gemappt).
    // 2) Sonst: der letzte bestätigte Snap (sticky map-matching).
    let hint =
      progressHintRef.current.trailId === selected.id
        ? progressHintRef.current.meters
        : null;
    const odo = recorderState.kind === 'recording'
      ? recorderState.stats?.distanceMeters
      : null;
    if (typeof odo === 'number' && Number.isFinite(odo) && routeLenMeters > 0) {
      hint = Math.min(odo, routeLenMeters);
    }
    const snap = nearestPointOnTrack(
      selected.track.points,
      pos.latitude,
      pos.longitude,
      undefined,
      { hintAlongMeters: hint },
    );
    // Hint für den nächsten Snap aktualisieren.
    progressHintRef.current = {
      trailId: selected.id,
      meters: snap.alongTrackMeters,
    };
    return snap;
  }, [selected, pos, recorderState, routeLenMeters]);

  // Höhe für einen frisch zu setzenden Crux: bevorzugt GPS-Altitude (das ist
  // exakt die Höhe wo der User physisch steht, gemessen vom Gerät) — sonst
  // SRTM-Sample am Track-Punkt der nearestPointOnTrack-Position. Beide
  // Quellen können null sein:
  //   - GPS-Altitude ist auf manchen iOS-Geräten ohne expliziten Permission-
  //     Klick nicht verfügbar (Browser liefert dann coords.altitude=null).
  //   - SRTM kann fehlen wenn die Map noch nicht terrain-fertig ist (sollte
  //     selten passieren weil der Trail-Sheet nur sichtbar ist wenn die Map
  //     läuft, aber pending-State ist möglich).
  // Wenn beide null: setzen wir den Crux ohne Höhe — der User sieht das im
  // Popup als fehlende Höhe-Zeile. Robust statt scheinpräzise mit "0 m".
  const currentCruxElevation = useMemo(() => {
    const gps = pos?.altitude;
    if (gps != null && Number.isFinite(gps)) return gps;
    const along = progress?.alongTrackMeters ?? null;
    if (along == null) return null;
    return interpolateElevationAtDistance(elevation.elevations, elevation.distances, along);
  }, [pos, progress, elevation.elevations, elevation.distances]);

  // Wish 5: Finger-Scrub aufs Höhenprofil → "imaginärer Punkt fährt den Trail
  // entlang". scrubMeters ist die aktuelle Position des Fingers (in Metern
  // vom Track-Start), null wenn der User gerade nicht scrubbt.
  // Wir cachen das kumulierte Distance-Array pro selektiertem Trail, damit
  // jedes pointermove keine O(N)-Reduktion macht (Tracks haben bis ~2k Punkte).
  const [scrubMeters, setScrubMeters] = useState<number | null>(null);
  const cumDist = useMemo(
    () => (selected ? cumulativeDistances(selected.track.points) : null),
    [selected],
  );

  // Wave 14ca-fix64: Custom-Tour Trail-Badges + farbiges Höhenprofil.
  // Wenn der selected Trail ein CustomTrail-Pseudo ist (er hat dann
  // routeSegments durchgereicht), bauen wir daraus die ElevationSegment-
  // Liste fuer:
  //   1) ElevationProfile mit per-Difficulty-gefärbten Sub-Linien
  //   2) Trail-Badge-Liste im Detail-Sheet (Tap → Highlight)
  //   3) Scrub-Reveal des aktuellen Trail-Namens
  //
  // Bei normalen Trails / DH-Runs / Featured-Tours ist routeSegments
  // undefined → elevationSegments bleibt leer → Profile rendert wie vorher
  // monochrom + keine Trail-Liste.
  const elevationSegments = useMemo(() => {
    if (!selected) return [];
    // Wave 14dw: DH-Runs sind KEINE monolithischen Geometrien — sie sind
    // eine Verkettung aus 2–N echten Trails. Wir bauen die Profil-Segmente
    // genau wie auf der RunDetailPage über buildRunElevationSegments aus
    // der Run-GPX + den Trail-Endpunkten. So sieht der User im Map-Sheet
    // dasselbe segmentierte Profil wie unter NAVI → Runs.
    if (isRunPseudoId(selected.id)) {
      const runId = extractRunId(selected.id);
      const run = runs.find((r) => r.id === runId);
      if (!run) return [];
      return buildRunElevationSegments(run, trailsById);
    }
    // Wave 14cl: Custom Tours UND Featured Tours bekommen die Segment-
    // Faerbung. Normale Trails sind monolithisch — kein Mapping nötig.
    if (
      !isCustomTrailPseudoId(selected.id) &&
      !isFeaturedTourPseudoId(selected.id)
    ) {
      return [];
    }
    // Coords aus selected.track.points zurueckbauen (lon/lat-Reihenfolge wie
    // urspruenglich bei customTrailAsTrail() umgekehrt). Wir vermeiden
    // Refactor von customTrailAsTrail damit der Wrapper monomorph bleibt.
    const coords: Array<[number, number]> = selected.track.points.map(
      (p) => [p.lon, p.lat],
    );
    // Wave 14ca-fix64-v2: Fallback fuer Tours ohne persistierte
    // routeSegments. User-Bug: "der grosse fix hat nicht geklappt — ich
    // sehe die Trails im Hoehenprofil nicht als Farbblock". Ursache:
    // CustomTours die VOR dem Save-Patch erstellt wurden haben das
    // Feld undefined. Wir leiten es in dem Fall live ab via Snap-to-
    // nearest-trail. Performance ist akzeptabel (~50-100ms einmalig
    // beim Oeffnen).
    //
    // Wave 14cl: Featured Tours haben NIE persistierte routeSegments —
    // sie kommen als blanke GPX vom Admin. Hier IMMER deriveRouteSegments
    // laufen lassen. selected.selectedTrailIds (von featuredTourAsTrail
    // aus tour.trailIds geliefert) wirkt als Allow-List in
    // routeSegmentsToElevationSegments unten — alles ausserhalb wird zu
    // 'connector' demoviert.
    let segments = selected.routeSegments;
    if (!segments || segments.length === 0) {
      segments = deriveRouteSegments(coords, trails, 25);
    }
    if (!segments || segments.length === 0) return [];
    // Wave 14ca-fix64-v3: Trail-Whitelist aus User-Auswahl beim Planen.
    // Trails die nicht in selectedTrailIds stehen werden in der
    // routeSegmentsToElevationSegments-Funktion zu Connector demoviert
    // (kein Badge, graue Profil-Linie). User-Wunsch: "ich will nur
    // Trails sehen die ich primaer fahren wollte".
    return routeSegmentsToElevationSegments(
      segments,
      coords,
      trails,
      selected.selectedTrailIds,
    );
  }, [selected, trails, runs, trailsById]);
  // Eindeutige Trails fuer die Badge-Liste — Reihenfolge erste Begegnung
  // entlang der Route.
  const uniqueTrails = useMemo(
    () => uniqueTrailsFromSegments(elevationSegments),
    [elevationSegments],
  );
  // Welcher Trail wird gerade ge-scrubbt (oder null wenn kein Scrub aktiv,
  // oder Scrub auf Connector/Bridge)? Wird unter dem Profil als kleiner
  // Hint-Text angezeigt damit der User sieht "an dieser Stelle: Trail X".
  const [scrubbedSegment, setScrubbedSegment] = useState<
    ReturnType<typeof routeSegmentsToElevationSegments>[number] | null
  >(null);
  // Click-Highlight: User tappt einen Trail-Badge → wir highlighten den
  // entsprechenden Bereich in der Map (Polyline-Highlight-Layer) UND im
  // Profil (kraeftigerer Stroke). null = kein Highlight aktiv.
  const [highlightedTrailIdInRoute, setHighlightedTrailIdInRoute] = useState<
    string | null
  >(null);
  // Map-Marker für die Scrub-Position. Wir halten ihn als Ref, damit wir nicht
  // bei jedem Pointer-Move einen neuen Marker erstellen — nur LngLat updaten.
  const scrubMarkerRef = useRef<maplibregl.Marker | null>(null);

  useEffect(() => {
    const map = mapBus.get();
    if (!map) return;

    const removeMarker = () => {
      if (scrubMarkerRef.current) {
        scrubMarkerRef.current.remove();
        scrubMarkerRef.current = null;
      }
    };

    if (scrubMeters == null || !selected || !cumDist) {
      removeMarker();
      return;
    }
    const pts = selected.track.points;
    if (pts.length < 2) {
      removeMarker();
      return;
    }
    const total = cumDist[cumDist.length - 1] ?? 0;
    if (total <= 0) {
      removeMarker();
      return;
    }
    const target = Math.max(0, Math.min(total, scrubMeters));
    // Lineare Suche nach dem umgebenden Segment — ausreichend bei <2k Punkten.
    let segI = 0;
    while (segI < cumDist.length - 1 && (cumDist[segI + 1] ?? 0) < target) segI++;
    const dStart = cumDist[segI] ?? 0;
    const dEnd = cumDist[segI + 1] ?? dStart;
    const segLen = dEnd - dStart;
    const t = segLen > 0 ? (target - dStart) / segLen : 0;
    const a = pts[segI]!;
    const b = pts[Math.min(segI + 1, pts.length - 1)]!;
    const lat = a.lat + (b.lat - a.lat) * t;
    const lon = a.lon + (b.lon - a.lon) * t;

    if (scrubMarkerRef.current) {
      scrubMarkerRef.current.setLngLat([lon, lat]);
    } else {
      // Weißer Dot mit teal-Aura — visuell klar abgegrenzt vom roten GPS-Pos
      // und blauen User-Triangle. Das matcht den Scrub-Cursor im Profil (auch
      // weiß), sodass der User die Verbindung "Profil-Cursor ↔ Karten-Marker"
      // sofort sieht.
      const el = document.createElement('div');
      el.style.width = '14px';
      el.style.height = '14px';
      el.style.borderRadius = '50%';
      el.style.background = '#ffffff';
      el.style.border = '2px solid #0d9488'; // turbo-500 teal
      el.style.boxShadow = '0 0 0 6px rgba(13,148,136,0.25), 0 1px 3px rgba(0,0,0,0.4)';
      el.style.pointerEvents = 'none';
      // Wave 14bn: occludedOpacity:1 — siehe LocateControl-Kommentar.
      scrubMarkerRef.current = new maplibregl.Marker({
        element: el,
        pitchAlignment: 'map',
        opacityWhenCovered: '1',
      })
        .setLngLat([lon, lat])
        .addTo(map);
    }
  }, [scrubMeters, selected, cumDist]);

  // Cleanup: wenn die Page unmountet, den Marker mit abräumen.
  useEffect(() => {
    return () => {
      if (scrubMarkerRef.current) {
        scrubMarkerRef.current.remove();
        scrubMarkerRef.current = null;
      }
    };
  }, []);

  // ─── POIs: Trinkwasser ─────────────────────────────────────────────────
  // Wenn der User im POI-Panel Trinkwasser togglet, laden wir die Daten (Cache
  // first, Overpass fallback) und rendern sie auf der Karte.
  //
  // Wave 14bo (Energiespar-Tipp #6): komplette Umstellung von DOM-Markern auf
  // einen GL-Symbol-Layer.
  //
  // Vorher: für jede sichtbare Fountain ein DOM-Element mit absoluter
  // Positionierung und 60fps CSS-Transform-Sync durch maplibregl.Marker. Bei
  // ~50 sichtbaren Markern waren das ~50 DOM-Nodes, die der Browser-Compositor
  // pro Frame auf dem WebGL-Canvas layern musste. Dazu unser eigener O(N)-
  // Viewport-Culling-Sweep auf jedem moveend. Alles in allem gemessene 4-6 %
  // CPU-Mehrlast auf iPhone 17 Pro Max wenn Drinkwater aktiv war.
  //
  // Jetzt: EINE GeoJSON-Source mit allen POIs, EIN Symbol-Layer mit einem
  // Canvas-gezeichneten Icon (Sky-500-Punkt + weißer Rand + 💧-Emoji). MapLibre
  // erledigt Viewport-Culling, Zoom-Min-Filtering (minzoom im Layer) und Icon-
  // Placement nativ auf der GPU. Kein DOM mehr, keine reconcile-Loop, kein
  // moveend/zoomend-Listener.
  //
  // POI_MIN_ZOOM kontrollieren wir direkt über `minzoom` auf dem Layer —
  // darunter rendert MapLibre schon gar nicht erst. Das war früher eine
  // explizite "if (zoom < min) {remove all}"-Logik, jetzt eine Config-Zeile.
  //
  // Style-Switch: MapLibre verwirft beim Style-Wechsel ALLE Sources, Layers
  // und registrierten Icons. Wir lauschen daher auf 'styledata' und
  // re-registrieren alles. Dieselbe Strategie wie bei der Hillshade-Visibility.
  const [drinkingWaterOn] = usePoiDrinkingWater();
  const [poiError, setPoiError] = useState<string | null>(null);

  // POI-Migration: wenn an (default), kommen drinking_water + fast_food aus den
  // Vektor-Tiles (poi-Source-Layer) statt aus dem Blob-Download. Der Blob-Pfad
  // bleibt als Fallback erhalten und übernimmt wieder, sobald der Flag aus ist.
  const [vectorPoiEnabled] = useVectorPoiEnabled();

  // ULTRA: generische Overpass-Kategorien (fast_food, supermarket, …).
  const overpassToggles = useOverpassPoiToggles();
  const enabledOverpassCats: PoiCategoryId[] = OVERPASS_EXTRA_CATEGORIES.filter(
    (c) => overpassToggles[c].enabled,
  );
  // Stabiler Dep-Key für den Render-Effekt (Array-Identität wechselt jeden Render).
  const enabledOverpassKey = enabledOverpassCats.join(',');

  // Wave 14cf-fix19: POI-Detail-Popup. Wenn der User auf einen POI tappt,
  // legen wir hier die Anzeige-Daten ab; der untere JSX-Teil rendert eine
  // kleine Karte mit Name, Notiz und Oeffnungszeiten. Click ausserhalb
  // des Popups setzt es auf null zurueck.
  const navigate = useNavigate();
  // ULTRA: id des aktuell angetippten POI → Highlight-Ring auf der Karte.
  const [selectedPoiId, setSelectedPoiId] = useState<string | null>(null);
  const [poiPopup, setPoiPopup] = useState<{
    category: string;
    name: string;
    note: string;
    openingHours: string;
    openNow?: string;
    source: string;
    website?: string;
    phone?: string;
    lat: number;
    lon: number;
  } | null>(null);

  // Highlight-Ring auf die ausgewählte POI-id setzen (auf aktueller + künftiger
  // Map). Wird in onPoiClick gesetzt, beim Schließen des Popups genullt.
  useEffect(() => {
    const apply = (m: maplibregl.Map | null) => {
      if (m) {
        try {
          setOverpassPoiHighlight(m, selectedPoiId);
        } catch {
          /* Layer noch nicht da — egal */
        }
      }
    };
    apply(mapBus.get());
    return mapBus.subscribe(apply);
  }, [selectedPoiId]);

  // ULTRA: Pit-Stop-"Voraus"-Treffer als anklickbare Marker auf der Karte
  // (Name + Entfernung + grober Umweg). Tap → Route dorthin.
  useEffect(() => {
    let markers: maplibregl.Marker[] = [];
    let boundMap: maplibregl.Map | null = null;
    const clearMarkers = () => {
      for (const m of markers) m.remove();
      markers = [];
    };
    const makeEl = (
      origin: { lat: number; lon: number },
      it: PitstopResults['items'][number],
    ): HTMLElement => {
      const emoji = POI_CATEGORIES[it.category as PoiCategoryId]?.emoji ?? '📍';
      const el = document.createElement('button');
      el.type = 'button';
      el.style.cssText =
        'display:flex;align-items:center;gap:6px;max-width:200px;padding:5px 9px;border-radius:9999px;border:1px solid rgba(56,189,248,0.6);background:rgba(15,23,42,0.92);color:#fff;font-size:12px;font-weight:600;line-height:1.1;box-shadow:0 2px 8px rgba(0,0,0,0.4);cursor:pointer;';
      const ico = document.createElement('span');
      ico.textContent = emoji;
      const txt = document.createElement('span');
      txt.style.cssText = 'display:flex;flex-direction:column;align-items:flex-start;overflow:hidden;';
      const nm = document.createElement('span');
      nm.textContent = it.name;
      nm.style.cssText = 'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:150px;';
      const meta = document.createElement('span');
      meta.style.cssText = 'font-weight:400;color:#7dd3fc;font-size:11px;';
      meta.textContent =
        it.detourKm != null
          ? `Umweg +${it.detourKm.toFixed(1)} km${it.detourHm != null ? ` · +${Math.round(it.detourHm)} hm` : ''}`
          : `${(it.distM / 1000).toFixed(1)} km voraus`;
      txt.appendChild(nm);
      txt.appendChild(meta);
      el.appendChild(ico);
      el.appendChild(txt);
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const start: [number, number] = [origin.lon, origin.lat];
        const rr = routeBus.get();
        if (rr) raceRefBus.set(rr); // Originalstrecke als Referenz merken
        // ULTRA-Fix: NICHT den skip-optimalen Backend-Wiedereinstieg nehmen
        // (der überspringt ganze Strecken-Schleifen, weil die Metrik die
        // gesparte Renn-Distanz gutschreibt → führt quer durch die Stadt zu
        // einem weit entfernten Rejoin). Stattdessen wie beim Karten-Popup:
        // hin zum POI und am NÄCHSTGELEGENEN Punkt vorne wieder auf die Strecke.
        // So bleibt die ganze Rennstrecke gefahren, der Umweg ist kurz.
        const target = buildPitstopTarget(start, it.lon, it.lat, it.name);
        pitstopTargetBus.publish(target);
        pitstopResultsBus.clear();
        navigate('/navi/custom');
      });
      return el;
    };
    const render = (m: maplibregl.Map | null, r: PitstopResults | null) => {
      clearMarkers();
      if (!m || !r || !r.items.length) return;
      const bounds = new maplibregl.LngLatBounds(
        [r.origin.lon, r.origin.lat],
        [r.origin.lon, r.origin.lat],
      );
      for (const it of r.items) {
        const mk = new maplibregl.Marker({
          element: makeEl(r.origin, it),
          anchor: 'bottom',
          // sonst macht 3D-Terrain die Marker durchsichtig ("verdeckt").
          opacityWhenCovered: '1',
        })
          .setLngLat([it.lon, it.lat])
          .addTo(m);
        markers.push(mk);
        bounds.extend([it.lon, it.lat]);
      }
      try {
        m.fitBounds(bounds, { padding: 90, maxZoom: 14, duration: 800 });
      } catch {
        /* style not ready */
      }
    };
    const bind = (m: maplibregl.Map | null) => {
      boundMap = m;
      render(m, pitstopResultsBus.get());
    };
    bind(mapBus.get());
    const unsubBus = mapBus.subscribe(bind);
    const unsubRes = pitstopResultsBus.subscribe((r) => render(boundMap ?? mapBus.get(), r));
    return () => {
      unsubBus();
      unsubRes();
      clearMarkers();
    };
  }, [navigate]);

  // ULTRA: Originalstrecken-Referenz (raceRefBus) als blasse orange Linie auf
  // der Karte — bleibt sichtbar während der Umweg-Fahrt, damit man immer sieht
  // wo der eigentliche Kurs langgeht. Robust gegen Style-Reloads (styledata).
  useEffect(() => {
    const SRC = 'adt-raceref';
    const LYR = 'adt-raceref-line';
    let boundMap: maplibregl.Map | null = null;
    let onStyle: (() => void) | null = null;
    const apply = (m: maplibregl.Map | null) => {
      if (!m) return;
      const route = raceRefBus.get();
      const coords = route ? route.map(([lat, lon]) => [lon, lat]) : [];
      const data: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {},
      };
      try {
        const src = m.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
        if (!src) {
          if (!coords.length) return;
          m.addSource(SRC, { type: 'geojson', data });
          m.addLayer({
            id: LYR,
            type: 'line',
            source: SRC,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': '#f59e0b',
              'line-width': 4,
              'line-opacity': 0.4,
              'line-dasharray': [1.5, 1.5],
            },
          });
        } else {
          src.setData(data);
        }
      } catch {
        /* Style nicht bereit */
      }
    };
    const bind = (m: maplibregl.Map | null) => {
      if (boundMap === m) return;
      if (boundMap && onStyle) {
        try {
          boundMap.off('styledata', onStyle);
        } catch {
          /* egal */
        }
      }
      boundMap = m;
      onStyle = null;
      if (!m) return;
      onStyle = () => apply(m);
      m.on('styledata', onStyle);
      apply(m);
    };
    bind(mapBus.get());
    const unsubMap = mapBus.subscribe(bind);
    const unsubRef = raceRefBus.subscribe(() => apply(boundMap));
    return () => {
      unsubMap();
      unsubRef();
      if (boundMap && onStyle) {
        try {
          boundMap.off('styledata', onStyle);
        } catch {
          /* egal */
        }
      }
    };
  }, []);

  // ULTRA: Routing-Graph vorwärmen. Der erste Pit-Stop-Umweg in einem ~500km-
  // Segment cold-baut den kompletten Segment-Graph in den RAM (~10s, blockierend)
  // — NICHT wegen des Radius, sondern weil der Graph pro Segment geladen wird.
  // Wenn eine Rennstrecke aktiv ist + Position bekannt, feuern wir EINMAL pro
  // Gegend eine winzige Hintergrund-Route, damit der Graph schon warm ist, bevor
  // du einen Stopp suchst → dann ist die Berechnung sub-sekündlich.
  const warmedCellRef = useRef<string | null>(null);
  useEffect(() => {
    const tryWarm = () => {
      const route = routeBus.get();
      const p = positionBus.get();
      if (!route || !p) return;
      const cell = `${activeRegion}:${Math.round(p.latitude)}_${Math.round(p.longitude)}`;
      if (warmedCellRef.current === cell) return;
      warmedCellRef.current = cell;
      void postPinRoute({
        start: [p.longitude, p.latitude],
        end: [p.longitude + 0.003, p.latitude],
        mode: 'road',
        region: activeRegion,
      }).catch(() => {
        /* Warm-Up ist Best-Effort */
      });
    };
    tryWarm();
    return positionBus.subscribe(tryWarm);
  }, [activeRegion]);

  // ULTRA Test-Standort: beim Mount wiederherstellen (falls im Profil aktiv).
  const [simEnabled] = useBoolPreference(SIM_ENABLED_PREF, false);
  useEffect(() => {
    restoreSimIfEnabled();
  }, []);

  // ULTRA: simulierte Position dauerhaft als 🧪-Marker auf der Karte zeigen,
  // solange ein Test-Standort gesetzt ist. Folgt Sim-Änderungen + Map-Wechsel.
  useEffect(() => {
    let marker: maplibregl.Marker | null = null;
    let boundMap: maplibregl.Map | null = null;
    const update = () => {
      const sim = positionBus.isSimulated() ? positionBus.get() : null;
      if (!boundMap || !sim) {
        if (marker) {
          marker.remove();
          marker = null;
        }
        return;
      }
      if (!marker) {
        const el = document.createElement('div');
        el.style.cssText =
          'width:24px;height:24px;border-radius:9999px;background:#d946ef;border:3px solid #fff;box-shadow:0 0 0 2px rgba(217,70,239,0.45),0 2px 6px rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;font-size:12px;';
        el.textContent = '🧪';
        el.title = 'Test-Standort';
        marker = new maplibregl.Marker({
          element: el,
          anchor: 'center',
          opacityWhenCovered: '1',
        });
      }
      marker.setLngLat([sim.longitude, sim.latitude]).addTo(boundMap);
    };
    const bind = (m: maplibregl.Map | null) => {
      if (marker) {
        marker.remove();
        marker = null;
      }
      boundMap = m;
      update();
    };
    bind(mapBus.get());
    const unsubMap = mapBus.subscribe(bind);
    const unsubPos = positionBus.subscribe(update);
    return () => {
      unsubMap();
      unsubPos();
      if (marker) marker.remove();
    };
  }, []);

  // ULTRA: Long-Press auf die Karte → Bestätigungs-Popup → als Test-Standort
  // speichern. Nur wenn "Andere Position simulieren" im Profil aktiv ist.
  const [pendingSim, setPendingSim] = useState<{ lat: number; lon: number } | null>(null);
  useEffect(() => {
    if (!simEnabled) return;
    let boundMap: maplibregl.Map | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let downPt: maplibregl.Point | null = null;
    let downLngLat: maplibregl.LngLat | null = null;
    const clear = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      downPt = null;
      downLngLat = null;
    };
    const onDown = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
      downLngLat = e.lngLat;
      downPt = e.point;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (downLngLat) setPendingSim({ lat: downLngLat.lat, lon: downLngLat.lng });
        timer = null;
      }, 600);
    };
    const onMove = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
      if (
        downPt &&
        e.point &&
        (Math.abs(e.point.x - downPt.x) > 12 || Math.abs(e.point.y - downPt.y) > 12)
      )
        clear();
    };
    const onCancel = () => clear();
    const wire = (m: maplibregl.Map, method: 'on' | 'off') => {
      const fn = m[method].bind(m) as (ev: string, cb: (e: unknown) => void) => void;
      fn('mousedown', onDown as (e: unknown) => void);
      fn('touchstart', onDown as (e: unknown) => void);
      fn('mousemove', onMove as (e: unknown) => void);
      fn('touchmove', onMove as (e: unknown) => void);
      fn('mouseup', onCancel as (e: unknown) => void);
      fn('touchend', onCancel as (e: unknown) => void);
      fn('dragstart', onCancel as (e: unknown) => void);
    };
    const bind = (m: maplibregl.Map | null) => {
      if (boundMap === m) return;
      if (boundMap) {
        try {
          wire(boundMap, 'off');
        } catch {
          /* destroyed */
        }
      }
      boundMap = m;
      if (!m) return;
      wire(m, 'on');
    };
    bind(mapBus.get());
    const unsub = mapBus.subscribe(bind);
    return () => {
      unsub();
      clear();
      if (boundMap) {
        try {
          wire(boundMap, 'off');
        } catch {
          /* destroyed */
        }
      }
    };
  }, [simEnabled]);

  useEffect(() => {
    if (!drinkingWaterOn) {
      // Wave 14ca-fix23: Defensive removal — auf der aktuellen Map UND
      // beim nächsten Map-Bus-Change. Vorher: wenn der User toggelt,
      // bevor mapBus gesetzt war, blieb der Layer für immer drauf
      // (re-add bei späterem mapBus-Set). Jetzt: subscribe damit jede
      // future Map auch gecleart wird, solange drinkingWaterOn=false.
      console.info('[poi-symbol] off — removing layer');
      const m0 = mapBus.get();
      if (m0) removePoiSymbol(m0);
      const unsubOff = mapBus.subscribe((m) => {
        if (m) removePoiSymbol(m);
      });
      setPoiError(null);
      return () => {
        unsubOff();
      };
    }

    // Wave 14ca-fix10: vereinfachte applyOnce-Logik. Vorheriges Setup hatte
    // einen Race zwischen run() (Features-Load) und mapBus-Subscribe (Map-
    // Bind) — wenn die Reihenfolge unglücklich lief blieb der Layer leer.
    // Jetzt: drei Trigger (features-loaded, map-bound, styledata-fired)
    // rufen alle dieselbe applyOnce(); sie checkt ob ALLE Voraussetzungen
    // erfüllt sind und feuert nur dann. Dadurch egal in welcher Reihenfolge
    // die Async-Schritte fertig werden.
    let cancelled = false;
    let features: GeoJSON.Feature<GeoJSON.Point>[] = [];
    let featuresLoaded = false;
    let boundMap: maplibregl.Map | null = null;
    let onStyleData: (() => void) | null = null;

    // Wave 14ca-fix20: isStyleLoaded()-Check rausgenommen — der ist während
    // setStyle-Sequenzen unzuverlässig (returnt false obwohl style.load
    // bereits gefeuert hat). Stattdessen einfach apply versuchen, MapLibre's
    // "Style is not done loading"-Throw mit try-catch abfangen, und beim
    // nächsten Trigger erneut probieren.
    let applyCount = 0;
    const applyOnce = (trigger: string) => {
      if (cancelled) return;
      const map = boundMap;
      if (!map) return;
      if (!featuresLoaded) return;
      try {
        ensurePoiIcon(map);
        ensurePoiSourceAndLayer(map);
        setPoiData(map, features);
        applyCount++;
        if (applyCount === 1) {
          console.info(
            `[poi-symbol] applied ${trigger} (${features.length} features)`,
          );
        }
      } catch (err) {
        // Style noch nicht ready → nächster Trigger holt's nach.
        // Kein UI-Error.
      }
    };

    let onIdle: (() => void) | null = null;
    let onPoiClick:
      | ((
          e: maplibregl.MapMouseEvent & {
            features?: maplibregl.MapGeoJSONFeature[];
          },
        ) => void)
      | null = null;
    const bindMap = (map: maplibregl.Map | null) => {
      if (boundMap === map) return;
      // unbind alte Map
      if (boundMap) {
        try {
          if (onStyleData) boundMap.off('styledata', onStyleData);
          if (onIdle) boundMap.off('idle', onIdle);
          if (onPoiClick) boundMap.off('click', POI_DW_LAYER, onPoiClick);
        } catch {
          /* destroyed map — egal */
        }
      }
      boundMap = map;
      onStyleData = null;
      onIdle = null;
      onPoiClick = null;
      if (!map) return;
      onStyleData = () => applyOnce('styledata');
      onIdle = () => applyOnce('idle');
      // Wave 14cf-fix19: Tap auf einen Drinking-Water-POI → Detail-Popup.
      onPoiClick = (e) => {
        const feat = e.features?.[0];
        if (!feat) return;
        const props = feat.properties || {};
        const geom = feat.geometry as GeoJSON.Point | undefined;
        const coords = geom?.coordinates as [number, number] | undefined;
        setSelectedPoiId(null); // anderer Layer → Overpass-Highlight aus
        setPoiPopup({
          category: 'drinking_water',
          name: String(props.name ?? ''),
          note: String(props.note ?? ''),
          openingHours: String(props.openingHours ?? ''),
          source: String(props.source ?? ''),
          lat: coords?.[1] ?? 0,
          lon: coords?.[0] ?? 0,
        });
      };
      map.on('styledata', onStyleData);
      // 'idle' fires when map is fully settled (Style + Sources + Tiles) —
      // garantiert ein finaler Trigger. Wave 14ca-fix20.
      map.on('idle', onIdle);
      map.on('click', POI_DW_LAYER, onPoiClick);
      // sofort versuchen — falls Style schon geladen ist, läuft's hier durch
      applyOnce('mount');
    };

    // Wave 14cf-fix10: alle drei Quellen vereinen — drinking_water-POIs
    // landen unabhängig von der Quelle (OSM / admin / user) im selben
    // Layer mit demselben Sky-blue-Droplet-Icon. User-Bug: ein selbst-
    // markierter Brunnen sah anders aus als OSM-Brunnen UND verschwand
    // nicht beim drinkingWater-Toggle off. Einheitliche Kategorie =
    // einheitliche Anzeige + ein gemeinsamer Toggle.
    const reloadFeatures = async (): Promise<void> => {
      try {
        const [osmRes, adminRes, userRes] = await Promise.all([
          loadPoisWithRefresh('drinking_water'),
          loadAdminPoisWithRefresh(),
          listUserPois(),
        ]);
        if (cancelled) return;
        if (osmRes.error && osmRes.pois.length === 0) {
          setPoiError(osmRes.error);
        } else {
          setPoiError(null);
        }
        // Wave 14cf-fix19: properties carry name + note + openingHours,
        // damit der Click-Handler das Detail-Popup mit den richtigen Feldern
        // rendern kann. category ist immer 'drinking_water' fuer diesen Layer.
        // POI-Migration: bei aktivem Vektor-Rendering kommen die OSM-Brunnen aus
        // den Vektor-Tiles (vectorPoiLayer) — hier NICHT nochmal aus dem Blob
        // rendern (sonst Doppel-Pins). Admin- + User-Brunnen bleiben unberührt,
        // die gibt es nur im Blob-Mix. Flag aus → wie bisher OSM aus dem Blob.
        const osmFeatures: GeoJSON.Feature<GeoJSON.Point>[] = vectorPoiEnabled
          ? []
          : osmRes.pois.map((p) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
              properties: {
                id: p.id,
                category: 'drinking_water',
                name: p.name ?? '',
                note: '',
                openingHours: '',
                source: 'osm',
              },
            }));
        const adminWaterFeatures: GeoJSON.Feature<GeoJSON.Point>[] =
          adminRes.pois
            .filter((p) => p.category === 'drinking_water')
            .map((p) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
              properties: {
                id: p.id,
                category: 'drinking_water',
                name: p.name ?? '',
                note: p.note ?? '',
                openingHours: p.openingHours ?? '',
                source: 'admin',
              },
            }));
        const userWaterFeatures: GeoJSON.Feature<GeoJSON.Point>[] = userRes
          .filter((p) => p.category === 'drinking_water')
          .map((p) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
            properties: {
              id: p.id,
              category: 'drinking_water',
              name: '',
              note: p.note ?? '',
              openingHours: '',
              source: 'user',
            },
          }));
        features = [...osmFeatures, ...adminWaterFeatures, ...userWaterFeatures];
        featuresLoaded = true;
        applyOnce('features-loaded');
      } catch (err) {
        if (!cancelled) setPoiError(err instanceof Error ? err.message : String(err));
      }
    };

    // Initial-Load
    void reloadFeatures();

    // Reload bei jedem 'adt:poi-added'-Event — damit der gerade markierte
    // POI sofort im Layer auftaucht ohne Page-Reload.
    const onPoiAdded = () => {
      void reloadFeatures();
    };
    // Wave 14cf-fix15: 'adt:poi-refresh' = User hat POI-Panel geöffnet.
    // Auch hier triggern wir einen Reload, damit eine frische Trinkwasser-
    // Markierung (admin oder user) sofort sichtbar wird.
    const onPoiRefresh = () => {
      void reloadFeatures();
    };
    window.addEventListener('adt:poi-added', onPoiAdded);
    window.addEventListener('adt:poi-refresh', onPoiRefresh);

    // Trigger 2 — Map binden (sofort + auf jeden Bus-Change)
    bindMap(mapBus.get());
    const unsubBus = mapBus.subscribe(bindMap);

    return () => {
      cancelled = true;
      unsubBus();
      window.removeEventListener('adt:poi-added', onPoiAdded);
      window.removeEventListener('adt:poi-refresh', onPoiRefresh);
      if (boundMap) {
        try {
          if (onStyleData) boundMap.off('styledata', onStyleData);
          if (onIdle) boundMap.off('idle', onIdle);
          if (onPoiClick) boundMap.off('click', POI_DW_LAYER, onPoiClick);
        } catch {
          /* destroyed */
        }
      }
      if (boundMap) removePoiSymbol(boundMap);
    };
  }, [drinkingWaterOn, vectorPoiEnabled]);

  // ─── ULTRA: generische Overpass-POIs (alle Kategorien außer drinking_water) ──
  // Eigener Layer (overpassPoiLayer), data-driven Emoji-Icons je Kategorie.
  // Gleiche robuste applyOnce/bindMap-Mechanik wie bei drinking_water. Re-runs
  // wenn sich die Menge aktivierter Kategorien ändert (enabledOverpassKey).
  useEffect(() => {
    // POI-Migration: fast_food kommt bei aktivem Vektor-Rendering aus den
    // Vektor-Tiles (vectorPoiLayer) — hier aus der Blob-Overpass-Pipeline
    // ausklammern, sonst Doppel-Pins. Flag aus → fast_food wieder aus dem Blob.
    const cats = ((enabledOverpassKey ? enabledOverpassKey.split(',') : []) as PoiCategoryId[]).filter(
      (c) => !(vectorPoiEnabled && c === 'fast_food'),
    );
    if (cats.length === 0) {
      const m0 = mapBus.get();
      if (m0) removeOverpassPoiLayer(m0);
      const unsubOff = mapBus.subscribe((m) => {
        if (m) removeOverpassPoiLayer(m);
      });
      return () => {
        unsubOff();
      };
    }

    let cancelled = false;
    let features: GeoJSON.Feature<GeoJSON.Point>[] = [];
    let featuresLoaded = false;
    let boundMap: maplibregl.Map | null = null;
    // ULTRA: Mitte der letzten (Google-)Abfrage — verhindert Refetch-Spam beim
    // Pannen, solange man sich nicht nennenswert weiterbewegt hat.
    let lastFetchCenter: maplibregl.LngLat | null = null;
    let moveTimer: ReturnType<typeof setTimeout> | null = null;
    let onStyleData: (() => void) | null = null;
    let onIdle: (() => void) | null = null;
    let onMoveEnd: (() => void) | null = null;
    let onPoiClick:
      | ((e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => void)
      | null = null;

    const applyOnce = () => {
      if (cancelled) return;
      const map = boundMap;
      if (!map || !featuresLoaded) return;
      try {
        ensureOverpassPoiIcons(map);
        ensureOverpassPoiSourceAndLayer(map);
        setOverpassPoiData(map, features);
      } catch {
        // Style noch nicht ready → nächster Trigger holt's nach.
      }
    };

    const bindMap = (map: maplibregl.Map | null) => {
      if (boundMap === map) return;
      if (boundMap) {
        try {
          if (onStyleData) boundMap.off('styledata', onStyleData);
          if (onIdle) boundMap.off('idle', onIdle);
          if (onMoveEnd) boundMap.off('moveend', onMoveEnd);
          if (onPoiClick) boundMap.off('click', OVERPASS_POI_LAYER, onPoiClick);
        } catch {
          /* destroyed map — egal */
        }
      }
      boundMap = map;
      onStyleData = null;
      onIdle = null;
      onMoveEnd = null;
      onPoiClick = null;
      if (!map) return;
      onStyleData = () => applyOnce();
      onIdle = () => applyOnce();
      onPoiClick = (e) => {
        const feat = e.features?.[0];
        if (!feat) return;
        const props = feat.properties || {};
        const geom = feat.geometry as GeoJSON.Point | undefined;
        const coords = geom?.coordinates as [number, number] | undefined;
        setSelectedPoiId(props.id ? String(props.id) : null);
        setPoiPopup({
          category: String(props.category ?? ''),
          name: String(props.name ?? ''),
          note: '',
          openingHours: String(props.openingHours ?? ''),
          openNow: String(props.openNow ?? ''),
          source: String(props.source ?? 'osm'),
          website: props.website ? String(props.website) : undefined,
          phone: props.phone ? String(props.phone) : undefined,
          lat: coords?.[1] ?? 0,
          lon: coords?.[0] ?? 0,
        });
      };
      // ULTRA: beim Pannen Google-POIs für den neuen Ausschnitt nachladen
      // (nur online + Google-Kategorie aktiv, debounced, und nur wenn die
      // Mitte sich >3 km bewegt hat → kein Call-Spam/Kosten).
      const hasGoogleCat = cats.some((c) => GOOGLE_LIVE_CATEGORIES.has(c));
      onMoveEnd = () => {
        if (!hasGoogleCat) return;
        if (moveTimer) clearTimeout(moveTimer);
        moveTimer = setTimeout(() => {
          const m = boundMap;
          if (!m || cancelled) return;
          const c = m.getCenter();
          if (
            lastFetchCenter &&
            haversine(lastFetchCenter.lat, lastFetchCenter.lng, c.lat, c.lng) < 3000
          )
            return;
          void reloadFeatures();
        }, 1200);
      };
      map.on('styledata', onStyleData);
      map.on('idle', onIdle);
      map.on('moveend', onMoveEnd);
      map.on('click', OVERPASS_POI_LAYER, onPoiClick);
      applyOnce();
    };

    // ULTRA: Sichtradius (m) aus dem Viewport — Mitte→Ecke, gecappt auf Googles
    // 50-km-Limit, min 2 km.
    const viewportRadiusM = (m: maplibregl.Map): number => {
      try {
        const c = m.getCenter();
        const ne = m.getBounds().getNorthEast();
        return Math.min(
          50000,
          Math.max(2000, Math.round(haversine(c.lat, c.lng, ne.lat, ne.lng))),
        );
      } catch {
        return 15000;
      }
    };

    // Eine Kategorie laden: online + Google-bevorzugt → Live-Google um die
    // Kartenmitte (frisch/korrekt), sonst/bei Fehler → OSM-Offline-Cache.
    const fetchCat = async (
      cat: PoiCategoryId,
    ): Promise<{ pois: POI[]; source: 'google' | 'osm' }> => {
      if (GOOGLE_LIVE_CATEGORIES.has(cat)) {
        const m = boundMap ?? mapBus.get();
        const c = m?.getCenter();
        if (m && c) {
          try {
            // cache-first (auch offline), sonst Live-Call wenn online.
            const g = await getGooglePois(cat, c.lat, c.lng, online, viewportRadiusM(m));
            if (g.length) return { pois: g, source: 'google' };
          } catch {
            // weiter zu OSM
          }
        }
      }
      const res = await loadPoisWithRefresh(cat);
      return { pois: res.pois, source: 'osm' };
    };

    const reloadFeatures = async (): Promise<void> => {
      try {
        const m0 = boundMap ?? mapBus.get();
        if (m0) lastFetchCenter = m0.getCenter();
        const results = await Promise.all(cats.map(fetchCat));
        if (cancelled) return;
        const all: GeoJSON.Feature<GeoJSON.Point>[] = [];
        results.forEach((res, i) => {
          const cat = cats[i];
          if (!cat) return;
          for (const p of res.pois) {
            const tags = p.tags ?? {};
            all.push({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
              properties: {
                id: p.id,
                category: cat,
                iconKey: overpassIconKey(cat),
                name: p.name ?? '',
                source: res.source,
                openingHours: tags['opening_hours'] ?? '',
                openNow: tags['open_now'] ?? '',
                website: tags['website'] ?? tags['contact:website'] ?? '',
                phone: tags['phone'] ?? tags['contact:phone'] ?? '',
              },
            });
          }
        });
        features = all;
        featuresLoaded = true;
        applyOnce();
      } catch {
        // Fehler still — drinking_water-Effekt zeigt schon poiError bei Bedarf.
      }
    };

    void reloadFeatures();
    const onPoiRefresh = () => {
      void reloadFeatures();
    };
    window.addEventListener('adt:poi-refresh', onPoiRefresh);
    bindMap(mapBus.get());
    const unsubBus = mapBus.subscribe(bindMap);

    return () => {
      cancelled = true;
      if (moveTimer) clearTimeout(moveTimer);
      unsubBus();
      window.removeEventListener('adt:poi-refresh', onPoiRefresh);
      if (boundMap) {
        try {
          if (onStyleData) boundMap.off('styledata', onStyleData);
          if (onIdle) boundMap.off('idle', onIdle);
          if (onMoveEnd) boundMap.off('moveend', onMoveEnd);
          if (onPoiClick) boundMap.off('click', OVERPASS_POI_LAYER, onPoiClick);
        } catch {
          /* destroyed */
        }
        removeOverpassPoiLayer(boundMap);
      }
    };
  }, [enabledOverpassKey, online, vectorPoiEnabled]);

  // ─── POI-Migration: drinking_water + fast_food aus den Vektor-Tiles ─────────
  //
  // Rendert die zwei Kategorien direkt aus dem OpenMapTiles-`poi`-Source-Layer
  // der Basemap (seit z14-Tiles), statt aus dem Blob-Download. Sichtbarkeit folgt
  // denselben Toggles wie das Blob-System (drinkingWaterOn / fast_food-Toggle).
  // Gleiche robuste applyOnce/bindMap-Mechanik wie oben. Flag aus → Layer weg,
  // Blob-System (oben) übernimmt wieder.
  useEffect(() => {
    if (!vectorPoiEnabled) {
      // Flag aus: sicherstellen dass die Vektor-Layer weg sind (falls vorher an).
      const m0 = mapBus.get();
      if (m0) removeVectorPoiLayers(m0);
      const unsubOff = mapBus.subscribe((m) => {
        if (m) removeVectorPoiLayers(m);
      });
      return () => {
        unsubOff();
      };
    }

    const ffOn = enabledOverpassKey.split(',').includes('fast_food');
    let boundMap: maplibregl.Map | null = null;
    let onStyleData: (() => void) | null = null;
    let onIdle: (() => void) | null = null;
    let onClickDw:
      | ((e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => void)
      | null = null;
    let onClickFf:
      | ((e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => void)
      | null = null;

    const applyOnce = () => {
      const map = boundMap;
      if (!map) return;
      try {
        ensureVectorPoiLayers(map);
        setVectorPoiVisibility(map, { drinkingWater: drinkingWaterOn, fastFood: ffOn });
      } catch {
        // Style noch nicht ready → nächster Trigger holt's nach.
      }
    };

    const makeClick =
      () =>
      (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
        const feat = e.features?.[0];
        if (!feat) return;
        const p = vectorPoiFeatureToPopup(feat);
        if (!p) return;
        setSelectedPoiId(p.id);
        setPoiPopup({
          category: p.category,
          name: p.name,
          note: '',
          openingHours: '',
          source: 'osm',
          lat: p.lat,
          lon: p.lon,
        });
      };

    const bindMap = (map: maplibregl.Map | null) => {
      if (boundMap === map) return;
      if (boundMap) {
        try {
          if (onStyleData) boundMap.off('styledata', onStyleData);
          if (onIdle) boundMap.off('idle', onIdle);
          if (onClickDw) boundMap.off('click', VECTOR_POI_DW_LAYER, onClickDw);
          if (onClickFf) boundMap.off('click', VECTOR_POI_FF_LAYER, onClickFf);
        } catch {
          /* destroyed */
        }
      }
      boundMap = map;
      onStyleData = null;
      onIdle = null;
      onClickDw = null;
      onClickFf = null;
      if (!map) return;
      onStyleData = () => applyOnce();
      onIdle = () => applyOnce();
      onClickDw = makeClick();
      onClickFf = makeClick();
      map.on('styledata', onStyleData);
      map.on('idle', onIdle);
      map.on('click', VECTOR_POI_DW_LAYER, onClickDw);
      map.on('click', VECTOR_POI_FF_LAYER, onClickFf);
      applyOnce();
    };

    bindMap(mapBus.get());
    const unsubBus = mapBus.subscribe(bindMap);

    return () => {
      unsubBus();
      if (boundMap) {
        try {
          if (onStyleData) boundMap.off('styledata', onStyleData);
          if (onIdle) boundMap.off('idle', onIdle);
          if (onClickDw) boundMap.off('click', VECTOR_POI_DW_LAYER, onClickDw);
          if (onClickFf) boundMap.off('click', VECTOR_POI_FF_LAYER, onClickFf);
        } catch {
          /* destroyed */
        }
        removeVectorPoiLayers(boundMap);
      }
    };
  }, [vectorPoiEnabled, drinkingWaterOn, enabledOverpassKey]);

  // ─── Custom POIs (alle Kategorien außer drinking_water) ────────────────
  //
  // Wave 14cf-fix10: ein Layer für custom-markierte POIs aller Kategorien
  // außer drinking_water (das wird vom poiSymbol-Layer gerendert, gemischt
  // OSM + admin + user). Hier landen Admin + User-Markierungen für cafe /
  // pranzo / bike_shop / beach / viewpoint — visuell per Kategorie
  // unterscheidbar (eigene Brand-Farbe pro Kategorie), Quelle (admin vs
  // user) wird NICHT mehr unterschieden.
  //
  // Wave 14cf-fix15: Pro-Kategorie-Toggles. `usePoiVisibility()` liest die
  // 5 boolean-Prefs — Features für ausgeschaltete Kategorien werden
  // gefiltert weggeworfen, Features für angeschaltete Kategorien rendern.
  // Zusätzlich auf 'adt:poi-refresh' lauschen, damit das Öffnen des POI-
  // Panels (User möchte einen frisch angelegten POI sehen) den Cache
  // bypasst und vom Server neu lädt.
  const poiVisibility = usePoiVisibility();
  useEffect(() => {
    let cancelled = false;
    let boundMap: maplibregl.Map | null = null;
    let onStyleData: (() => void) | null = null;
    let onIdle: (() => void) | null = null;
    let features: GeoJSON.Feature<GeoJSON.Point>[] = [];

    const applyOnce = () => {
      if (cancelled || !boundMap) return;
      try {
        ensureCustomPoiIcons(boundMap);
        ensureCustomPoiSourceAndLayer(boundMap);
        setCustomPoiData(boundMap, features);
      } catch {
        /* Style noch nicht ready — nächster Trigger holt's nach. */
      }
    };

    const reloadFeatures = async (forceRefresh = false) => {
      // forceRefresh: bypass den 1h-Cache. Wird gesetzt wenn der User das
      // POI-Panel öffnet — dann erwartet er sofort frische Server-Daten.
      const [adminRes, userRes] = await Promise.all([
        forceRefresh ? forceRefreshAdminPois() : loadAdminPoisWithRefresh(),
        listUserPois(),
      ]);
      if (cancelled) return;
      // Kategorie-Whitelist aus den aktuellen Toggle-Werten — ein Set, damit
      // Filter-Lookup O(1) bleibt.
      const enabledCats = new Set<string>();
      if (poiVisibility.cafe) enabledCats.add('cafe');
      if (poiVisibility.pranzo) enabledCats.add('pranzo');
      if (poiVisibility.bike_shop) enabledCats.add('bike_shop');
      if (poiVisibility.beach) enabledCats.add('beach');
      if (poiVisibility.viewpoint) enabledCats.add('viewpoint');

      // Wave 14cf-fix19: name + openingHours mit in Feature-Properties
      // damit der Click-Handler das Detail-Popup damit fuettern kann.
      const adminFeats: GeoJSON.Feature<GeoJSON.Point>[] = adminRes.pois
        .filter((p) => p.category !== 'drinking_water' && enabledCats.has(p.category))
        .map((p) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
          properties: {
            id: p.id,
            category: p.category,
            name: p.name ?? '',
            note: p.note ?? '',
            openingHours: p.openingHours ?? '',
            source: 'admin',
          },
        }));
      const userFeats: GeoJSON.Feature<GeoJSON.Point>[] = userRes
        .filter((p) => p.category !== 'drinking_water' && enabledCats.has(p.category))
        .map((p) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
          properties: {
            id: p.id,
            category: p.category,
            name: '',
            note: p.note ?? '',
            openingHours: '',
            source: 'user',
          },
        }));
      features = [...adminFeats, ...userFeats];
      applyOnce();
    };

    let onPoiClick:
      | ((
          e: maplibregl.MapMouseEvent & {
            features?: maplibregl.MapGeoJSONFeature[];
          },
        ) => void)
      | null = null;
    const bindMap = (map: maplibregl.Map | null) => {
      if (boundMap === map) return;
      if (boundMap) {
        try {
          if (onStyleData) boundMap.off('styledata', onStyleData);
          if (onIdle) boundMap.off('idle', onIdle);
          if (onPoiClick)
            boundMap.off('click', CUSTOM_POI_LAYER, onPoiClick);
        } catch {
          /* destroyed map */
        }
      }
      boundMap = map;
      onStyleData = null;
      onIdle = null;
      onPoiClick = null;
      if (!map) return;
      onStyleData = () => applyOnce();
      onIdle = () => applyOnce();
      // Wave 14cf-fix19: Tap auf einen Custom-POI → Detail-Popup mit
      // Name / Notiz / Oeffnungszeiten.
      onPoiClick = (e) => {
        const feat = e.features?.[0];
        if (!feat) return;
        const props = feat.properties || {};
        const geom = feat.geometry as GeoJSON.Point | undefined;
        const coords = geom?.coordinates as [number, number] | undefined;
        setSelectedPoiId(null); // Custom-Layer → Overpass-Highlight aus
        setPoiPopup({
          category: String(props.category ?? ''),
          name: String(props.name ?? ''),
          note: String(props.note ?? ''),
          openingHours: String(props.openingHours ?? ''),
          source: String(props.source ?? ''),
          lat: coords?.[1] ?? 0,
          lon: coords?.[0] ?? 0,
        });
      };
      map.on('styledata', onStyleData);
      map.on('idle', onIdle);
      map.on('click', CUSTOM_POI_LAYER, onPoiClick);
      applyOnce();
    };

    void reloadFeatures();
    bindMap(mapBus.get());
    const unsubBus = mapBus.subscribe(bindMap);

    const onPoiAdded = () => void reloadFeatures();
    // Wave 14cf-fix15: 'adt:poi-refresh' = User hat das POI-Panel geöffnet.
    // Cache bypassen damit frisch angelegte Admin-POIs sofort sichtbar sind
    // (statt nach 1h Stale-While-Revalidate).
    const onPoiRefresh = () => void reloadFeatures(true);
    window.addEventListener('adt:poi-added', onPoiAdded);
    window.addEventListener('adt:poi-refresh', onPoiRefresh);

    return () => {
      cancelled = true;
      unsubBus();
      window.removeEventListener('adt:poi-added', onPoiAdded);
      window.removeEventListener('adt:poi-refresh', onPoiRefresh);
      if (boundMap) {
        try {
          if (onStyleData) boundMap.off('styledata', onStyleData);
          if (onIdle) boundMap.off('idle', onIdle);
          if (onPoiClick)
            boundMap.off('click', CUSTOM_POI_LAYER, onPoiClick);
        } catch {
          /* destroyed */
        }
        removeCustomPoiSymbol(boundMap);
      }
    };
  }, [poiVisibility.cafe, poiVisibility.pranzo, poiVisibility.bike_shop, poiVisibility.beach, poiVisibility.viewpoint]);

  // ─── Show-on-Map Custom Tours ────────────────────────────────────────────
  //
  // Wave 14cf-fix11: Custom Tours die der User per "Auf Karte anzeigen"-
  // Toggle markiert hat (CustomTrail.showOnMap === true) werden permanent
  // als dünne sky-blue Linie gerendert — auch wenn nicht aktiv selektiert
  // und nicht im Ride-Modus. Mehrere Tours dürfen gleichzeitig sichtbar
  // sein. Reload via CUSTOM_TRAILS_UPDATED_EVENT damit Toggle-Aktionen aus
  // CustomTrailsPage / CustomTrailDetailPage instant durchschlagen.
  //
  // Wave 14cf-fix15: Click-Handler — Tap auf eine sichtbare Custom-Tour-
  // Linie öffnet das Trail-Detail-Sheet (analog zu echten Trails). Vorher
  // konnte der User nur auf echte Trail-Linien klicken; Custom Tours waren
  // visuell sichtbar aber nicht selektierbar. setSelected(customTrailAsTrail
  // (ct)) liefert den gleichen Pseudo-Trail-Wrapper wie wenn man die Tour
  // aus der CustomTrailsPage öffnet.
  useEffect(() => {
    let cancelled = false;
    let boundMap: maplibregl.Map | null = null;
    let onStyleData: (() => void) | null = null;
    let onIdle: (() => void) | null = null;
    let onClick:
      | ((
          e: maplibregl.MapMouseEvent & {
            features?: maplibregl.MapGeoJSONFeature[];
          },
        ) => void)
      | null = null;
    let features: GeoJSON.Feature<GeoJSON.LineString>[] = [];
    let kmMarkerFeatures: GeoJSON.Feature<GeoJSON.Point>[] = [];

    const applyOnce = () => {
      if (cancelled || !boundMap) return;
      try {
        ensureShowOnMapSourceAndLayer(boundMap);
        ensureKmMarkerSourceAndLayer(boundMap);
        setShowOnMapData(boundMap, features);
        setKmMarkerData(boundMap, kmMarkerFeatures);
      } catch {
        /* Style noch nicht ready — nächster Trigger */
      }
    };

    const reload = async () => {
      try {
        const all = await listCustomTrails();
        if (cancelled) return;
        const visible = all.filter((t) => t.showOnMap === true);
        features = visible.map(
          (t): GeoJSON.Feature<GeoJSON.LineString> => ({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: t.coords.map(
                (c) => [c[0], c[1]] as [number, number],
              ),
            },
            // Wave 14cd: `kind: 'custom'` damit der Click-Handler unten
            // zwischen Custom- und Featured-Tours unterscheiden kann.
            properties: { id: t.id, name: t.name, kind: 'custom' },
          }),
        );
        // km-Marker pro Tour einsammeln (nur wenn kmMarkers != 'off').
        kmMarkerFeatures = [];
        for (const t of visible) {
          const km = t.kmMarkers ?? 'off';
          if (km === 'off') continue;
          const intervalM = km === '10km' ? 10000 : 1000;
          const coords2d = t.coords.map(
            (c) => [c[0], c[1]] as [number, number],
          );
          const markers = computeKmMarkers(coords2d, intervalM);
          for (const m of markers) {
            // Tour-ID an die Properties haengen — falls wir spaeter
            // pro-Tour-Klick handeln wollen.
            m.properties = { ...(m.properties ?? {}), tourId: t.id };
          }
          kmMarkerFeatures.push(...markers);
        }

        // Wave 14cd: Featured Tours mit per-User showOnMap=true merging.
        // Featured Tours leben server-seitig; per-User-Sichtbarkeit lebt
        // in featuredTourPrefs (IDB). Wir laden beides parallel + fetchen
        // GPX nur für die wirklich sichtbaren (Cache in
        // featuredTourGpxCache vermeidet redundante Fetches bei Re-Renders).
        const [tours, prefsMap] = await Promise.all([
          listFeaturedTours(),
          listFeaturedTourPrefs(),
        ]);
        if (cancelled) return;
        const visibleFeatured = tours.filter(
          (t) => prefsMap.get(t.id)?.showOnMap === true,
        );
        const domParser = new DOMParser();
        for (const tour of visibleFeatured) {
          // Geometrie cachen: wenn schon mal gefetched + geparsed, direkt
          // wiederverwenden. Cache-Key = Tour-ID + gpxFilename (falls Admin
          // die Datei austauscht ohne ID-Wechsel, lädt der Cache neu).
          let coords2d = featuredTourCoordsCache.get(
            `${tour.id}:${tour.gpxFilename}`,
          );
          if (!coords2d) {
            const gpx = await fetchFeaturedTourGpx(tour.gpxFilename);
            if (cancelled) return;
            if (!gpx) continue;
            try {
              const parsed = parseGpx(gpx, { domParser });
              const allPoints = parsed.flatMap((tt) => tt.points);
              if (allPoints.length < 2) continue;
              coords2d = allPoints.map(
                (p) => [p.lon, p.lat] as [number, number],
              );
              featuredTourCoordsCache.set(
                `${tour.id}:${tour.gpxFilename}`,
                coords2d,
              );
            } catch (err) {
              console.warn(
                '[map] featured-tour parse failed',
                tour.id,
                err,
              );
              continue;
            }
          }
          features.push({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: coords2d },
            properties: { id: tour.id, name: tour.name, kind: 'featured' },
          });
          // km-Marker für Featured Tours analog.
          const km = prefsMap.get(tour.id)?.kmMarkers ?? 'off';
          if (km !== 'off') {
            const intervalM = km === '10km' ? 10000 : 1000;
            const markers = computeKmMarkers(coords2d, intervalM);
            for (const m of markers) {
              m.properties = { ...(m.properties ?? {}), tourId: tour.id };
            }
            kmMarkerFeatures.push(...markers);
          }
        }

        applyOnce();
      } catch (err) {
        console.warn('[map] show-on-map tours load failed', err);
      }
    };

    const bindMap = (map: maplibregl.Map | null) => {
      if (boundMap === map) return;
      if (boundMap) {
        try {
          if (onStyleData) boundMap.off('styledata', onStyleData);
          if (onIdle) boundMap.off('idle', onIdle);
          if (onClick) {
            boundMap.off('click', SHOW_ON_MAP_LAYER, onClick);
            boundMap.off('click', SHOW_ON_MAP_LAYER_CASING, onClick);
          }
        } catch {
          /* destroyed map */
        }
      }
      boundMap = map;
      onStyleData = null;
      onIdle = null;
      onClick = null;
      if (!map) return;
      onStyleData = () => applyOnce();
      onIdle = () => applyOnce();
      // Wave 14cf-fix15: Click-Handler für Custom-Tour-Linien.
      // queryRenderedFeatures direkt im Click-Event greift den getroffenen
      // Feature; properties.id ist die CustomTrail-ID. Wir laden die volle
      // Tour async (getCustomTrail) und setzen sie als selected.
      onClick = (e) => {
        const feats = e.features;
        if (!feats || feats.length === 0) return;
        const props = feats[0]?.properties ?? {};
        const id = props.id;
        const kind = props.kind;
        if (typeof id !== 'string' || !id) return;
        // Wave 14cd: kind-discrimination. Custom Tours laden aus dem
        // lokalen IDB, Featured Tours dispatchen via mapIntentsBus (das
        // bestehende Pseudo-ID-Handling weiter oben in MapPage resolved
        // dann den Featured-Tour-Wrapper aus dem Server-Catalog).
        if (kind === 'featured') {
          mapIntentsBus.publish({ featuredTourId: id, resetMode: false });
          return;
        }
        void (async () => {
          try {
            const ct = await getCustomTrail(id);
            if (!cancelled && ct) {
              setSelected(customTrailAsTrail(ct));
            }
          } catch (err) {
            console.warn('[map] custom-tour click load failed', err);
          }
        })();
      };
      map.on('styledata', onStyleData);
      map.on('idle', onIdle);
      // Click auf Linie ODER Casing (User trifft das breitere Casing eher).
      map.on('click', SHOW_ON_MAP_LAYER, onClick);
      map.on('click', SHOW_ON_MAP_LAYER_CASING, onClick);
      applyOnce();
    };

    void reload();
    bindMap(mapBus.get());
    const unsubBus = mapBus.subscribe(bindMap);

    const onUpdated = () => void reload();
    window.addEventListener(CUSTOM_TRAILS_UPDATED_EVENT, onUpdated);
    // Wave 14cd: auch auf Featured-Tour-Prefs-Updates reagieren (User
    // toggelt showOnMap oder km-Marker in der Featured-Tour-Liste).
    window.addEventListener(FEATURED_TOUR_PREFS_UPDATED_EVENT, onUpdated);

    return () => {
      cancelled = true;
      unsubBus();
      window.removeEventListener(CUSTOM_TRAILS_UPDATED_EVENT, onUpdated);
      window.removeEventListener(FEATURED_TOUR_PREFS_UPDATED_EVENT, onUpdated);
      if (boundMap) {
        try {
          if (onStyleData) boundMap.off('styledata', onStyleData);
          if (onIdle) boundMap.off('idle', onIdle);
          if (onClick) {
            boundMap.off('click', SHOW_ON_MAP_LAYER, onClick);
            boundMap.off('click', SHOW_ON_MAP_LAYER_CASING, onClick);
          }
        } catch {
          /* destroyed */
        }
        removeShowOnMapLayer(boundMap);
      }
    };
  }, []);

  // ─── Schlüsselstellen / Crux-Points ──────────────────────────────────────
  //
  // Admin markiert pro Trail Distance-Punkte (s. CachedTrail.cruxPoints). Wir
  // berechnen die Geo-Koordinate jedes Crux-Punkts aus dem Track via
  // cumDist + linearer Interpolation, setzen einen maplibregl.Marker mit
  // Gefahr-Dreieck. Wird NUR beim aktuell selektierten Trail gezeigt — der
  // User braucht keine Crux-Warnungen für Trails die er nicht fährt.
  //
  // Sichtbar in Explore- UND Ride-Mode (User-Wunsch: "trailansicht UND ride
  // ansicht"). Toggle via useShowCruxPoints (Default AN).
  //
  // Marker-Anchor 'bottom' damit die Spitze des Dreiecks die exakte
  // Trail-Position markiert — ähnlich wie ein Push-Pin. Leicht skaliert
  // (30x30) damit's auf 16px-ish-Pixel-Density gut lesbar ist ohne das Route-
  // Line zu verdecken.
  const [showCrux] = useShowCruxPoints();
  const cruxMarkersRef = useRef<maplibregl.Marker[]>([]);
  const clearCruxMarkers = useCallback(() => {
    for (const m of cruxMarkersRef.current) m.remove();
    cruxMarkersRef.current = [];
  }, []);

  useEffect(() => {
    clearCruxMarkers();
    if (!showCrux || !selected || !cumDist) return;
    const pts = selected.track.points;
    if (pts.length < 2) return;
    const total = cumDist[cumDist.length - 1] ?? 0;
    if (total <= 0) return;
    const cruxes = selected.cruxPoints ?? [];
    if (cruxes.length === 0) return;

    const tryRender = (retries: number) => {
      const map = mapBus.get();
      if (!map) {
        if (retries > 0) setTimeout(() => tryRender(retries - 1), 120);
        return;
      }
      for (const c of cruxes) {
        const target = Math.max(0, Math.min(total, c.distanceMeters));
        let segI = 0;
        while (segI < cumDist.length - 1 && (cumDist[segI + 1] ?? 0) < target) segI++;
        const dStart = cumDist[segI] ?? 0;
        const dEnd = cumDist[segI + 1] ?? dStart;
        const segLen = dEnd - dStart;
        const t = segLen > 0 ? (target - dStart) / segLen : 0;
        const a = pts[segI]!;
        const b = pts[Math.min(segI + 1, pts.length - 1)]!;
        const lat = a.lat + (b.lat - a.lat) * t;
        const lon = a.lon + (b.lon - a.lon) * t;

        const el = document.createElement('div');
        // Im Ride-Mode die Warndreiecke VIEL GRÖSSER rendern, damit der Fahrer
        // sie unterwegs nicht übersieht (User-Wunsch Wave 14o). Im Explore-
        // Mode bleibt die kompakte Standardgröße, damit die Übersicht nicht
        // überladen wirkt.
        el.className =
          mode === 'ride' ? 'adt-crux-marker adt-crux-marker--ride' : 'adt-crux-marker';
        el.style.cursor = 'pointer';
        // Tooltip: optional note + immer die Distance-Position damit der User
        // auch sieht "wo am Trail bin ich gerade gewarnt". Wichtig fürs
        // Desktop-Hover; auf Touch zeigen wir den vollen Text im Popup (s.u.).
        const distLabel =
          c.distanceMeters < 1000
            ? `${Math.round(c.distanceMeters)} m`
            : `${(c.distanceMeters / 1000).toFixed(2)} km`;
        el.title = c.note ? `${c.note} · ${distLabel} from start` : `Key section · ${distLabel} from start`;
        el.innerHTML = HAZARD_TRIANGLE_SVG_MARKUP;

        // Wave 14bn: occludedOpacity:1 — siehe LocateControl-Kommentar.
        const marker = new maplibregl.Marker({
          element: el,
          anchor: 'bottom',
          opacityWhenCovered: '1',
        })
          .setLngLat([lon, lat])
          .addTo(map);
        cruxMarkersRef.current.push(marker);

        // --- Click-Popup mit Beschreibung -------------------------------
        // User-Wunsch (Feld-Report): "drauf geklickt (auf das logo)
        // verschwunden. sollte nie verschwinden. viel mehr beim klick drauf,
        // falls gesetzt, die beschreibung des users anzeigen."
        //
        // Wir bauen einen kleinen Popup beim Click auf das Marker-Element.
        // stopPropagation/preventDefault verhindern, dass der Click an die
        // Karte oder an ein darunter liegendes Trail-Layer weiterfließt
        // (→ sonst würde z.B. der Trail deselektiert und das Marker wäre
        // plötzlich "weg").
        // Höhe für die Popup-Anzeige: Server-Wert > SRTM-Interpolation > null.
        // Server-Wert ist, was der User beim Setzen (GPS oder Admin-distanz)
        // gespeichert hat. Falls der null ist (alte Cruxes, Offline-Setup ohne
        // Fix, Admin-UI hat keine SRTM-Daten übermittelt), fallen wir auf das
        // lokal gecachte Höhenprofil zurück — die `elevation`-Daten kommen
        // entweder direkt aus der GPX (wenn die Höhen hat) oder aus dem SRTM-
        // Cache den wir beim ersten Trail-Besuch angelegt haben.
        const eleFromStore = c.elevationMeters;
        const eleFromProfile =
          eleFromStore != null
            ? null
            : interpolateElevationAtDistance(
                elevation.elevations,
                elevation.distances,
                c.distanceMeters,
              );
        const popupElevation = eleFromStore ?? eleFromProfile;
        const popupHtml = buildCruxPopupHtml(c, distLabel, popupElevation);
        const popup = new maplibregl.Popup({
          offset: 20, // über der Dreiecks-Spitze anchor=bottom
          closeButton: true,
          closeOnClick: false,
          closeOnMove: false,
          maxWidth: '240px',
          // Scoped class → unser CSS (index.css) stylt den default-Close-Button
          // auf weiß, vergrößert ihn und positioniert ihn sauber in der rechten
          // oberen Ecke unseres dunklen Popup-Cards. User-Wunsch Batch 8:
          // "ich muss das Infofenster schließen können, es braucht ein X oben
          // rechts." MapLibre's Default-X ist da, aber auf weißem Chrome über
          // unserer dunklen Card kaum zu sehen.
          className: 'adt-crux-popup',
        }).setHTML(popupHtml);

        const handleClick = (ev: Event) => {
          ev.stopPropagation();
          ev.preventDefault();
          popup.setLngLat([lon, lat]).addTo(map);
        };
        el.addEventListener('click', handleClick);
        // Touch-Geräte: click feuert auf Tap, aber wir blocken zusätzlich
        // touchend-propagation damit kein MapLibre-Map-Tap durchkommt.
        el.addEventListener('touchend', (ev) => ev.stopPropagation());
        el.addEventListener('mousedown', (ev) => ev.stopPropagation());

        // Beim Marker-Remove auch Popup abräumen.
        const origRemove = marker.remove.bind(marker);
        marker.remove = () => {
          popup.remove();
          return origRemove();
        };
      }
    };
    tryRender(20);

    return () => clearCruxMarkers();
    // cumDist refresht sich mit selected (s. useMemo weiter oben), daher in
    // den Deps mit drin — sonst würde beim Track-Simplify-Update die Position
    // nicht neu berechnet.
    //
    // elevation.elevations + elevation.distances als Deps: wenn die SRTM-
    // Samples nach dem ersten Mount asynchron eintrudeln, müssen wir die
    // Crux-Popups neu bauen, damit cruxes ohne gespeicherte elevationMeters
    // die interpolierte Höhe anzeigen statt "no altitude". Ohne diese Deps
    // würde der erste Render leere Höhen zeigen und nie nachziehen.
    // `mode` als Dep: wenn der User Ride startet/beendet, müssen die Marker
    // neu aufgebaut werden, damit sie auf die passende (größere/kleinere)
    // Variante umschalten.
  }, [showCrux, selected, cumDist, clearCruxMarkers, elevation.elevations, elevation.distances, mode]);

  // Unmount-Safety für die Crux-Marker (Page-Wechsel o. ä.).
  useEffect(() => {
    return () => clearCruxMarkers();
  }, [clearCruxMarkers]);

  // Wenn der Trail wechselt oder das Sheet zugeht, scrub abbrechen.
  useEffect(() => {
    setScrubMeters(null);
  }, [selected?.id]);

  /**
   * Admin-Helper: Server gibt uns nach einem erfolgreichen PATCH die kanonische
   * cruxPoints-Liste zurück. Wir pflegen die in `trails`-State UND `selected`-
   * State ein — so sieht der User die neue Schlüsselstelle sofort auf der
   * Karte (über den showCrux-Effekt) und im Höhenprofil (über die Prop).
   * Die IDB-Zeile wird im admin.ts-Helper bereits aktualisiert.
   */
  const handleCruxesUpdated = useCallback(
    (trailId: string, cruxes: CruxPoint[]) => {
      setTrails((prev) =>
        prev.map((t) => (t.id === trailId ? { ...t, cruxPoints: cruxes } : t)),
      );
      setSelected((prev) =>
        prev && prev.id === trailId ? { ...prev, cruxPoints: cruxes } : prev,
      );
    },
    [],
  );

  // Ride-Button-Action: in Ride-Mode wechseln, hart zentrieren auf Rider,
  // Zoom auf rideZoom. Wenn noch kein GPS-Fix: LocateControl startet via
  // forceActivate ohnehin Tracking und fliegt beim ersten Fix auf den
  // Ziel-Zoom. Zusätzlich: Kompass-Rose auf 'heading' schalten (User-Wunsch:
  // "im ride mode standard handy mit kompass drehen"). Der Call läuft
  // synchron aus diesem click-Handler, daher bleibt der User-Gesture-Kontext
  // für die iOS-Permission erhalten.
  /** Eigentlicher Ride-Start. Vor dieser Funktion sitzt ein Intro-Gate
   *  (startRide) das beim allerersten Aufruf den RideIntroModal zeigt. */
  const doStartRide = () => {
    setMode('ride');
    const locate = locateControlBus.get();
    if (locate) locate.forceActivate(rideZoom);
    const compass = compassBus.get();
    // isUserInitiated=true: dieser Call kommt aus dem click-Handler des Ride-
    // Buttons, also echter User-Gesture. Wenn iOS die Motion-Permission
    // verweigert, soll der sichtbare Deny-Alert mit Safari-Reset-Anleitung
    // kommen (statt silent fail und User wundert sich warum die Karte nicht
    // mitdreht). setMode selbst ist idempotent — wenn Compass gar nicht
    // existiert (compassBus noch nicht befüllt), überspringen wir still.
    if (compass) void compass.setMode('heading', true);
    // Wave 14ca-fix41: GPS-Recording startet automatisch beim RIDE-Tap.
    // Combine-Prompt: wenn die letzte beendete Ride <30min her ist UND ihr
    // letzter Punkt <250m von der aktuellen Position, fragen wir ob die
    // alte Ride fortgesetzt werden soll.
    void startRecording();
  };

  /** Wave 14ch: Public ride-start. Zeigt beim ersten Mal den Intro-Modal
   *  (Display-off-pause-Warning + Pocket-Mode-Tipp), danach Direkt-Start.
   *  Der "Don't show again"-Checkbox-Wert wird im Modal selbst persistiert. */
  const startRide = () => {
    if (shouldShowRideIntro()) {
      // Den eigentlichen Start parken, Modal zeigen. Beim Confirm-Click
      // rendert das Modal die "Got it"-Button und ruft den Callback auf.
      setRideIntroPending(() => doStartRide);
    } else {
      doStartRide();
    }
  };

  /** Wave 14ca-fix50: Featured-Tour-Navigation — prev/next durch die
      gefilterte Tour-Liste. Wenn id im aktuellen Listenstand vorhanden,
      laden wir das wrap'd Trail. Gibt einen Index-Hinweis im UI. */
  const goToFeaturedTour = useCallback(
    async (tour: FeaturedTour) => {
      try {
        const trail = await featuredTourAsTrail(tour, new DOMParser());
        if (trail) setSelected(trail);
      } catch (err) {
        console.warn('[map] go-to featured tour failed', err);
      }
    },
    [],
  );

  /** Wave 14ca-fix41: Recording starten mit optionalem Combine-Prompt.
      Async damit die Combine-Detection den positionBus nutzen kann. Wenn
      kein Combine-Match: einfach neue Ride. iOS-User-Gesture-Kontext bleibt
      erhalten weil wir nur einen await auf IDB-Read machen, nicht auf eine
      GPS-Permission-Anfrage. */
  const startRecording = async () => {
    if (rideRecorder.get().kind === 'recording') return;
    const pos = positionBus.get();
    if (pos) {
      const recent = await findRecentlyEndedRide(
        pos.latitude,
        pos.longitude,
      );
      if (recent) {
        const km = (
          recent.points.reduce((sum, p, i, arr) => {
            if (i === 0) return 0;
            const prev = arr[i - 1]!;
            const R = 6371000;
            const toRad = (d: number) => (d * Math.PI) / 180;
            const dl = toRad(p.lat - prev.lat);
            const dlo = toRad(p.lon - prev.lon);
            const a =
              Math.sin(dl / 2) ** 2 +
              Math.cos(toRad(prev.lat)) *
                Math.cos(toRad(p.lat)) *
                Math.sin(dlo / 2) ** 2;
            return sum + 2 * R * Math.asin(Math.sqrt(a));
          }, 0) / 1000
        ).toFixed(1);
        const ok = window.confirm(
          `Continue your previous ride?\n\nLast ride: ${km} km, ended ${formatRelative(recent.endedAt!)}. Tap OK to combine, Cancel to start a fresh ride.`,
        );
        if (ok) {
          await rideRecorder.resume(recent.id);
          return;
        }
      }
    }
    await rideRecorder.start();
  };

  const stopRide = () => {
    setMode('explore');
    const locate = locateControlBus.get();
    // lockZoom zurück auf explore — damit der nächste Locate-Tap wieder
    // explore-zoom bekommt.
    if (locate) locate.setLockZoom(exploreZoom);
  };

  // Wave 14ca-fix53: Wenn Recording stoppt waehrend wir noch im ride-Mode
  // sind, automatisch zu explore zurueck. Vorher blieb der Mode haengen
  // weil die ActiveRideOverlay-Stop-Pille nur das Recording stoppt, nicht
  // den Map-Mode. Jetzt sind die beiden Konzepte gekoppelt: Stop-Recording
  // = End-Ride-Mode.
  const prevRecorderStateRef = useRef(recorderState.kind);
  useEffect(() => {
    if (
      prevRecorderStateRef.current === 'recording' &&
      recorderState.kind === 'idle' &&
      mode === 'ride'
    ) {
      stopRide();
    }
    prevRecorderStateRef.current = recorderState.kind;
    // mode bewusst NICHT in deps — wir wollen den Cleanup nur bei recorder-
    // State-Transition triggern, nicht wenn der User Mode anderweitig wechselt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorderState.kind]);

  // Trail-Card-Höhe via ResizeObserver tracken, damit das MapLibre-
  // Attribution-Control sich darüber positionieren kann (sonst liegt der
  // Copyright-Strip optisch UNTER dem geöffneten Trail-Sheet, halb-
  // transparent durchscheinend = User-Bug-Report 14ce). Die Höhe wird als
  // CSS-Variable `--trail-card-height` auf den Root-Container gepublisht,
  // styles/index.css picked sie für `.maplibregl-ctrl-bottom-left` auf.
  // Callback-Ref weil die Trail-Card konditional gerendert wird (mount/
  // unmount bei jedem Trail-Open/-Close, plus Switch zwischen explore/
  // ride-Mode).
  const [trailCardHeight, setTrailCardHeight] = useState(0);
  const trailCardObserverRef = useRef<ResizeObserver | null>(null);
  const trailCardRefCallback = useCallback((el: HTMLDivElement | null) => {
    trailCardObserverRef.current?.disconnect();
    trailCardObserverRef.current = null;
    if (!el) {
      setTrailCardHeight(0);
      return;
    }
    // Wave 14ca-fix25: offsetHeight statt contentRect.height — die Card hat
    // pt-2 / pb-3 / safe-bottom-padding plus border-t, was contentRect NICHT
    // mitzählt. Folge: gemessene Höhe um ~30-40px zu klein → Attribution
    // landet INNERHALB der Card statt drüber. offsetHeight inkludiert
    // padding + border (border-box) und ist die wirkliche visible Höhe.
    const ob = new ResizeObserver(() => {
      setTrailCardHeight(el.offsetHeight);
    });
    ob.observe(el);
    // initial sofort messen — ResizeObserver feuert async, das initiale
    // height ist sonst beim ersten Render-Tick noch 0.
    setTrailCardHeight(el.offsetHeight);
    trailCardObserverRef.current = ob;
  }, []);

  return (
    <div
      className="absolute inset-0"
      style={{
        // CSS-Variable von `.maplibregl-ctrl-bottom-left` ausgewertet, plus
        // Fallback 0px wenn kein Trail offen ist.
        ['--trail-card-height' as string]: `${trailCardHeight}px`,
      }}
    >
      {/* Kein `key={style}` — sonst remountet React die ganze MapView und
          springt auf den initialen Center/Zoom zurück. Stattdessen reagiert
          MapView intern via map.setStyle() auf die fallbackStyle-Prop und
          behält dabei Kamera (Position/Zoom/Pitch). */}
      <MapView
        trails={mapTrails}
        fallbackStyle={style}
        selectedTrailId={selected?.id ?? null}
        // Wave 14ah: Pseudo-Run-Selection braucht eine Override-Geometrie für
        // die Aura, weil der Run nicht mehr als ein Stück in `mapTrails`
        // steht (sondern als N farbige Sub-Segmente). Difficulty/colorHex
        // hier sind die NOMINELLEN — nur für die Aura-Tönung. Bei Multi-
        // Difficulty-Runs ist das ein Kompromiss, aber die bunten Linien
        // darunter zeigen ja die wahren Difficulties; die Aura wird einfach
        // den Hauptton (oder den Run-Default) tragen.
        // Wave 14ak: memoized, s. oben — sonst re-applyt der Overlay-Effect
        // bei jedem MapPage-Render (→ Aura flackert).
        selectionOverride={selectionOverride}
        rideNextAuraMeters={mode === 'ride' ? rideNextAuraMeters : 0}
        onTrailClick={handleTrailClick}
      />

      {/* Top-Left Overlay-Stack: RIDE-Badge + Style-Switcher vertikal.
          safe-area-inset-top schiebt den Stack unter Dynamic Island / Notch.
          Wave 14ca-fix64: im Fullscreen komplett ausblenden — User-Wunsch
          "im Fullscreen sollen alle blau eingekreisten Buttons weg sein,
          nur die grauen rechts bleiben". */}
      {!isFullscreen && (
      <div
        className="absolute left-3 flex flex-col gap-2 pointer-events-none"
        style={{
          // Wave 14cf-fix6: bei aktivem Recording rückt der RIDE-Balken
          // ans linke Top — Style-Switcher etc. wandern eine Reihe runter
          // damit sie nicht überlappen.
          top:
            recorderState.kind === 'recording'
              ? 'calc(env(safe-area-inset-top, 0px) + 4rem)'
              : 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
        }}
      >
        {/* Aktuelle Geschwindigkeit — auch OHNE RIDE-Modus sichtbar, sobald GPS
            eine Geschwindigkeit liefert. Beim Recording übernimmt die
            ActiveRideOverlay-Pille (zeigt km/h dort), daher hier nur wenn NICHT
            aufgenommen wird, um Doppelanzeige zu vermeiden. */}
        {recorderState.kind !== 'recording' && (() => {
          const kmh = kmhFromCoords(pos);
          if (kmh == null) return null;
          return (
            <div className="bg-turbo-900/85 backdrop-blur-md border border-turbo-500/40 rounded-lg px-2.5 py-1 shadow-lg self-start flex items-baseline gap-1 pointer-events-none">
              <span className="text-white text-base font-bold tabular-nums leading-none">
                {formatKmh(kmh)}
              </span>
              <span className="text-white/55 text-[11px] font-medium">km/h</span>
            </div>
          );
        })()}

        {/* Mode-Badge: nur im Ride-Mode sichtbar — im Explore-Mode weglassen
            weil's die default-Ansicht ist und keinen Namen braucht. Das X ist
            bewusst ein dicker 44x44-Tap-Target (iOS HIG minimum) — muss auch
            mit Handschuhen auf MTB-Lenker sicher zu treffen sein. Der Badge
            selbst skaliert NICHT (s. Parent-Kommentar). */}
        {/* Wave 14ca-fix53: alte rote RIDE-Pille nur noch sichtbar wenn KEIN
            Recording laeuft. Sobald rideRecorder aktiv ist, uebernimmt die
            ActiveRideOverlay-Pille den Indikator + den Stop-Button. Vorher
            stackten beide Pills uebereinander — User-Feedback "schau mal
            wie das dann oben aussieht...". */}
        {mode === 'ride' && recorderState.kind !== 'recording' && (
          <div className="bg-red-600/90 text-white text-sm font-semibold pl-3 pr-1 py-1 rounded-lg pointer-events-auto flex items-center gap-2 shadow-lg self-start">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span>RIDE</span>
            <button
              onClick={stopRide}
              className="w-11 h-11 flex items-center justify-center rounded-md hover:bg-white/15 active:bg-white/25 transition-colors text-white text-2xl leading-none font-light"
              title="Back to Explore mode"
              aria-label="End ride"
            >
              ✕
            </button>
          </div>
        )}

        {/* Trail-Count ist nach unten gewandert (siehe BottomRightStack) — User-
            Wunsch Batch 3: "der grosse button '8 trails loaded' gehört in
            kleiner schrift zwischen die neue farbskale unten und den
            masstabsbalken, somit 2 icons oben rechts und 2 oben links." */}

        {/* Style-Switcher als horizontales Segmented-Toggle-Pill (User-Wunsch
            Batch 14j): eine dunkle Turbo-Pille, in der alle verfügbaren Kart-
            Styles als kleine Icon-Buttons nebeneinander sitzen. Der aktive
            Style bekommt einen orangen Fill (matcht den Active-Accent von
            TabBar + LocateControl), die inaktiven sind nur dezent-weiß
            angedeutet. Jeder Button ist direkt anklickbar → Style-Wechsel
            ohne Menü-Umwege.

            Styles:
              - mapout (DEFAULT): Topo-Karte mit Höhenlinien — Berg-Icon
              - minimal: reduzierte Basemap ohne Contour — Papier-Icon
              - satellite: Satelliten-Layer (nur wenn im Profil opt-in'd) —
                Satelliten-Icon

            Der `.adt-scaled-stack-left`-Hook skaliert im Glove-Mode mit
            (transform-origin top-left → wächst nach rechts/unten, keine
            Kollision mit dem RIDE-Badge darüber). */}
        {/* Wave 14aw: Offline → Container wird subtil gelb (User-Wunsch
            "mach einfach den dunklen hintergrund um die karten-art-logos
            oben links gelb"). Ersetzt den separaten Offline-Banner unten,
            der oft das Höhenprofil verdeckt hat. Satellite-Button wird
            offline komplett ausgeblendet — der Style funktioniert eh nur
            mit Netz und führt offline zu schwarzem Screen.
            Wave 14bf: OFFLINE-Pille wanderte aus der BottomBar hierher
            (direkt unter den Style-Icons) — User-Feedback: "offline modus
            sollte nicht unten in der leiste mit skala + POIs angezeigt
            werden". Wir rendern den Indikator als zweite Zeile im selben
            amber-Container, damit er visuell zur gelben Umrandung
            gehört statt isoliert zu hängen. */}
        <div
          className={`adt-scaled-stack-left pointer-events-auto self-start flex flex-col gap-0.5 p-1 rounded-xl shadow-lg transition-colors duration-300 ${
            online
              ? 'adt-chrome-secondary'
              : 'bg-amber-500/85 border border-amber-400/60 backdrop-blur-md'
          }`}
          title={online ? undefined : 'Offline — cached maps active'}
          aria-label={online ? undefined : 'Offline mode — using cached maps'}
        >
          <div className="flex items-center gap-0.5">
            {(
              [
                'mapout',
                'minimal',
                ...(satelliteEnabled && online ? (['satellite'] as const) : []),
              ] as const
            ).map((s) => {
              const isActive = style === s;
              const label =
                s === 'mapout'
                  ? 'Topographic map with contour lines'
                  : s === 'minimal'
                  ? 'Minimal map (clean, no contours)'
                  : 'Satellite imagery';
              // Offline: aktiver Style bekommt dunkleres "on amber"-Pill (das
              // orange würde auf dem Amber-Hintergrund verschwimmen), inaktive
              // bleiben dunkelgrau damit sie vom gelben BG absetzen.
              const activeCls = online
                ? 'bg-orange-500 text-white shadow-sm'
                : 'bg-turbo-900/85 text-white shadow-sm';
              const inactiveCls = online
                ? 'text-white/55 hover:text-white hover:bg-white/5'
                : 'text-black/70 hover:text-black hover:bg-black/10';
              return (
                <button
                  key={s}
                  onClick={() => setStyle(s)}
                  aria-pressed={isActive}
                  title={label}
                  aria-label={label}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-150 ${
                    isActive ? activeCls : inactiveCls
                  }`}
                >
                  <StyleIcon kind={s} />
                </button>
              );
            })}
          </div>
          {!online && (
            <span
              className="text-[10px] uppercase tracking-[0.15em] font-bold text-turbo-900 text-center leading-none pb-0.5 pt-0.5"
              aria-label="Offline mode"
              title="No internet — running on cached maps & trails"
            >
              offline
            </span>
          )}
        </div>
      </div>
      )}

      {/* Bottom-Bar: vereinheitlichte dunkle Pill unten mit ScaleBar,
          Difficulty-Legende, POI-Button und Elevation-Badge. Design matcht
          jetzt die TabBar (turbo-900/85 + turbo-700/50 border + rounded-2xl).
          Trail-Count ist NICHT mehr hier — der sitzt als orange Notification-
          Badge direkt auf dem TRAILS-Tab. Im Ride-Mode wird die Bar nach oben
          geschoben damit sie nicht mit dem Höhenprofil-Strip kollidiert.
          Wave 14cb: im Fullscreen-Mode komplett weggeblendet. */}
      {!isFullscreen && <BottomBar shiftUp={mode === 'ride' && !!selected} />}

      {/* Wave 14cb-fix4: FullscreenToggle wandert in den ZoomButtons-Stack
          (siehe ZoomButtons-Komponente unten). Hier kein separater Render
          mehr nötig. */}

      {/* Wave 14bf: Grosse, halbtransparente Zoom-Buttons rechts auf
          Hüfthöhe. User-Feedback: Pitch-Zoom (zwei Finger nach oben/unten)
          verschiebt die Karte hart vom Fahrer weg, und der LocateControl-
          Re-Center springt unerwartet auf den Explore-Zoom zurück. Die
          expliziten +/- Buttons machen map.zoomIn/zoomOut — das bewegt
          nur den Zoom, Center und Pitch bleiben unangetastet, und der
          LocateControl-State (lockZoom) wird NICHT neu gesetzt.
          Positionierung: absolute, rechts, in der unteren Hälfte — nah
          am rechten Daumen, aber über der BottomBar. Im Ride-Mode wird
          sie nach oben verschoben, damit das Höhenprofil-Strip nicht
          drübermauert. Tap-Targets sind 56px (iOS HIG: 44px minimum,
          Gloves: gerne größer). */}
      <ZoomButtons shiftUp={mode === 'ride' && !!selected} />

      {/* POI-Fehler-Toast. Zeigt sich nur wenn Overpass beim ERSTEN Aktivieren
          gescheitert ist (Offline und kein Cache). Auto-hides nach kurzer Zeit
          nicht — der User soll wissen warum keine Marker da sind. Klick = weg. */}
      {poiError && drinkingWaterOn && (
        <div
          role="status"
          onClick={() => setPoiError(null)}
          className="absolute top-16 left-1/2 -translate-x-1/2 bg-red-600/90 text-white text-xs px-3 py-2 rounded-lg shadow-lg cursor-pointer z-40 max-w-xs text-center leading-snug"
        >
          Couldn't load drinking water POIs. You may be offline and we haven't cached them yet. Tap to dismiss.
        </div>
      )}

      {/* Trail-Detail-Sheet: im Explore-Mode kompakt mit Profil + side-by-side
          Buttons. User-Wunsch: "viel kleiner machen, start ride kurzen button,
          DANEBEN navigate to trailhead. länge und vertikale meter kleiner
          machen bitte!. die info, wie hier 'Downhill Intermediate' viel näher
          an trailname, alles enger rücken bitte."
          → p-3 (statt p-4), engere mt-Gaps, Stats als one-liner inline.
          Wave 14cb: im Fullscreen-Mode versteckt. */}
      {selected && mode === 'explore' && !isFullscreen && (
        <div
          ref={trailCardRefCallback}
          className="absolute left-0 right-0 bottom-0 safe-bottom bg-turbo-900/95 border-t border-turbo-700/40 px-3 pt-2 pb-3 max-h-[70vh] overflow-auto"
        >
          {/* Wave 14ca-fix50: Featured-Tour-Navigator. Sichtbar nur wenn
              eine Featured Tour selected ist UND mindestens 2 gefilterte
              Touren existieren. Buttons + Swipe (touch handler oben am
              Card-Container) wechseln zur prev/next-Tour. n/N-Indikator
              zeigt Position in der gefilterten Liste. */}
          {(() => {
            if (!isFeaturedTourPseudoId(selected.id)) return null;
            if (filteredFeaturedTours.length < 2) return null;
            const currentId = extractFeaturedTourId(selected.id);
            const idx = filteredFeaturedTours.findIndex(
              (t) => t.id === currentId,
            );
            if (idx < 0) return null;
            const prev =
              filteredFeaturedTours[
                (idx - 1 + filteredFeaturedTours.length) %
                  filteredFeaturedTours.length
              ];
            const next =
              filteredFeaturedTours[(idx + 1) % filteredFeaturedTours.length];
            return (
              // Wave 14cf-fix24: Horizontal-Swipe-Handler hängt JETZT an
              // diesem Navigator-Header (vorher an der äußeren Trail-Card)
              // damit der Swipe NUR im Title-Bereich greift. Höhenprofil +
              // Description darunter sind frei für vertikales Inspizieren
              // ohne versehentliches Tour-Wechseln (User-Wunsch). Render-
              // Gating durch das umschließende IIFE (`isFeaturedTourPseudoId`
              // + `filteredFeaturedTours.length >= 2`) garantiert dass der
              // Header — und damit die Swipe-Area — überhaupt nur dann
              // existiert wenn Prev/Next-Tour-Switching Sinn macht.
              <div
                className="flex items-center justify-between gap-2 mb-1.5 -mt-0.5"
                onTouchStart={(e) => {
                  const t = e.touches[0];
                  if (!t) return;
                  (e.currentTarget as HTMLElement & {
                    _swipeStart?: { x: number; y: number };
                  })._swipeStart = { x: t.clientX, y: t.clientY };
                }}
                onTouchEnd={(e) => {
                  const start = (e.currentTarget as HTMLElement & {
                    _swipeStart?: { x: number; y: number };
                  })._swipeStart;
                  if (!start) return;
                  const t = e.changedTouches[0];
                  if (!t) return;
                  const dx = t.clientX - start.x;
                  const dy = t.clientY - start.y;
                  // Horizontaler Swipe: dx > 50px UND dx >> dy. Sonst ist's
                  // wahrscheinlich vertikales Scrollen oder ein Tap.
                  if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 2) {
                    // Swipe LEFT (dx<0) = next; Swipe RIGHT (dx>0) = prev.
                    const target = dx < 0 ? next : prev;
                    if (target) void goToFeaturedTour(target);
                  }
                }}
              >
                <button
                  type="button"
                  onClick={() => prev && void goToFeaturedTour(prev)}
                  className="flex-1 min-w-0 flex items-center gap-1 text-xs text-white/70 hover:text-white px-2 py-1 rounded-md bg-turbo-700/40 border border-turbo-500/30"
                  title={prev ? `Previous: ${prev.name}` : ''}
                >
                  <span aria-hidden>←</span>
                  <span className="truncate">{prev?.name ?? ''}</span>
                </button>
                <span className="text-[11px] text-white/45 tabular-nums shrink-0">
                  {idx + 1}/{filteredFeaturedTours.length}
                </span>
                <button
                  type="button"
                  onClick={() => next && void goToFeaturedTour(next)}
                  className="flex-1 min-w-0 flex items-center gap-1 justify-end text-xs text-white/70 hover:text-white px-2 py-1 rounded-md bg-turbo-700/40 border border-turbo-500/30"
                  title={next ? `Next: ${next.name}` : ''}
                >
                  <span className="truncate">{next?.name ?? ''}</span>
                  <span aria-hidden>→</span>
                </button>
              </div>
            );
          })()}
          {/* Header-Zeile: Farb-Dot + Trailname + Close-X. mb-0.5 hält den
              Difficulty-Subtitle DICHT drunter. */}
          <div className="flex items-center gap-2">
            {/* Wave 14aj: 1px weißer Rand, damit dunkle Difficulty-Farben
                (expert=schwarz, extreme=b/w-stripe) auf dem turbo-900-
                Sheet-BG sichtbar bleiben.
                Wave 14ak: Für DH-Runs zeigen wir N Kreise — einen pro Trail
                in Reihenfolge — statt eines einzelnen Kompositkreises. Der
                User sieht sofort "Isallo Extasy (blau) → Revenant (blau-
                schwarz gestreift)". Für normale Trails bleibt der eine Dot
                unverändert. */}
            {runTrailsInOrder.length >= 1 ? (
              <span className="flex items-center gap-1 flex-shrink-0">
                {runTrailsInOrder.map((t, i) => (
                  <span
                    key={`${t.id}-${i}`}
                    className="w-3 h-3 rounded-full border border-white/35"
                    style={{
                      background: t.colorHex || difficultySwatchBackground(t.difficulty),
                    }}
                    aria-label={DIFFICULTY_LABEL[t.difficulty as Difficulty]}
                  />
                ))}
              </span>
            ) : (
              <span
                className="w-3 h-3 rounded-full flex-shrink-0 border border-white/35"
                style={{ background: selected.colorHex || difficultySwatchBackground(selected.difficulty) }}
              />
            )}
            <h2 className="font-semibold text-base flex-1 truncate">{selected.name}</h2>
            {/* Close-X — Wave 14ce: User-Wunsch "der X button zum trail
                schliessen ist nicht sichtbar genug, mach ihn vll rot und
                grösser". Roter Filled-Circle mit weissem X, 48×48 Tap-
                Target (statt vorher transparent 44×44). Bold-Weight auf
                dem Glyph macht die Striche dicker, leichter Shadow gibt
                Tiefe gegen die dunkle Sheet-BG. */}
            <button
              className="w-12 h-12 -mr-1 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 active:bg-red-700 text-white text-2xl leading-none font-bold transition-colors shadow-md"
              onClick={() => setSelected(null)}
              aria-label="Close trail"
            >
              ✕
            </button>
          </div>

          {/* Trail-Type · Difficulty unmittelbar unter dem Namen — keine
              Zwischen-Margin (außer das natürliche line-height). Dieselbe
              Indent-Spalte wie der Name (ml-5 = 12px Dot + 8px Gap — stimmt
              beim Single-Dot; bei Multi-Circle leicht verschoben, egal).
              Wave 14ak: Bei Multi-Trail-Runs (>= 2 Trails) lassen wir die
              Difficulty-Liste WEG — die Kreise links vom Namen zeigen die
              Schwerigkeiten jetzt visuell in der gleichen Reihenfolge. Nur
              "DH Run" als Label bleibt. Bei 1 Trail (oder normalen Trails)
              kommt weiterhin "<type> · <difficulty>". */}
          <div className="ml-5 -mt-0.5 text-[11px] text-white/50 capitalize leading-tight">
            {runTrailsInOrder.length >= 2 || isCustomTrailPseudoId(selected.id)
              ? selected.trailType
              : `${selected.trailType} · ${DIFFICULTY_LABEL[selected.difficulty]}`}
          </div>

          {/* Stats als kompakter one-liner statt 3 grosser Karten. Inline-
              Format: "2.34 km · ↓ 340 m · ↑ 12 m". Spart viel vertikalen
              Platz und matcht visuell die Ride-Mode-Mini-Leiste.
              Wave 14aq: Gain/Loss kommen jetzt aus `derivedStats` (siehe
              useMemo oben) — das reflektiert die tatsächlich angezeigten
              Profil-Samples (GPX oder SRTM). Bei pending-SRTM zeigen wir
              "…" statt "0" um klarzustellen dass da noch gerechnet wird.
              Bei Totalausfall (source='none') fallback auf die Server-Stats,
              damit echt-flache Trails auch 0 zeigen (statt "—"). */}
          {(() => {
            const gainLoss = derivedStats ?? (
              elevation.source === 'none'
                ? { gain: selected.stats.gain, loss: selected.stats.loss }
                : null
            );
            const pending = elevation.source === 'pending' && !derivedStats;
            return (
              <div className="mt-2 text-[12px] text-white/80 tabular-nums flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                <span>
                  <span className="font-semibold">{(selected.stats.lengthMeters / 1000).toFixed(2)}</span>
                  <span className="text-white/55"> km</span>
                </span>
                <span>
                  <span className="text-white/55">↓ </span>
                  <span className="font-semibold">
                    {pending ? '…' : gainLoss ? gainLoss.loss.toFixed(0) : '—'}
                  </span>
                  <span className="text-white/55"> m</span>
                </span>
                <span>
                  <span className="text-white/55">↑ </span>
                  <span className="font-semibold">
                    {pending ? '…' : gainLoss ? gainLoss.gain.toFixed(0) : '—'}
                  </span>
                  <span className="text-white/55"> m</span>
                </span>
                {/* Wave 14ca-fix30: Akkuverbrauchs-Vorhersage. Erst sobald
                    gainLoss bekannt (sonst zeigen wir 0m_up was Müll wäre). */}
                {gainLoss && !pending && (
                  <PredictBadge
                    distanceKm={selected.stats.lengthMeters / 1000}
                    elevationGainM={gainLoss.gain}
                  />
                )}
                {/* Wave 14ca-fix47 (PRO): GPX-Export fuer Custom Tours direkt
                    aus der Map-Trail-Detail-Card. User-Wunsch: "ich sehe
                    keinen gpx downloadbutton wenn ich eine custom tour
                    oeffne". Vorher war der Export nur in der Listen-Page +
                    der separaten Detail-Page (die ueber die UI gar nicht
                    erreicht wird). Jetzt auch hier rechts vom PredictBadge.
                    Nur fuer Custom Tours (nicht Single-Trails — die sind
                    bewusst nicht exportierbar gegen Trail-Klau). */}
                {isCustomTrailPseudoId(selected.id) && (
                  <button
                    type="button"
                    onClick={async () => {
                      const customId = selected.id.replace(/^custom:/, '');
                      const { getCustomTrail } = await import(
                        '../lib/customTrails'
                      );
                      const tour = await getCustomTrail(customId);
                      if (!tour) return;
                      const { customTourToGpx, downloadGpx } = await import(
                        '../lib/gpxFormat'
                      );
                      const gpx = await customTourToGpx(tour);
                      downloadGpx(`adt-tour-${tour.name}`, gpx);
                    }}
                    className="px-2 py-1 rounded-md text-xs border border-sky-500/40 text-sky-200 bg-sky-500/15 hover:bg-sky-500/25 transition-colors"
                    title="Download as GPX"
                  >
                    ↓ GPX
                  </button>
                )}
                {/* Wave 14ca-fix48: Featured-Tour-Export. Holt das original
                    GPX vom Backend (statt aus dem geparsten Track zu re-
                    serialisieren) damit die Original-Datei mit allen
                    GPX-Metadata-Feldern erhalten bleibt. */}
                {isFeaturedTourPseudoId(selected.id) && (
                  <button
                    type="button"
                    onClick={async () => {
                      const { fetchFeaturedTourGpx } = await import(
                        '../lib/featuredTours'
                      );
                      const gpx = await fetchFeaturedTourGpx(
                        selected.gpxFilename,
                      );
                      if (!gpx) return;
                      const { downloadGpx } = await import('../lib/gpxFormat');
                      downloadGpx(`adt-featured-${selected.name}`, gpx);
                    }}
                    className="px-2 py-1 rounded-md text-xs border border-sky-500/40 text-sky-200 bg-sky-500/15 hover:bg-sky-500/25 transition-colors"
                    title="Download as GPX"
                  >
                    ↓ GPX
                  </button>
                )}
                {/* Wave 14ca-fix39: Admin-Quick-Actions direkt in der Stat-Row
                    rechts vom PredictBadge — Add Key Section + Edit. Nur fuer
                    Admins (lokaler Toggle + Token), nicht fuer Run/Custom-
                    Pseudo-IDs (die haben keinen editierbaren Backend-Trail). */}
                {adminMode &&
                  adminToken &&
                  !isRunPseudoId(selected.id) &&
                  !isCustomTrailPseudoId(selected.id) && (
                    <>
                      <AddCruxHereButton
                        trailId={selected.id}
                        existingCruxes={selected.cruxPoints ?? []}
                        alongTrackMeters={progress?.alongTrackMeters ?? null}
                        offTrackMeters={progress?.offTrackMeters ?? null}
                        elevationMeters={currentCruxElevation}
                        onUpdated={(cruxes) =>
                          handleCruxesUpdated(selected.id, cruxes)
                        }
                        variant="compact"
                      />
                      <button
                        type="button"
                        onClick={() => setEditingTrail(selected)}
                        className="px-2.5 py-1.5 rounded-lg flex items-center gap-1 text-[12px] leading-tight font-medium border bg-sky-500/15 hover:bg-sky-500/25 text-sky-200 border-sky-500/40 transition-colors"
                        title="Edit name, type, difficulty, color, or reverse direction"
                      >
                        <PencilIcon /> Edit
                      </button>
                    </>
                  )}
              </div>
            );
          })()}

          {/* Höhenprofil mit Finger-Scrub. Im Explore-Mode wollen wir's leicht
              höher als Default (96px statt 80px), damit das Scrubben Spass
              macht — kleine Profile sind schwer präzise zu treffen. */}
          <div className="mt-2">
            {/* Wave 14ca-fix60: Im Explore-Mode kein progressMeters, damit
                ElevationProfile Start/End-Hoehen-Labels anzeigt statt
                "km done / km left". User-Wunsch: km-done/left ist nur im
                Ride-Mode relevant; im Explore zaehlt die Trail-Anker-
                Information mehr.
                Wave 14ca-fix64: bei Custom-Tour (selected.routeSegments
                gesetzt) faerben wir das Profil nach Trail-Difficulty +
                blenden den aktuellen Trail-Namen ÜBER dem Profil an wenn
                User scrubt. (Wave 14cl: Tooltip wandert von unten nach
                oben — sonst verdeckt der Finger ihn.) */}
            {/* Scrub-Reveal-Hint OBEN. Mit min-height damit die Profil-
                Linie nicht hochpopt wenn der Hint erscheint/verschwindet. */}
            <div className="mb-1 min-h-[18px] flex items-center gap-2 text-[12px] text-white/85 leading-tight">
              {scrubbedSegment && scrubbedSegment.kind === 'trail' && (
                <>
                  <span
                    className="inline-block w-3 h-3 rounded-full border border-white/35 shrink-0"
                    style={{ background: scrubbedSegment.color }}
                    aria-hidden="true"
                  />
                  <span className="truncate">{scrubbedSegment.trailName}</span>
                  {scrubbedSegment.difficulty && (
                    <span className="text-white/55 text-[11px] capitalize">
                      {scrubbedSegment.difficulty.replace('_', ' ')}
                    </span>
                  )}
                </>
              )}
            </div>
            <ElevationProfile
              trailId={selected.id}
              data={elevation}
              progressMeters={null}
              offTrackMeters={null}
              gpsAltitude={pos?.altitude ?? null}
              heightPx={96}
              onScrub={setScrubMeters}
              scrubMeters={scrubMeters}
              cruxPoints={showCrux ? selected.cruxPoints : []}
              segments={
                elevationSegments.length > 0 ? elevationSegments : undefined
              }
              onSegmentScrub={
                elevationSegments.length > 0 ? setScrubbedSegment : undefined
              }
            />
          </div>

          {/* Wave 14ca-fix64: Trail-Badges-Liste fuer Custom Tours.
              Sichtbar nur wenn der selected Trail mehrere Trails enthaelt.
              Tap auf Badge → highlightedTrailIdInRoute setzt — wir
              koennten in Folge-Patches die entsprechende Profil-Section +
              Map-Polyline hervorheben. Fuer jetzt: einfacher Toggle der
              wieder zurueckgesetzt wird beim 2. Tap. */}
          {uniqueTrails.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="text-[11px] text-white/55 self-center mr-1">
                Trails:
              </span>
              {uniqueTrails.map((t) => {
                const isHighlighted = highlightedTrailIdInRoute === t.trailId;
                const lengthLabel =
                  t.totalMeters >= 1000
                    ? `${(t.totalMeters / 1000).toFixed(1)} km`
                    : `${Math.round(t.totalMeters)} m`;
                return (
                  <button
                    key={t.trailId}
                    type="button"
                    onClick={() =>
                      setHighlightedTrailIdInRoute((prev) =>
                        prev === t.trailId ? null : t.trailId,
                      )
                    }
                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] border transition-colors ${
                      isHighlighted
                        ? 'bg-white/15 border-white/50 text-white shadow-sm'
                        : 'bg-turbo-700/40 border-turbo-700/60 text-white/85 hover:bg-turbo-700/60'
                    }`}
                    aria-pressed={isHighlighted}
                    title={`${t.trailName} — ${lengthLabel}`}
                  >
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full border border-white/35 shrink-0"
                      style={{ background: t.color }}
                      aria-hidden="true"
                    />
                    <span className="truncate max-w-[10rem]">{t.trailName}</span>
                    <span className="text-white/45 tabular-nums">
                      {lengthLabel}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {selected.description && (
            // Wave 14dw: Description sichtbarer machen — vorher 12px @ 65%
            // ging im Light-Mode komplett unter. Jetzt 13px @ 85% (analog
            // RunDetailPage) + leichter Container für visuelles Gewicht.
            <div className="mt-3 rounded-lg bg-turbo-800/40 border border-turbo-700/30 px-3 py-2">
              <p className="text-[13px] text-white/85 whitespace-pre-wrap leading-snug">
                {selected.description}
              </p>
            </div>
          )}
          {selected.photos && selected.photos.length > 0 && (
            <div className="mt-2 flex gap-1.5 overflow-x-auto">
              {selected.photos.map((photo) => (
                <img
                  key={photo}
                  src={`/api/admin/trails/photos/${photo}`}
                  alt=""
                  className="h-20 rounded-md object-cover flex-shrink-0"
                  loading="lazy"
                />
              ))}
            </div>
          )}

          {/* Button-Reihe: Ride (primary, rot, super-kompakt) + Navigate
              (kompakt, content-width). User-Wunsch Batch 5: "navigate to start
              button so klein wie möglich neben ride, vll hab ich später noch
              ideen für mehr buttons etc rechts davon. auch bitte linksbündig
              direkt neben RIDE".
              → Beide Buttons sind flex-shrink-0, die Reihe ist links-bündig
              (justify-start = flex-Default). Rechts bleibt leerer Raum für
              zukünftige Buttons (z. B. Share, Save, Rate). */}
          <div className="mt-3 flex gap-2 justify-start">
            <button
              onClick={startRide}
              className="bg-red-600 hover:bg-red-500 active:bg-red-700 transition-colors text-white font-semibold px-4 py-2.5 rounded-lg shadow-md flex items-center justify-center gap-1.5 flex-shrink-0"
            >
              <span className="text-base leading-none">▶</span>
              <span>Ride</span>
            </button>
            <NavigateToTrailhead points={selected.track.points} label={selected.name} compact />
            {/* Schlüsselstellen-Toggle rechts neben "Navigate to start" (User-
                Wunsch Batch 7). Zählt die auf diesem Trail definierten
                Cruxes als Badge; bei 0 bleibt der Button sichtbar als Hinweis
                dass es das Feature gibt. */}
            <CruxToggle variant="compact" count={selected.cruxPoints?.length ?? 0} />
            {/* DH-Runs-Button (Wave 14af): zeigt an, in wie vielen DH-Runs
                dieser Trail vorkommt. Tap öffnet ein Bottom-Sheet mit den
                jeweiligen Runs ("kill bill 1 - cacciatore" etc.), Tap auf
                Run navigiert zur /runs/:id-Ansicht.
                Wave 14ag: für Pseudo-Runs ausblenden — die Frage "welche Runs
                enthalten diesen Run?" ist nicht meaningful. */}
            {!isRunPseudoId(selected.id) && !isCustomTrailPseudoId(selected.id) && !isFeaturedTourPseudoId(selected.id) && (
              <RunsForTrailButton
                trailId={selected.id}
                runs={runs}
                trailsById={trailsById}
                trailName={selected.name}
              />
            )}
            {/* Admin-only: Markiere die aktuelle GPS-Position als Crux. Nur
                sichtbar mit aktiviertem Admin-Mode im Profile + Token.
                Wave 14ag: für Pseudo-Runs ausblenden — Cruxes leben auf den
                Original-Trails, nicht auf der Run-Hülle. Würde auch gegen
                einen nicht-existenten Backend-Trail ("run:xxx") schreiben. */}
            {!isRunPseudoId(selected.id) && !isCustomTrailPseudoId(selected.id) && !isFeaturedTourPseudoId(selected.id) && (
              <AddCruxHereButton
                trailId={selected.id}
                existingCruxes={selected.cruxPoints ?? []}
                alongTrackMeters={progress?.alongTrackMeters ?? null}
                offTrackMeters={progress?.offTrackMeters ?? null}
                elevationMeters={currentCruxElevation}
                onUpdated={(cruxes) => handleCruxesUpdated(selected.id, cruxes)}
                variant="compact"
              />
            )}
            {/* Rebuild-Elevation-Button (Wave 14ar): sichtbar im Admin-Mode
                IMMER, sonst nur wenn das aktuelle Profil verdächtig flach
                aussieht (Trail >200m aber Höhen-Spread <5m). Tap löscht den
                IDB-Cache und startet den SRTM-Retry-Loop neu — Fix für
                User-Bug "höhenkurven bleiben weg". */}
            <RebuildElevationButton data={elevation} />
          </div>
        </div>
      )}

      {/* Ride-Mode Bottom-Strip: nur Name (klein), Profil, Progress-Zahlen.
          max-height fix damit es kein scrollendes Sheet wird — Ride-Mode will
          Map-Fläche maximieren, daher kompakt. Scrub auch hier aktiv — manche
          User wollen während der Fahrt am Trail nach vorne schauen ("was
          kommt da gleich?").
          Wave 14cb: im Fullscreen-Mode komplett weg — User-Wunsch
          "beim RIDE modus auch das höhenprofil [versteckt]". */}
      {selected && mode === 'ride' && !isFullscreen && (
        <div
          ref={trailCardRefCallback}
          className="absolute left-0 right-0 bottom-0 safe-bottom bg-turbo-900/95 border-t border-turbo-700/40 px-3 pt-2 pb-2"
        >
          {/* Wave 14cf: Roter Close-X — User-Wunsch "ich möchte ein rotes X
              rechts oben im höhenprofil um den trail zu schliessen", damit
              man im RIDE-Modus einen ausgewählten Trail loswerden kann
              ohne den Recorder zu stoppen. Absolut positioniert oben rechts,
              damit er nicht den Trail-Namen verdrängt. setSelected(null)
              berührt den Recorder nicht (mode bleibt 'ride', recording
              läuft weiter — nur das Detail-Sheet verschwindet). */}
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="absolute -top-3 right-2 w-9 h-9 z-10 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 active:bg-red-700 text-white text-lg leading-none font-bold transition-colors shadow-md"
            aria-label="Close trail (keep ride running)"
            title="Close trail (keep ride running)"
          >
            ✕
          </button>
          <div className="flex items-center gap-2 mb-1">
            {/* Wave 14aj: weißer Rand wie im Explore-Sheet-Header.
                Wave 14ak: Multi-Kreis-Ansicht für DH-Runs — analog zum
                Explore-Header, nur kleiner (w-2.5 statt w-3). */}
            {runTrailsInOrder.length >= 1 ? (
              <span className="flex items-center gap-0.5 flex-shrink-0">
                {runTrailsInOrder.map((t, i) => (
                  <span
                    key={`${t.id}-${i}`}
                    className="w-2.5 h-2.5 rounded-full border border-white/35"
                    style={{
                      background: t.colorHex || difficultySwatchBackground(t.difficulty),
                    }}
                    aria-label={DIFFICULTY_LABEL[t.difficulty as Difficulty]}
                  />
                ))}
              </span>
            ) : (
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0 border border-white/35"
                style={{ background: selected.colorHex || difficultySwatchBackground(selected.difficulty) }}
              />
            )}
            <h2 className="font-medium text-sm truncate flex-1">{selected.name}</h2>
            {/* Admin-only Add-Crux-Button. Sichtbar im Ride-Mode ohne
                Kontextwechsel — genau das, was der User wollte: "ich halte
                an und gebe es ein, DA wo es gefährlich ist."
                Wave 14ag: bei Pseudo-Runs ausblenden — Crux-Writes gehen
                gegen den Original-Trail, nicht gegen die Run-Hülle. */}
            {!isRunPseudoId(selected.id) && !isCustomTrailPseudoId(selected.id) && !isFeaturedTourPseudoId(selected.id) && (
              <AddCruxHereButton
                trailId={selected.id}
                existingCruxes={selected.cruxPoints ?? []}
                alongTrackMeters={progress?.alongTrackMeters ?? null}
                offTrackMeters={progress?.offTrackMeters ?? null}
                elevationMeters={currentCruxElevation}
                onUpdated={(cruxes) => handleCruxesUpdated(selected.id, cruxes)}
                variant="floating"
              />
            )}
            {/* Crux-Toggle inline, klein, direkt vor der km-Angabe — der User
                kann die Warn-Marker im Ride-Mode ein-/ausblenden, ohne das
                Sheet zu schließen. count > 0 zeigt Badge. */}
            {(selected.cruxPoints?.length ?? 0) > 0 && (
              <CruxToggle variant="floating" count={selected.cruxPoints!.length} />
            )}
            <span className="text-[10px] text-white/50 uppercase tabular-nums">
              {(selected.stats.lengthMeters / 1000).toFixed(1)} km
            </span>
          </div>
          {/* Wave 14cl: Scrub-Reveal-Hint UEBER das Profil — sonst verdeckt
              der Finger den Trail-Namen. min-height reserviert den Platz
              damit die Profil-Linie nicht hochpopt beim Erscheinen. */}
          <div className="mb-1 min-h-[16px] flex items-center gap-2 text-[11px] text-white/85 leading-tight">
            {scrubbedSegment && scrubbedSegment.kind === 'trail' && (
              <>
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full border border-white/35 shrink-0"
                  style={{ background: scrubbedSegment.color }}
                  aria-hidden="true"
                />
                <span className="truncate flex-1">
                  {scrubbedSegment.trailName}
                </span>
                {scrubbedSegment.trailId && (
                  <button
                    type="button"
                    onClick={() => {
                      // Tap → wechselt selected zur Trail-Detail-Ansicht.
                      // Damit kann der User die Description lesen ohne den
                      // Ride zu beenden.
                      const t = trails.find(
                        (x) => x.id === scrubbedSegment.trailId,
                      );
                      if (t) setSelected(t);
                    }}
                    className="text-sky-300 hover:text-sky-200 text-[11px] underline-offset-2 underline"
                  >
                    open
                  </button>
                )}
              </>
            )}
          </div>
          <ElevationProfile
            trailId={selected.id}
            data={elevation}
            progressMeters={progress?.alongTrackMeters ?? null}
            offTrackMeters={progress?.offTrackMeters ?? null}
            gpsAltitude={pos?.altitude ?? null}
            heightPx={56}
            onScrub={setScrubMeters}
            scrubMeters={scrubMeters}
            cruxPoints={showCrux ? selected.cruxPoints : []}
            // Wave 14ca-fix64: bei Custom-Tour faerbe das Ride-Mode-Profil
            // ebenfalls nach Trail-Difficulty + Scrub-Reveal aktiv.
            // Wave 14cl: Featured Tours bekommen das gleiche Behandlung.
            segments={
              elevationSegments.length > 0 ? elevationSegments : undefined
            }
            onSegmentScrub={
              elevationSegments.length > 0 ? setScrubbedSegment : undefined
            }
          />
        </div>
      )}
      {/* Wave 14ca-fix39: Trail-Edit-Sheet (Bottom-Sheet via Portal). Wird
          getriggert vom Edit-Button neben dem PredictBadge. Nach Save/Reverse
          syncen wir den Trail-Cache neu damit Map + Detail aktuelle Werte
          zeigen, und schliessen das Sheet via setEditingTrail(null). */}
      {/* Wave 14ca-fix41: Top-of-Map Aktiv-Ride-Overlay + Free-Ride-FAB.
          - ActiveRideOverlay rendert sich selbst conditional auf
            recorderState.kind === 'recording'.
          - Free-Ride-Button: erscheint nur wenn KEINE Ride laeuft UND
            kein Trail ausgewaehlt ist (sonst nutzt der User den Ride-
            Button im Trail-Detail). Sitzt rechts unten ueber der
            BottomBar, nicht im Fullscreen-Modus damit dort die Map
            wirklich frei bleibt.
            Wave 14ca-fix64-v2: NUR auf Map-Route rendern. MapPage ist
            dauerhaft gemountet, der km/hm/Stop-Pille macht aber nur auf
            der Karte Sinn — auf NAVI/GUIDE/OFFLINE/PROFILE waere sie
            visuell falsch positioniert (durchscheinen durch das
            Overlay-Page-Chrome). */}
      {isMapRoute && !isFullscreen && <ActiveRideOverlay />}

      {/* Wave 14cm: Akku-Wechsel-Stationen für VL-Kunden. Komponente
          managed Map-Subscription + Fetch + Marker + Detail-Sheet
          intern, returnt null wenn der User kein VL ist (Null-Overhead
          für normale User). Nur auf der Map-Route rendern. */}
      {isMapRoute && <BatteryStationOverlay />}

      {/* Wave 14dt: Shuttle-Mode-Overlay. Aktiv nur wenn die URL
          `?mode=shuttle` enthält. Versteckt Trail-Polylines, zeigt 5
          Area-Badges (PL/KA/IS/ME/BN), bietet Tap-to-Zoom + Exit-X.
          Self-contained — kein zusätzlicher State in MapPage nötig. */}
      {isMapRoute && <ShuttleModeOverlay />}

      {/* Wave 14ca-fix64: Trail-Name-Pill waehrend Recording.
          Sobald der User innerhalb 30 m eines bekannten Trails ist,
          erscheint eine kleine Pill ueber dem Bottom-Bar mit dem
          Trail-Namen + Difficulty-Swatch. User-Wunsch: "wenn ich auf
          einem Trail bin sehe ich seinen Namen". Pill ist tappable —
          tappen oeffnet das Trail-Detail-Sheet (setSelected).
          Wave 14cf: wenn bereits ein Trail selektiert ist (= das
          Detail-Sheet mit Name + Profil unten offen), pille
          ausblenden — User-Wunsch "doppelt gemoppelt, das eingekreiste
          Revenant 9m muss da weg" + die kleine Pille überlagerte
          häßlich mit der BottomBar-Pille. */}
      {isMapRoute &&
        recorderState.kind === 'recording' &&
        nearestTrail &&
        !selected &&
        !isFullscreen && (
          <button
            type="button"
            onClick={() => setSelected(nearestTrail.trail)}
            className="absolute left-1/2 -translate-x-1/2 z-30 pointer-events-auto bg-turbo-900/85 backdrop-blur-md border rounded-full px-3 py-1.5 shadow-lg flex items-center gap-2 max-w-[80%]"
            style={{
              bottom: 'calc(env(safe-area-inset-bottom, 0px) + 9rem)',
              borderColor:
                (nearestTrail.trail.colorHex ||
                  DIFFICULTY_COLOR[nearestTrail.trail.difficulty]) + 'aa',
            }}
            aria-label={`On trail: ${nearestTrail.trail.name}`}
            title={`On trail: ${nearestTrail.trail.name} — tap to open detail`}
          >
            {/* Difficulty-Swatch links */}
            <span
              className="w-3 h-3 rounded-full shrink-0 border border-white/40"
              style={{
                background:
                  nearestTrail.trail.colorHex ||
                  difficultySwatchBackground(nearestTrail.trail.difficulty),
              }}
              aria-hidden="true"
            />
            <span className="text-white text-[13px] font-semibold leading-tight truncate">
              {nearestTrail.trail.name}
            </span>
            {/* Off-track-Distanz subtil rechts — User sieht "ich bin 5m vom
                Track entfernt" und weiss ob der Snap zuverlaessig ist. */}
            <span className="text-white/55 text-[11px] tabular-nums">
              {nearestTrail.offTrackMeters < 1
                ? '·'
                : `${nearestTrail.offTrackMeters.toFixed(0)} m`}
            </span>
          </button>
        )}

      {/* Wave 14ca-fix64: Way-ID-Pick-Mode-Overlay.
          Wenn aktiv UND noch kein Modal offen: orange Border + Click-Capture-
          Layer + Top-Hint. Sobald `pickedSnap` gesetzt ist (Modal sichtbar),
          schalten wir den Click-Capture-Layer + Top-Hint aus, damit der User
          die Karte oberhalb des Bottom-Sheets frei pannen/zoomen kann um den
          gerade hervorgehobenen Way im Detail zu inspizieren. Die orange
          Border bleibt — visuell weiterhin "du bist im Pick-Mode". */}
      {wayPickMode && (
        <>
          {/* Orange Border bleibt immer sichtbar im Pick-Mode (auch wenn
              Modal offen). Click-Capture-Layer NUR wenn kein Modal offen. */}
          <div
            className="absolute inset-0 z-40 pointer-events-none"
            style={{
              boxShadow: 'inset 0 0 0 4px rgba(249, 115, 22, 0.85)',
            }}
            aria-hidden="true"
          />
          {!pickedSnap && (
            <>
              {/* Click-Capture nur ohne offenes Modal. */}
              <div
                className="absolute inset-0 z-40"
                style={{ cursor: 'crosshair' }}
                onClick={handlePickClick}
                aria-label="Way-pick mode — tap on a way to identify it"
              />
              {/* Top-Hint mit Dismiss-Button. */}
              <div
                className="pointer-events-none absolute z-50 flex justify-center"
                style={{
                  top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
                  left: '0',
                  right: '0',
                }}
              >
                <div className="pointer-events-auto bg-orange-500 border border-orange-300 rounded-xl px-3 py-1.5 shadow-lg flex items-center gap-2 max-w-[90%]">
                  <span aria-hidden="true">🔍</span>
                  <span className="text-white text-[13px] font-semibold leading-tight">
                    Tap a way on the map to identify it
                  </span>
                  <button
                    type="button"
                    onClick={() => wayPickBus.deactivate()}
                    className="ml-2 px-3 h-8 bg-white/20 hover:bg-white/30 text-white text-[12px] font-semibold rounded-lg"
                    aria-label="Exit way-pick mode"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Way-Pick-Result-Modal — Portal, damit es ueber allem liegt. */}
      {pickedSnap &&
        createPortal(
          <WayPickModal
            snap={pickedSnap}
            onAdd={handleAddOverride}
            onCancel={handleDismissPick}
            onClose={() => {
              // "Skip — pick another": Modal zu, Tap-Marker auch weg, aber
              // Pick-Mode bleibt aktiv damit User sofort den naechsten Way
              // antippen kann.
              setPickedSnap(null);
              setPickedTapLonLat(null);
            }}
          />,
          document.body,
        )}

      {/* Wave 14cf-fix3: Vorher 4 separat gerenderte Buttons (WAY ID
          standalone + DiscoveryButtons-Stack mit BLOCK/ONE-WAY/SCHLÜSSEL).
          Jetzt eine einzige PLUS-Toggle, die nur die jeweils gerade
          anwendbaren Aktionen aufklappt — plus den neuen "POI markieren"-
          Eintrag (s. PlusActionMenu).
          Sichtbarkeitslogik bleibt 1:1 erhalten:
            - WAY ID:        adminMode + discoveryMode + admin_light_plus
            - BLOCK/ONE-WAY: zusätzlich Recording aktiv
            - SCHLÜSSEL:     adminMode + Recording + admin_light
            - POI markieren: jeder eingeloggte User mit GPS-Fix
          PlusActionMenu rendert NICHTS wenn keine Action sichtbar ist
          (z. B. anonymer User). Der !isFullscreen-Gate bleibt: im
          Fullscreen-Mode ist die Map-Action-Spalte ausgeblendet. */}
      {isMapRoute && !isFullscreen && (
        <div
          className="pointer-events-none absolute z-30"
          style={{
            // Wave 14cf-fix6: bei aktivem Recording rückt der RIDE-Balken
            // nach links-bündig in die obere Reihe — Style-Switcher und
            // PLUS-Menu wandern eine Reihe nach unten (~3.25rem extra).
            // Wave 14ep: Offset um 1rem erhöht damit das + nicht in das
            // OFFLINE-Badge unter dem Style-Switcher reinkracht.
            top:
              recorderState.kind === 'recording'
                ? 'calc(env(safe-area-inset-top, 0px) + 8.5rem)'
                : 'calc(env(safe-area-inset-top, 0px) + 5.5rem)',
            left: '0.75rem',
          }}
        >
          <div className="pointer-events-auto">
            <PlusActionMenu
              role={role}
              adminMode={adminMode}
              discoveryMode={discoveryMode}
              recording={recorderState.kind === 'recording'}
              effectiveDiscoveryRole={
                // Bei admin_light_plus / admin nur dann das volle Set
                // ('admin') zeigen wenn discoveryMode aktiv ist; sonst
                // 'admin_light' simulieren damit nur SCHLÜSSEL erscheint.
                // So bleibt der discoveryMode-Toggle weiterhin Gate fuer
                // BLOCK/ONE-WAY (User-Wunsch von 14ca-fix58).
                discoveryMode ? role : 'admin_light'
              }
            />
          </div>
        </div>
      )}
      {recorderState.kind === 'idle' && !selected && !isFullscreen && isMapRoute && (
        <button
          type="button"
          onClick={startRide}
          className="absolute right-3 bottom-24 z-30 bg-red-600 hover:bg-red-500 active:bg-red-700 text-white font-semibold px-4 py-3 rounded-full shadow-lg flex items-center gap-2 pointer-events-auto"
          aria-label="Start free ride recording"
        >
          <span className="text-base leading-none">▶</span>
          <span>Ride</span>
        </button>
      )}

      {/* Wave 14cf-fix19: POI-Detail-Popup. Tap auf einen POI-Pin (drinking
          water, café, lunch, bike shop, beach, viewpoint) oeffnet diese
          kleine Karte mit Name + Notiz + Oeffnungszeiten. Click ausserhalb
          → schliesst das Popup. */}
      {simEnabled && (
        <div className="pointer-events-none fixed left-1/2 -translate-x-1/2 top-[max(0.5rem,env(safe-area-inset-top))] z-[56] rounded-full bg-fuchsia-600/90 text-white text-[12px] font-semibold px-3 py-1 shadow-lg">
          🧪 Test-Standort aktiv — lang auf die Karte drücken
        </div>
      )}

      {/* ULTRA: Bestätigung nach Long-Press → als Test-Standort speichern. */}
      {pendingSim && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-6"
          role="dialog"
          aria-modal="true"
          onClick={() => setPendingSim(null)}
        >
          <div
            className="bg-turbo-900 border border-fuchsia-500/40 rounded-2xl p-4 w-full max-w-[300px] shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-semibold mb-1">Als Test-Standort speichern?</div>
            <div className="text-[12px] text-white/60 mb-4 tabular-nums">
              {pendingSim.lat.toFixed(4)}, {pendingSim.lon.toFixed(4)}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setSimLocation(pendingSim.lat, pendingSim.lon);
                  setPendingSim(null);
                }}
                className="flex-1 rounded-lg bg-fuchsia-600/40 border border-fuchsia-400/60 text-fuchsia-50 text-sm py-2 font-semibold"
              >
                Ja, speichern
              </button>
              <button
                type="button"
                onClick={() => setPendingSim(null)}
                className="rounded-lg bg-white/10 border border-white/20 text-white/80 text-sm px-4"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      {poiPopup && (
        <PoiDetailCard
          popup={poiPopup}
          onClose={() => {
            setPoiPopup(null);
            setSelectedPoiId(null);
          }}
          onNavigate={() => {
            const pos = positionBus.get();
            if (!pos) {
              window.alert(
                'Keine Position — Locate-Button drücken oder Test-Standort setzen.',
              );
              return;
            }
            pitstopTargetBus.publish(
              buildPitstopTarget(
                [pos.longitude, pos.latitude],
                poiPopup.lon,
                poiPopup.lat,
                poiPopup.name || poiPopup.category,
              ),
            );
            setPoiPopup(null);
            setSelectedPoiId(null);
            navigate('/navi/custom');
          }}
        />
      )}
      {editingTrail && (
        <TrailEditSheet
          trail={{
            id: editingTrail.id,
            name: editingTrail.name,
            // CachedTrail.trailType ist als string typisiert (Server kann
            // technisch alles liefern), TrailEditSheet erwartet die enge
            // Union TrailType. Backend liefert immer eines der 6 Werte —
            // Cast ist semantisch sicher.
            trailType: editingTrail.trailType as TrailType,
            difficulty: editingTrail.difficulty,
            colorHex: editingTrail.colorHex ?? null,
          }}
          onClose={() => setEditingTrail(null)}
          onSaved={() => {
            // Trail-Cache neu syncen: ueberschreibt IDB mit Server-Stand,
            // feuert TRAILS_UPDATED_EVENT damit andere Hooks (Trail-Listen)
            // mitkriegen, und updated `trails` (lokaler State) via
            // setTrails — so dass das selected-Objekt beim naechsten
            // Trail-Tap die neuen Werte traegt.
            syncTrailCache({
              domParser: new DOMParser(),
              onProgress: (trail) => {
                setTrails((prev) => {
                  const idx = prev.findIndex((t) => t.id === trail.id);
                  if (idx < 0) return [...prev, trail];
                  const next = prev.slice();
                  next[idx] = trail;
                  return next;
                });
                if (selected && selected.id === trail.id) {
                  setSelected(trail);
                }
              },
            })
              .then(() => {
                window.dispatchEvent(new Event(TRAILS_UPDATED_EVENT));
              })
              .catch((err) =>
                console.error('[map] reload after edit failed', err),
              );
          }}
        />
      )}

      {/* Wave 14ch: Ride-Intro-Modal. Erscheint wenn der User RIDE drückt
          (Map-Button oder via Save-&-Ride-Intent von CreateCustomPage) und
          das Popup noch nie weggeklickt hat. "Got it" ruft die geparkte
          doStartRide-Funktion auf; "Don't show again"-Checkbox setzt den
          localStorage-Flag (in dismissRideIntroForever). */}
      {rideIntroPending && (
        <RideIntroModal
          onConfirm={() => {
            const proceed = rideIntroPending;
            setRideIntroPending(null);
            proceed();
          }}
        />
      )}
    </div>
  );
}

/** Wave 14ca-fix41: "vor X min" Helper fuer Combine-Prompt. */
function formatRelative(isoTime: string): string {
  const now = Date.now();
  const t = new Date(isoTime).getTime();
  const diffMs = now - t;
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 min ago';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? '1 h ago' : `${hours} h ago`;
}

/** Wave 14ca-fix39: kleines Pencil-Icon fuer den Edit-Button, lucide-stylig. */
function PencilIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 3l3 3-9 9H5v-3z" />
    </svg>
  );
}

/**
 * BottomBar — vereinheitlichte dunkle Pill unten, die ScaleBar, Difficulty-
 * Legende, POI-Button und ElevationBadge in einer Reihe bündelt. Design
 * folgt der TabBar (turbo-900/85 Hintergrund, turbo-700/50 Border,
 * rounded-2xl, backdrop-blur). Eine einzige visuelle Einheit — macht den
 * unteren Rand deutlich ruhiger als vorher drei separate dunkle Buttons.
 *
 * Layout:
 *   [ScaleBar] · [Difficulty] · [POIs] · [Elevation]
 *
 * Positionierung:
 *   - right: 3rem   (lässt Platz fürs MapLibre-"i"-Attribution am unteren-
 *                    rechten Eck — das bleibt visuell eigenständig in der
 *                    Ecke)
 *   - left:  0.75rem
 *   - bottom: 1.5rem im Standard — sitzt komfortabel über dem Attribution-
 *                    Strip UND der TabBar; im Ride-Mode zusätzlich auf
 *                    ~150px angehoben damit der Höhenprofil-Strip nicht
 *                    kollidiert.
 *
 * User-Wunsch Batch 14b: "die anderen verbleibenden logos würde ich im
 * schönen design passend zur untersten leiste machen". Trail-Count ist
 * NICHT mehr Teil der Bar — der ist jetzt ein Notification-Badge direkt
 * auf dem TRAILS-Tab (siehe App.tsx TabBadge).
 */
function BottomBar({
  shiftUp = false,
}: {
  shiftUp?: boolean;
  /**
   * Wave 14bf: `online` wurde entfernt — die OFFLINE-Pille hängt jetzt
   * direkt unter dem Style-Switcher oben links (statt verloren zwischen
   * Skala / POIs / Höhe). BottomBar bleibt dadurch reinlich funktional
   * und nimmt nicht mehr an State-Props teil, die nur für den Indikator
   * relevant waren.
   */
}) {
  return (
    <div
      className="absolute flex justify-end pointer-events-none"
      style={{
        // Links viel Luft, rechts Platz für das "i" (32px Abstand reicht);
        // die Pill selbst richtet sich rechts aus (justify-end), wächst nach
        // links wenn die ScaleBar breiter wird.
        left: '0.75rem',
        right: '2.75rem',
        bottom: shiftUp ? '150px' : '1.5rem',
      }}
    >
      <div
        className="pointer-events-auto flex items-center gap-2.5 px-3 py-1.5 rounded-2xl bg-turbo-900/85 border border-turbo-700/50 backdrop-blur-md shadow-lg"
      >
        <ScaleBar />
        <PillDivider />
        <POIButton embedded />
        <PillDivider />
        <PitstopPanel embedded />
        <PillDivider />
        <ElevationBadge embedded />
      </div>
    </div>
  );
}

/** Dünner vertikaler Trenner zwischen BottomBar-Segmenten. Turbo-Palette. */
function PillDivider() {
  return <span className="w-px h-4 bg-turbo-700/60" aria-hidden="true" />;
}

/**
 * Wave 14bf: Grosse +/- Zoom-Buttons rechts, in der unteren Hälfte. Wir
 * bauen sie bewusst OHNE den NavigationControl von MapLibre (zu klein,
 * zu nah am Rand, und er montiert sich gleichzeitig mit Compass-/Pitch-
 * Knöpfen, die wir nicht wollen) und ohne Library-Icons (keine neue
 * Dependency für zwei Striche).
 *
 * Positionierung: `absolute right-3` + `bottom: 33vh` → unteres Drittel
 * rechts. 50px/50px Buttons mit 20px Gap stapeln sich vertikal; plus
 * oben, minus unten, wie im Auto-Zoom und im iOS-Karten-App.
 *
 * Interaktion: Tap → `map.zoomIn()` / `map.zoomOut()`. Das sind die
 * MapLibre-Methoden, die smooth mit einem flyTo(zoom ±1) animieren,
 * Center/Pitch aber NICHT anfassen. Damit bleibt der Pitch-Tilt erhalten
 * (wichtig im 3D-Vorausblick) und der LocateControl-Lockzoom wird nicht
 * überschrieben — im Gegensatz zu einem Re-Center-Tap, der sonst hart
 * auf `exploreZoom` springt.
 *
 * Im Ride-Mode schieben wir den Stack 150px hoch (selbe Logik wie
 * BottomBar), damit das Höhenprofil-Sheet nicht über die Buttons fällt.
 */
function ZoomButtons({ shiftUp = false }: { shiftUp?: boolean }) {
  // Wave 14ca-fix64: Rain-Mode + Pocket-Mode Feature-Gates.
  const [rainModeEnabled] = useRainModeEnabled();
  const [pocketModeEnabled] = usePocketModeEnabled();
  const zoomBy = (delta: number) => {
    const map = mapBus.get();
    if (!map) return;
    // User-Wunsch: nach Pinch landet man oft auf einer Zwischen-Stufe
    // (z.B. 13.4). Ein Tap auf + soll dann auf 14 springen statt auf 14.4 —
    // also immer auf die nächste ganzzahlige Stufe in der jeweiligen Richtung
    // snappen. Wenn man bereits auf einer ganzen Stufe steht, geht's um
    // genau ±1 weiter (alte Semantik).
    //   + : nicht-ganzzahlig → ceil(cur), sonst cur + 1
    //   − : nicht-ganzzahlig → floor(cur), sonst cur − 1
    // Plus Clamping auf [minZoom, maxZoom] damit wir kein no-op-easeTo
    // feuern wenn wir am Rand sind.
    const cur = map.getZoom();
    const min = map.getMinZoom();
    const max = map.getMaxZoom();
    let target: number;
    if (delta > 0) {
      target = Number.isInteger(cur) ? cur + 1 : Math.ceil(cur);
    } else {
      target = Number.isInteger(cur) ? cur - 1 : Math.floor(cur);
    }
    target = Math.max(min, Math.min(max, target));
    if (target === cur) return;
    // easeTo lässt Center und Pitch unangetastet (im Gegensatz zu flyTo /
    // jumpTo) — wichtig damit der LocateControl-State / 3D-Tilt erhalten
    // bleiben. 200 ms ist nah am Default von zoomIn/Out und fühlt sich
    // im Vergleich nicht zäher an.
    map.easeTo({ zoom: target, duration: 200 });
  };

  // Wave 14cb-fix4: Live-Zoom-Level zwischen + und − anzeigen (User-Wunsch
  // "zwischen plus und minus gut sichtbar die aktuelle zoomstufe"). Wir
  // subscriben auf das Map-`zoom`-Event analog ScaleBar.
  const [zoomLevel, setZoomLevel] = useState<number | null>(null);
  useEffect(() => {
    let currentMap: maplibregl.Map | null = null;
    let onZoom: (() => void) | null = null;
    const attach = (map: maplibregl.Map | null) => {
      if (currentMap && onZoom) currentMap.off('zoom', onZoom);
      currentMap = map;
      if (!map) {
        setZoomLevel(null);
        return;
      }
      const update = () => setZoomLevel(map.getZoom());
      onZoom = update;
      map.on('zoom', update);
      if (map.loaded()) update();
      else map.once('load', update);
    };
    attach(mapBus.get());
    const unsub = mapBus.subscribe(attach);
    return () => {
      unsub();
      if (currentMap && onZoom) currentMap.off('zoom', onZoom);
    };
  }, []);

  // Wave 14ca-fix64: shiftUp im Ride-Mode auf 0 zurueck (s.u.).
  // Wave 14ep: Stack wandert von 33vh → 20vh. User-Feedback "die buttons
  // im handschuhmode sind alle zu hoch! mach sie deutlich zum unteren
  // bildschirmrand". 33vh war ~Bildschirm-Mitte was bei aktivem Glove-Mode
  // (Stack mit 4-5 Buttons, +35% scale) deutlich in die obere Haelfte
  // hineinwuchs. 20vh = sitzt deutlich tiefer, ohne die untere Bottom-
  // Bar (~bottom 10vh) zu beruehren.
  const bottom = '20vh';
  void shiftUp; // Param bleibt kompatibel falls wir spaeter doch was brauchen
  // Wave 14ca-fix64: Style-Update der Zoom + Fullscreen-Buttons.
  //   - rounded-xl statt rounded-full: rechteckige Pillen wie der
  //     Style-Switcher links → konsistente UI.
  //   - bg-turbo-900/35 statt /55: durchsichtiger, sieht "leichter" aus,
  //     Karte schimmert besser durch.
  //   - Wrapper-Klasse `adt-scaled-stack-right` skaliert nach
  //     --icon-scale-tertiary, sodass Glove-Mode (medium/large) auch DIESE
  //     Buttons groesser macht — User-Wunsch: "ändert ausgerechnet NICHT
  //     die größe, dabei bedient man sowas am häufigsten".
  return (
    <div
      // Wave 14ca-fix64-v2: gap auf gap-4 (16 px) erhoeht. Bei gloves-Scale
      // (1.5x) ergibt das 24 px visueller Abstand zwischen den Buttons —
      // genug auch mit shadow-lg drumrum. Plus: der ganze Stack ist mit
      // up to 6 Buttons (Zoom-In/Pille/Out/Vollbild/Rain/Pocket) bei 1.5x
      // ziemlich hoch (~580 px). Damit er nicht in die Locate/Compass-
      // Controls oben rechts reinwaechst, liegen die hier sowieso bei
      // bottom: 33vh, was auf den meisten iPhones genug Platz laesst.
      className="adt-scaled-stack-right absolute right-3 flex flex-col gap-4 pointer-events-none"
      style={{ bottom }}
      aria-hidden={false}
    >
      {/* Wave 14cf-fix6: Zoom-Pille — ein einziges ovales Element statt drei
          getrennter Buttons. + oben, − unten, Zoom-Stufe elegant in der
          Mitte. User-Wunsch nach SMA-App-Vergleich.
          Komposition:
            - Wrapper trägt Border + Hintergrund + Shadow.
            - Drei Sektionen (+, Display, −) sind simple flex-Kinder mit
              dünnen Trennlinien (border-y).
            - rounded-3xl → echte Pille mit großen Halbkreis-Enden. */}
      <div
        className="pointer-events-auto w-14 rounded-3xl bg-turbo-900/35 border border-white/15 backdrop-blur-sm shadow-lg flex flex-col overflow-hidden"
        role="group"
        aria-label="Zoom"
      >
        <button
          type="button"
          onClick={() => zoomBy(+1)}
          className="h-12 flex items-center justify-center text-white text-3xl font-light leading-none hover:bg-turbo-900/30 active:bg-turbo-900/50 transition-colors"
          title="Zoom in"
          aria-label="Zoom in"
        >
          +
        </button>
        <div
          className="h-7 flex items-center justify-center text-white text-[13px] font-semibold leading-none tabular-nums select-none border-y border-white/10"
          title={zoomLevel != null ? `Zoom level ${zoomLevel.toFixed(2)}` : 'Zoom level'}
          aria-label={zoomLevel != null ? `Current zoom ${zoomLevel.toFixed(1)}` : 'Zoom level'}
        >
          {zoomLevel != null ? zoomLevel.toFixed(1) : '—'}
        </div>
        <button
          type="button"
          onClick={() => zoomBy(-1)}
          className="h-12 flex items-center justify-center text-white text-3xl font-light leading-none hover:bg-turbo-900/30 active:bg-turbo-900/50 transition-colors"
          title="Zoom out"
          aria-label="Zoom out"
        >
          −
        </button>
      </div>

      {/* Wave 14cb-fix4: Vollbild direkt unter Minus, gleicher Look. */}
      <FullscreenToggle />

      {/* Wave 14ca-fix64: Rain-Mode-Toggle. Aktiviert den Lock-Overlay
          (Display-Lock gegen Regentropfen). Entsperrung via 3-Finger-
          Long-Press 2 s. Recording laeuft im Lock weiter.
          Sichtbarkeit ueber Admin-Toggle in Profile (rainModeEnabled). */}
      {rainModeEnabled && (
        <button
          type="button"
          onClick={() => rainModeBus.activate()}
          className="pointer-events-auto w-14 h-14 rounded-xl bg-turbo-900/35 border border-white/15 backdrop-blur-sm shadow-lg flex items-center justify-center text-white/90 text-2xl leading-none hover:bg-turbo-900/55 active:bg-turbo-900/75 transition-colors"
          title="Rain mode — lock display against raindrops"
          aria-label="Activate rain mode"
        >
          🌧️
        </button>
      )}

      {/* Wave 14ca-fix64: Pocket-Mode-Toggle. Macht das Display komplett
          schwarz fuer OLED-Energie-Spar (Pixel physikalisch aus). Recording
          laeuft im Wake-Lock weiter. Entsperrung wie Rain-Mode: 3 Finger
          2 s halten. Sichtbarkeit ueber Admin-Toggle in Profile. */}
      {pocketModeEnabled && (
        <button
          type="button"
          onClick={() => pocketModeBus.activate()}
          className="pointer-events-auto w-14 h-14 rounded-xl bg-turbo-900/35 border border-white/15 backdrop-blur-sm shadow-lg flex items-center justify-center text-white/90 text-2xl leading-none hover:bg-turbo-900/55 active:bg-turbo-900/75 transition-colors"
          title="Battery safe mode — mostly-black screen with a mini map, OLED battery save"
          aria-label="Activate battery safe mode"
        >
          🔋
        </button>
      )}
    </div>
  );
}

/**
 * StyleIcon — 3 Inline-SVGs für die Style-Switcher-Pill oben links (Wave 14j).
 *
 * Lucide-Stil (stroke-basiert, 2px, round join), damit sich die Icons homogen
 * in die restliche Icon-Sprache der App einfügen (TabBar, LocateControl,
 * Hazard-Triangle). `currentColor` → das umgebende <button> steuert über die
 * text-class die Farbe: weiß auf Orange (aktiv) oder white/55 auf dark
 * (inaktiv).
 *
 * Varianten:
 *   - mapout: Berggipfel (lucide `mountain`) → Topo-Karte mit Höhenlinien.
 *     Die spitze Zick-Zack-Kontur macht visuell sofort klar: "Gelände".
 *   - minimal: Dokument mit geknickter Ecke (lucide `file-text` reduziert)
 *     → reduzierte Basemap. Ein "Blatt Papier", wie der User es genannt hat.
 *   - satellite: Orbiting-Satellit mit Antennen-Ausleger (lucide `satellite`).
 *     Nicht die Dish-Antenne (die verwechselt man mit WLAN) — ein richtiges
 *     Orbit-Satellit-Silhouetten-Icon, damit die Bedeutung "Satelliten-Bild"
 *     eindeutig ist.
 */
function StyleIcon({ kind }: { kind: 'mapout' | 'minimal' | 'satellite' }) {
  if (kind === 'mapout') {
    return (
      <svg
        width={20}
        height={20}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
      </svg>
    );
  }
  if (kind === 'minimal') {
    return (
      <svg
        width={20}
        height={20}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    );
  }
  // satellite
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M13 7 9 3 5 7l4 4" />
      <path d="m17 11 4 4-4 4-4-4" />
      <path d="m8 12 4 4" />
      <path d="m16 8 3-3" />
      <path d="M9 21a6 6 0 0 0-6-6" />
    </svg>
  );
}

/**
 * Kleines Höhen-Badge. Wird von BottomBar in die vereinheitlichte Pill
 * eingebaut (`embedded` = true → kein eigener Hintergrund).
 *
 * Quelle der Höhe (in Reihenfolge):
 *   1. pos.altitude (GPS) — kostet nichts, genau ±5-30m wenn GPS gut, sonst null
 *   2. map.queryTerrainElevation([lon,lat]) — SRTM aus den lokalen PMTiles
 *      (MapView hat setTerrain mit exaggeration=0 aktiviert, siehe dort)
 *   3. nichts anzeigen wenn weder noch verfügbar ist
 *
 * Wir re-querien die Höhe bei jedem Position-Update (das feuert max alle
 * paar Sekunden bei watchPosition). Kein eigener Timer nötig.
 */
function ElevationBadge({ embedded = false }: { embedded?: boolean } = {}) {
  const pos = useCurrentPosition();
  const [srtmAlt, setSrtmAlt] = useState<number | null>(null);

  useEffect(() => {
    if (!pos) {
      setSrtmAlt(null);
      return;
    }
    // GPS-Höhe verfügbar → SRTM-Fallback nicht nötig
    if (pos.altitude != null) {
      setSrtmAlt(null);
      return;
    }
    const map = mapBus.get();
    if (!map) return;
    try {
      const alt = map.queryTerrainElevation([pos.longitude, pos.latitude]);
      // queryTerrainElevation gibt bei nicht-geladenem Tile null zurück —
      // kein Grund zur Sorge, nächstes Update versucht's neu.
      setSrtmAlt(alt ?? null);
    } catch (err) {
      console.warn('[elev] queryTerrainElevation failed', err);
      setSrtmAlt(null);
    }
  }, [pos]);

  // Ohne GPS-Fix: Platzhalter "— m" (vorher: null → Segment verschwand und
  // der PillDivider hing links daneben). Embedded-Modus soll visuell stabil
  // bleiben, auch wenn wir noch keine Höhe haben.
  const altitudeM = pos ? pos.altitude ?? srtmAlt : null;
  const source = pos && pos.altitude != null ? 'GPS' : pos && srtmAlt != null ? 'SRTM' : null;

  // Wave 14cb-fix4: embedded-Variante von text-[11px] → text-sm (User-Wunsch
  // "die aktuelle höhe unten rechts größere schrift"). Standalone-Variante
  // bleibt unverändert (wird nur in OfflinePage o.ä. genutzt).
  const wrapperClass = embedded
    ? 'text-white text-sm font-semibold tabular-nums leading-none flex items-baseline gap-0.5 select-none'
    : 'bg-black/70 text-white text-xs font-medium px-2 py-1 rounded-md shadow-sm flex items-baseline gap-1';

  // User-Feedback 14d: "rechts neben POIs bei der höhe doch lieber so: alt. 538m
  // sonst weiss man nicht dass es die höhe sind". Im embedded-Modus prefixen
  // wir das Label daher mit "alt." (gedimmt, tracking-wide), damit der User
  // die Zahl instant als Höhenmeter lesen kann — ohne dass ein zusätzlicher
  // Divider nötig wird oder die Pill breiter wird.
  return (
    <div className={wrapperClass}>
      {embedded && <span className="text-white/55 font-normal mr-0.5">alt.</span>}
      {altitudeM != null ? (
        <>
          <span>{Math.round(altitudeM)}</span>
          <span className="text-white/60">m</span>
          {source && !embedded && (
            <span className="text-[9px] text-white/40 uppercase ml-0.5">{source}</span>
          )}
        </>
      ) : (
        <span className="text-white/50">— m</span>
      )}
    </div>
  );
}

/**
 * Popup-HTML für einen Crux-Marker. Bewusst als String (kein React-Portal) —
 * MapLibre-Popups sind native DOM, ein React-Portal wäre aufwändig für den
 * trivialen Fall "zeige kurzen Text". Escaping: wir escapen note+distLabel
 * defensiv, selbst wenn die Quelle (Admin-Eingabe oder Server) kontrolliert
 * ist — kein XSS-Vektor, auch wenn zukünftig anonymere Einträge kämen.
 */
function buildCruxPopupHtml(
  c: CruxPoint,
  distLabel: string,
  elevationMeters: number | null,
): string {
  const noteHtml = c.note
    ? `<div style="color:#e7e5e4;font-size:13px;line-height:1.35;margin-bottom:4px;white-space:pre-wrap;word-break:break-word;">${escapeHtml(c.note)}</div>`
    : `<div style="color:#a8a29e;font-size:12px;font-style:italic;margin-bottom:4px;">No description</div>`;
  const header = `
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
      <span style="display:inline-block;width:14px;height:14px;">
        <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" style="display:block;">
          <path d="M10 1.8 L18.6 17 L1.4 17 Z" fill="#facc15" stroke="#111827" stroke-width="1.4" stroke-linejoin="round"/>
          <rect x="9" y="7" width="2" height="5.4" rx="0.4" fill="#111827"/>
          <circle cx="10" cy="14.4" r="1.15" fill="#111827"/>
        </svg>
      </span>
      <strong style="color:#fbbf24;font-size:12px;text-transform:uppercase;letter-spacing:0.04em;">Key section</strong>
    </div>`;
  // User-Wunsch Batch 7 Folge: Sportler haben Bike-Computer/Smartwatch mit
  // Höhenanzeige — wenn sie wissen "dich trifft's bei 1040 m ü.M.", können
  // sie antizipieren. Wir rendern Distanz + Höhe nebeneinander wenn vorhanden,
  // sonst nur Distanz (alter Look). elevationMeters kommt gespeichert vom
  // Server ODER wird aus den lokalen SRTM-Samples interpoliert (MapPage).
  const eleChunk =
    elevationMeters != null
      ? `<span style="color:#14b8a6;"> · </span><span style="color:#fef3c7;">${escapeHtml(
          `${Math.round(elevationMeters)} m alt.`,
        )}</span>`
      : '';
  const footer = `<div style="color:#9ca3af;font-size:11px;font-variant-numeric:tabular-nums;">${escapeHtml(distLabel)} from start${eleChunk}</div>`;
  return `<div style="background:#0b1120;padding:8px 10px;border-radius:6px;color:white;">${header}${noteHtml}${footer}</div>`;
}

/** Minimal-HTML-Escape. Für Texte, die wir als string in innerHTML einsetzen. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Linear-Interpolation einer Höhe an einer gegebenen Distance, basierend auf
 * den vorhandenen Profile-Samples (distances[] + elevations[], parallel indiziert).
 *
 * Returnt null wenn:
 *   - Samples noch nicht geladen (pending state der SRTM-Query),
 *   - Arrays leer sind,
 *   - targetMeters außerhalb des Tracks liegt (→ clampen statt extrapolieren).
 *
 * Wird für zwei Zwecke genutzt:
 *   (1) Cruxes ohne gespeicherte elevationMeters mit einer best-effort Schätzung
 *       im Popup anzeigen (z.B. Cruxes die via Admin-UI per Distanz gesetzt
 *       wurden, bevor wir das Feld eingeführt haben).
 *   (2) AddCruxHereButton bekommt einen Fallback wenn der aktuelle GPS-Fix
 *       keine altitude liefert (altitude kann auf manchen iPhones null sein
 *       ohne Permission-Issue — Geolocation-API-Eigenart).
 */
function interpolateElevationAtDistance(
  elevations: number[] | null,
  distances: number[],
  targetMeters: number,
): number | null {
  if (!elevations || elevations.length === 0 || distances.length === 0) return null;
  if (elevations.length !== distances.length) return null;
  if (!Number.isFinite(targetMeters) || targetMeters < 0) return null;

  const last = distances[distances.length - 1] ?? 0;
  // Clamp auf Track-Bereich — Extrapolation würde die Zahl verfälschen.
  const clamped = Math.max(0, Math.min(last, targetMeters));

  // Binary-Search wäre overkill bei 100 Samples; lineares Scannen reicht.
  let i = 0;
  while (i < distances.length - 1 && (distances[i + 1] ?? 0) < clamped) i++;
  const dA = distances[i] ?? 0;
  const dB = distances[Math.min(i + 1, distances.length - 1)] ?? dA;
  const segLen = dB - dA;
  const t = segLen > 0 ? (clamped - dA) / segLen : 0;
  const eA = elevations[i]!;
  const eB = elevations[Math.min(i + 1, elevations.length - 1)]!;
  const value = eA + (eB - eA) * t;
  return Number.isFinite(value) ? value : null;
}

/**
 * Wave 14ca-fix64: Modal nach erfolgreichem Way-ID-Snap.
 *
 * Zeigt way-id, Length, Name, Highway-Tag, dann fuer jeden moeglichen
 * Status (block/approve/one-way f/r) einen Button. Tap → POST → Modal
 * schliesst, Pick-Mode bleibt aktiv damit der Admin den naechsten Way
 * markieren kann. "Cancel" schliesst nur das Modal (Pick-Mode aktiv);
 * "Exit pick mode" verlaesst den Modus komplett.
 */
function WayPickModal({
  snap,
  onAdd,
  onCancel,
  onClose,
}: {
  snap: SnapResult;
  onAdd: (status: WayOverrideStatus) => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  const lengthLabel =
    typeof snap.wayLengthM === 'number' && snap.wayLengthM > 0
      ? snap.wayLengthM >= 1000
        ? `${(snap.wayLengthM / 1000).toFixed(2)} km`
        : `${snap.wayLengthM} m`
      : '— m';
  // Wave 14ca-fix64: Bottom-Sheet statt Fullscreen-Modal.
  // User-Bug: "ich wollte den ganzen Weg sehen, jetzt ist alles unscharf
  // hinter dem Modal". Loesung: Sheet sitzt am unteren Bildschirmrand,
  // Map bleibt komplett sichtbar oberhalb (mit der fuchsia-Polyline +
  // Tap-Marker drin). Kein Backdrop-Blur, kein Dark-Overlay — nur das
  // Sheet selbst hat solide BG. Der Tap-Capture-Layer im Pick-Mode wird
  // ausserdem in MapPage automatisch ausgeblendet solange `pickedSnap`
  // gesetzt ist, sodass der User die Karte oberhalb des Sheets frei
  // pannen/zoomen kann ohne aus Versehen einen neuen Way zu picken.
  return (
    <div
      className="fixed left-0 right-0 bottom-0 z-[60] flex justify-center pointer-events-none"
      role="dialog"
      aria-modal="false"
      aria-label="Add OSM way to discovery overrides"
    >
      <div
        className="bg-turbo-900 border-t border-l border-r border-sky-500/40 rounded-t-2xl p-3 w-full max-w-md shadow-2xl pointer-events-auto"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag-Indikator + Schliess-Button */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="w-10 h-1 rounded-full bg-white/25" aria-hidden="true" />
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white text-xl leading-none w-8 h-8 -mr-1 -mt-1 flex items-center justify-center"
            aria-label="Close"
            title="Skip this way and pick another"
          >
            ✕
          </button>
        </div>

        {/* Header + Way-Info kompakt in einer Zeile.
            Wave 14ca-fix64: das alte 5-row Info-Block-Panel ist gestrichen —
            Way-Info als Inline-Header. way-id verlinkt auf openstreetmap.org. */}
        <div className="mb-3">
          <div className="text-[11px] text-sky-300/85 uppercase tracking-wider font-semibold mb-0.5">
            OSM way identified
          </div>
          <div className="text-white text-base font-semibold leading-tight">
            {snap.wayName || 'Unnamed way'}
          </div>
          <div className="text-[12px] text-white/60 mt-0.5 flex items-center gap-2 flex-wrap">
            <a
              href={`https://www.openstreetmap.org/way/${snap.wayId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-sky-300 hover:text-sky-200"
            >
              way:{snap.wayId} ↗
            </a>
            <span className="text-white/30">·</span>
            <span className="tabular-nums">{lengthLabel}</span>
            {snap.highway && (
              <>
                <span className="text-white/30">·</span>
                <span>{snap.highway}</span>
              </>
            )}
            <span className="text-white/30">·</span>
            <span className="tabular-nums text-white/45">
              tap {snap.distM.toFixed(1)} m off
            </span>
          </div>
        </div>

        {/* Status-Buttons — kompakter (h-12 statt h-14) damit das Sheet
            insgesamt weniger Karte verdeckt. */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => onAdd('blocked')}
            className="w-full h-12 rounded-xl bg-red-500/95 hover:bg-red-400 active:bg-red-600 text-white font-bold text-sm flex items-center justify-center gap-2 border border-red-300/50 shadow"
          >
            <span aria-hidden="true">🚫</span>
            <span>Block this way</span>
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onAdd('one_way_forward')}
              className="h-11 rounded-xl bg-amber-500/85 hover:bg-amber-400 active:bg-amber-600 text-black font-semibold text-sm flex items-center justify-center gap-1 border border-amber-300/50 shadow"
              title="Allow only direction from-node → to-node"
            >
              <span aria-hidden="true">→</span>
              <span>One-way fwd</span>
            </button>
            <button
              type="button"
              onClick={() => onAdd('one_way_reverse')}
              className="h-11 rounded-xl bg-amber-500/85 hover:bg-amber-400 active:bg-amber-600 text-black font-semibold text-sm flex items-center justify-center gap-1 border border-amber-300/50 shadow"
              title="Allow only direction to-node → from-node"
            >
              <span aria-hidden="true">←</span>
              <span>One-way rev</span>
            </button>
          </div>
          <button
            type="button"
            onClick={() => onAdd('approved')}
            className="w-full h-10 rounded-xl bg-emerald-500/80 hover:bg-emerald-400 active:bg-emerald-600 text-white font-medium text-[13px] flex items-center justify-center gap-2 border border-emerald-300/50 shadow"
          >
            <span aria-hidden="true">✓</span>
            <span>Approve (force-include)</span>
          </button>
        </div>

        {/* Footer-Action: Exit pick mode komplett. Skip ist via X oben rechts. */}
        <button
          type="button"
          onClick={onCancel}
          className="mt-2 w-full h-9 rounded-lg bg-transparent text-white/55 hover:text-white/85 font-medium text-[12px] border border-white/10"
        >
          Exit pick mode
        </button>
      </div>
    </div>
  );
}

// ─── PoiDetailCard ──────────────────────────────────────────────────────
//
// Wave 14cf-fix19: kompakte Karte mit POI-Daten — Name, Notiz,
// Öffnungszeiten — wird oben in der MapPage gerendert wenn der User auf
// einen POI tappt. Layout: floating-card am UNTEREN Rand der Map (über
// der BottomBar, unter dem Trail-Detail-Sheet falls beides sichtbar
// wäre — was praktisch nicht passiert weil ein POI-Tap "selected" nicht
// ändert), sodass der Tap-Marker auf der Karte sichtbar bleibt. Click
// ausserhalb (Backdrop) schliesst das Popup.
const POI_CATEGORY_DISPLAY: Record<
  string,
  { label: string; emoji: string }
> = {
  drinking_water: { label: 'Drinking water', emoji: '💧' },
  cafe: { label: 'Café', emoji: '☕' },
  pranzo: { label: 'Lunch & dinner', emoji: '🍝' },
  bike_shop: { label: 'Bike shop', emoji: '🔧' },
  beach: { label: 'Beach', emoji: '🏖️' },
  viewpoint: { label: 'Viewpoint', emoji: '🏞️' },
  // ULTRA: generische Overpass-Kategorien.
  fast_food: { label: 'Fast food / kebab', emoji: '🥙' },
  restaurant_indian: { label: 'Indian restaurant', emoji: '🍛' },
  camp_site: { label: 'Campsite', emoji: '⛺' },
  bakery: { label: 'Bakery', emoji: '🥖' },
  supermarket: { label: 'Supermarket', emoji: '🛒' },
  bicycle_shop: { label: 'Bike shop (OSM)', emoji: '🚲' },
  shelter: { label: 'Shelter / hut', emoji: '🛖' },
  swimming: { label: 'Pool / swimming', emoji: '🏊' },
  decathlon: { label: 'Decathlon', emoji: '🏬' },
};

/**
 * Baut das Routing-Ziel für einen Pit-Stop. Bei aktiver Rennstrecke (routeBus)
 * eine RUNDFAHRT: Position → POI (via) → Wiedereinstieg auf die Strecke (end =
 * nächster Streckenpunkt zum POI) — so kommt man nach dem Stopp zurück auf den
 * Kurs, die Kombination ist die kürzeste. Ohne Strecke: einfacher Weg zum POI.
 */
function buildPitstopTarget(
  start: [number, number],
  poiLon: number,
  poiLat: number,
  name: string,
): PitstopTarget {
  // Strecke aus routeBus (aktiv gefolgte) ODER raceRefBus (persistente
  // Referenz, falls routeBus beim Verlassen der Karte schon geleert wurde) —
  // so klappt der Rückweg auf die Strecke auch nach dem ersten Mal.
  const route = routeBus.get() ?? raceRefBus.get();
  // Originalstrecke als persistente Referenz merken (überlebt Navigation +
  // Detour-Ride), damit sie überall als blasse Linie sichtbar bleibt.
  if (route) raceRefBus.set(route);
  // Wiedereinstieg VORWÄRTS (Fahrtrichtung), nicht der global nächste Punkt —
  // sonst führt die Route zurück dahin wo man herkam. (Fallback ohne vorab
  // berechnetes Optimum — der Marker-Klick nutzt das echte Optimum.)
  const rejoin = route
    ? forwardRejoin(route, start[1], start[0], poiLat, poiLon)
    : null;
  if (route && rejoin) {
    return {
      start,
      via: [[poiLon, poiLat]],
      end: rejoin,
      endName: name,
      raceRoute: route,
    };
  }
  return { start, end: [poiLon, poiLat], endName: name };
}

function PoiDetailCard({
  popup,
  onClose,
  onNavigate,
}: {
  popup: {
    category: string;
    name: string;
    note: string;
    openingHours: string;
    openNow?: string;
    source: string;
    website?: string;
    phone?: string;
    lat: number;
    lon: number;
  };
  onClose: () => void;
  onNavigate: () => void;
}) {
  const def = POI_CATEGORY_DISPLAY[popup.category] ?? {
    label: popup.category,
    emoji: '📍',
  };
  // Source-Tag — nur fuer admin/user POIs interessant. OSM-Brunnen brauchen
  // keinen Hinweis, das ist die Default-Quelle bei drinking_water.
  const sourceLabel =
    popup.source === 'admin'
      ? 'Admin pick'
      : popup.source === 'user'
        ? 'Your mark'
        : popup.source === 'google'
          ? '🟢 Live von Google'
          : null;

  // Title: name oder fallback auf Kategorie-Label.
  const title = popup.name.trim() || def.label;
  // Subtitle: Kategorie wenn Name verschieden ist, sonst nichts (sonst
  // hätten wir "Café — Café" was albern aussieht).
  const subtitle = popup.name.trim() && popup.name.trim() !== def.label ? def.label : null;

  return (
    <div
      // Backdrop auf voller Hoehe — Click ausserhalb schliesst.
      className="fixed inset-0 z-[55] flex items-end justify-center pointer-events-none"
      onClick={onClose}
    >
      {/* Stop-propagation auf den Card-Container, damit interne Klicks
          das Popup nicht schliessen. pointer-events-auto wieder an
          (nur die Karte selbst ist klickbar — der Rest der Map bleibt
          benutzbar fuer Pan/Zoom). */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="pointer-events-auto m-4 mb-28 w-full max-w-sm bg-turbo-900/95 backdrop-blur-md border border-sky-500/30 rounded-2xl shadow-xl px-4 py-3"
      >
        <div className="flex items-start gap-3">
          <span className="text-2xl leading-none mt-0.5" aria-hidden="true">
            {def.emoji}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold text-base leading-tight">
              {title}
            </div>
            {subtitle && (
              <div className="text-white/55 text-[12px] mt-0.5">
                {subtitle}
              </div>
            )}
            <div className="text-white/40 text-[11px] mt-0.5 font-mono tabular-nums">
              {popup.lat.toFixed(5)}, {popup.lon.toFixed(5)}
              {sourceLabel && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-sky-500/15 border border-sky-500/30 text-sky-200 text-[10px] uppercase tracking-wide">
                  {sourceLabel}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 -mt-1 w-8 h-8 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 text-lg leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {popup.note.trim() && (
          <div className="mt-3 text-white/85 text-sm leading-snug whitespace-pre-wrap">
            {popup.note}
          </div>
        )}

        {(popup.openNow === 'true' || popup.openNow === 'false') && (
          <div
            className={`mt-2 text-sm font-semibold ${
              popup.openNow === 'true' ? 'text-emerald-300' : 'text-red-300'
            }`}
          >
            {popup.openNow === 'true' ? '🟢 Jetzt geöffnet' : '🔴 Jetzt geschlossen'}
          </div>
        )}

        {popup.openingHours.trim() && (
          <div className="mt-2 flex items-start gap-2 text-amber-300/95 text-sm leading-snug">
            <span aria-hidden="true">🕒</span>
            <span>{popup.openingHours}</span>
          </div>
        )}

        {popup.phone && popup.phone.trim() && (
          <a
            href={`tel:${popup.phone.replace(/\s+/g, '')}`}
            className="mt-2 flex items-center gap-2 text-sky-300 hover:text-sky-200 text-sm"
          >
            <span aria-hidden="true">📞</span>
            <span>{popup.phone}</span>
          </a>
        )}

        {popup.website && popup.website.trim() && (
          <a
            href={popup.website}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 flex items-center gap-2 text-sky-300 hover:text-sky-200 text-sm truncate"
          >
            <span aria-hidden="true">🌐</span>
            <span className="truncate">{popup.website.replace(/^https?:\/\//, '')}</span>
          </a>
        )}

        {/* Mit ADTU hinnavigieren — Route vom aktuellen (ggf. simulierten)
            Standort zu diesem POI über den bestehenden Pit-Stop-Handoff. */}
        <button
          type="button"
          onClick={onNavigate}
          className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg bg-teal-600/30 border border-teal-400/60 hover:bg-teal-600/40 text-teal-50 text-sm font-semibold py-2"
        >
          <span aria-hidden="true">🚴</span>
          <span>Navigate with ADTU</span>
        </button>

        {/* In Google Maps öffnen — nur aus Koordinaten gebaut, kein Google-
            Daten-Speichern (entspricht ULTRA_PROJECT_MEMORY §7). */}
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${popup.lat},${popup.lon}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 flex items-center justify-center gap-2 rounded-lg bg-sky-600/20 border border-sky-500/40 hover:bg-sky-600/30 text-sky-100 text-sm py-2"
        >
          <span aria-hidden="true">🧭</span>
          <span>Open in Google Maps</span>
        </a>
      </div>
    </div>
  );
}

